import { FormatRegistry, type Static, type TUnion, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  AnnouncementBusinessEventPayloadSchema,
  isAnnouncementBusinessEventPayload,
} from "./announcement.js";
import { BusinessOperationTraceSidecarV1Schema } from "./business-operation.js";
import {
  orderAnomalyTerm,
  orderOpeningMethodTerm,
  orderStatusTerm,
  paymentMethodTerm,
  paymentStatusTerm,
  syncStateTerm,
} from "./business-transaction-terminology.js";
import { CheckoutSplitPlanReadModelSchema } from "./checkout-split.js";
import {
  type CoachAssignableServiceOption,
  CoachAssignableServiceOptionSchema,
  type CoachConfigurationImportResult,
  CoachConfigurationImportResultSchema,
  type CoachConfigurationSnapshot,
  CoachConfigurationSnapshotSchema,
  type CoachMonthlyCommissionReadModel,
  CoachMonthlyCommissionReadModelSchema,
  type CoachMonthlyCommissionSummary,
  CoachMonthlyCommissionSummarySchema,
  type CoachOrderLine,
  CoachOrderLineSchema,
  type CoachProfile,
  CoachProfileSchema,
  CoachQueueEntrySchema,
  type CoachRuntimeQueueReadModel,
  CoachRuntimeQueueReadModelSchema,
  type CoachRuntimeReadModel,
  CoachRuntimeReadModelSchema,
  type CoachRuntimeServiceReadModel,
  CoachRuntimeServiceReadModelSchema,
  type CoachRuntimeShiftReadModel,
  CoachRuntimeShiftReadModelSchema,
  type CoachRuntimeSnapshot,
  CoachRuntimeSnapshotSchema,
  type CoachServiceAssignmentOperatorResult,
  CoachServiceAssignmentOperatorResultSchema,
  type CoachServiceRecord,
  CoachServiceRecordSchema,
  type CoachServiceReplacementOperatorResult,
  CoachServiceReplacementOperatorResultSchema,
  type CoachServiceReplacementPreviewReadModel,
  CoachServiceReplacementPreviewReadModelSchema,
  type CoachServiceRule,
  CoachServiceRuleSchema,
  type CoachServiceStopRequest,
  type CoachServiceStopRequestList,
  CoachServiceStopRequestListSchema,
  CoachServiceStopRequestSchema,
  type CoachSessionStopCompletedService,
  CoachSessionStopCompletedServiceSchema,
  type CoachSessionStopPreview,
  CoachSessionStopPreviewSchema,
  CoachShiftSchema,
  type CoachSummaryRow,
  CoachSummaryRowSchema,
} from "./coach.js";
import {
  CoachServiceCorrectionNoticeSchema,
  CoachServiceCorrectionResultSchema,
} from "./coach-service-correction.js";
import { CombinedCheckoutReadModelSchema } from "./combined-checkout.js";
import {
  CustomerRentalChargeItemSchema,
  CustomerRentalRecordSchema,
  isCustomerRentalChargeItem,
} from "./customer-rental.js";
import { CustomerReservationBusinessEventPayloadSchema } from "./customer-reservation.js";
import { CustomerStorageRecordSchema } from "./customer-storage.js";
import {
  GroupBuyFulfillmentRecordSchema,
  GroupBuyInventoryCostSnapshotSchema,
} from "./group-buy.js";
import {
  GroupBuyReconciliationInternalReferenceIdSchema,
  GroupBuyReconciliationSignoffPackageSchema,
} from "./group-buy-reconciliation-resolution.js";
import { InventoryCountBusinessEventPayloadSchema } from "./inventory-count.js";
import { InventoryLossBusinessEventPayloadSchema } from "./inventory-loss.js";
import {
  KioskProductReservationBusinessEventPayloadSchema,
  KioskProductReservationReadModelSchema,
} from "./kiosk-product-order.js";
import {
  isMemberReceivableWorkItemSnapshot,
  MemberDebitStateSchema,
  MemberReceivableWorkItemSnapshotSchema,
} from "./member-offline-debit.js";
import { isOrderNumberForOrderId } from "./order-number.js";
import { PaidOrderItemSaleReturnRecordSchema } from "./paid-sale-return.js";
import {
  PurchaseOrderShortClosedBusinessEventPayloadSchema,
  PurchaseReceiptBusinessEventPayloadSchema,
} from "./purchase-order-receiving.js";
import {
  PurchasePayableReconciliationBusinessEventPayloadSchema,
  SupplierPaymentBusinessEventPayloadSchema,
} from "./purchase-payable.js";
import { PurchaseReturnBusinessEventPayloadSchema } from "./purchase-return.js";
import {
  isTrackedSaleInventoryReturnSnapshot,
  SaleReturnInventoryEffectSchema,
  SaleReturnPhysicalDispositionSchema,
  saleReturnInventoryEffectForDisposition,
  TrackedSaleInventoryCostSnapshotSchema,
  TrackedSaleInventoryReturnSnapshotSchema,
  trackedSaleInventoryCostSnapshotMatchesItem,
} from "./sale-inventory.js";
import { SessionStartAdjustmentEventEvidenceSchema } from "./session-start-adjustment.js";
import {
  isStorePackageOrderItem,
  StorePackageOrderItemReturnRecordSchema,
  StorePackageOrderItemSchema,
} from "./store-package.js";
import { SupplierBatchPaymentBusinessEventPayloadSchema } from "./supplier-batch-payment.js";
import {
  SupplierCreditBusinessEventPayloadSchema,
  SupplierPaymentReversalBusinessEventPayloadSchema,
  SupplierRefundBusinessEventPayloadSchema,
} from "./supplier-credit-settlement.js";
import {
  isWorkforceAttendanceMutationResult,
  WorkforceAttendanceBusinessEventPayloadSchema,
} from "./workforce-attendance.js";
import { WorkforcePayrollBusinessEventPayloadSchema } from "./workforce-payroll.js";

export * from "./announcement.js";
export * from "./approval-workflow.js";
export * from "./authorization.js";
export * from "./business-audit.js";
export * from "./business-metric-report.js";
export * from "./business-metrics.js";
export * from "./business-operation.js";
export * from "./business-transaction-terminology.js";
export * from "./canonical-coach-lifecycle.js";
export * from "./canonical-coach-onboarding.js";
export * from "./canonical-coach-profile-maintenance.js";
export * from "./cash-difference-business-metric-details.js";
export * from "./cash-shift-cash-movement.js";
export * from "./cash-shift-handover.js";
export * from "./checkout-split.js";
export * from "./coach.js";
export * from "./coach-service-correction.js";
export * from "./coach-workspace.js";
export * from "./combined-checkout.js";
export * from "./commercial.js";
export * from "./commission-ledger.js";
export * from "./commission-payable-business-metric-details.js";
export * from "./confirmed-receipt-business-metric-details.js";
export * from "./customer-rental.js";
export * from "./customer-reservation.js";
export * from "./customer-self-service.js";
export * from "./customer-storage.js";
export * from "./frontdesk-operating-fact.js";
export * from "./group-buy.js";
export * from "./group-buy-daily-reconciliation.js";
export * from "./group-buy-operator-decision.js";
export * from "./group-buy-reconciliation-number.js";
export * from "./group-buy-reconciliation-resolution.js";
export * from "./group-buy-redemption.js";
export * from "./inventory-count.js";
export * from "./inventory-loss.js";
export * from "./inventory-lot.js";
export * from "./inventory-movement-report.js";
export * from "./kiosk-product-order.js";
export * from "./manual-review-resolution.js";
export * from "./member-fund-business-metrics.js";
export * from "./member-lifecycle.js";
export * from "./member-marketing.js";
export * from "./member-offline-debit.js";
export * from "./member-recharge-reconciliation.js";
export * from "./member-spend-preview.js";
export * from "./official-payment.js";
export * from "./operator-session.js";
export * from "./order-number.js";
export * from "./order-transaction.js";
export * from "./owner-dashboard.js";
export * from "./paid-sale-return.js";
export * from "./procurement.js";
export * from "./product-cost-business-metric-details.js";
export * from "./purchase-order-approval.js";
export * from "./purchase-order-number.js";
export * from "./purchase-order-receiving.js";
export * from "./purchase-order-short-close-number.js";
export * from "./purchase-payable.js";
export * from "./purchase-payable-number.js";
export * from "./purchase-receipt-number.js";
export * from "./purchase-return.js";
export * from "./purchase-return-number.js";
export * from "./purchase-terminology.js";
export * from "./refund-business-metric-details.js";
export * from "./route-migration.js";
export * from "./sale-inventory.js";
export * from "./service-duty.js";
export * from "./session-start-adjustment.js";
export * from "./store-package.js";
export * from "./supplier-batch-payment.js";
export * from "./supplier-credit-settlement.js";
export * from "./supplier-settlement-ledger.js";
export * from "./supplier-settlement-ledger-export.js";
export * from "./support-access.js";
export * from "./workforce.js";
export * from "./workforce-attendance.js";
export * from "./workforce-invitation.js";
export * from "./workforce-management.js";
export * from "./workforce-payroll.js";

const DATE_TIME_SEPARATOR = /t/i;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(z|([+-])(\d{2})(?::?(\d{2}))?)$/i;
const DAYS_PER_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isRfc3339DateTime(value: string): boolean {
  const [datePart, timePart, extraPart] = value.split(DATE_TIME_SEPARATOR);
  if (!datePart || !timePart || extraPart) return false;

  const date = DATE.exec(datePart);
  const time = TIME.exec(timePart);
  if (!date || !time) return false;

  const year = Number(date[1]);
  const month = Number(date[2]);
  const day = Number(date[3]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const maxDay = month === 2 && leapYear ? 29 : (DAYS_PER_MONTH[month] ?? 0);
  if (month < 1 || month > 12 || day < 1 || day > maxDay) return false;

  const hour = Number(time[1]);
  const minute = Number(time[2]);
  const second = Number(time[3]);
  const timezoneSign = time[5] === "-" ? -1 : 1;
  const timezoneHour = Number(time[6] ?? 0);
  const timezoneMinute = Number(time[7] ?? 0);
  if (timezoneHour > 23 || timezoneMinute > 59) return false;
  if (hour <= 23 && minute <= 59 && second < 60) return true;

  const utcMinute = minute - timezoneMinute * timezoneSign;
  const utcHour = hour - timezoneHour * timezoneSign - (utcMinute < 0 ? 1 : 0);
  return (
    (utcHour === 23 || utcHour === -1) && (utcMinute === 59 || utcMinute === -1) && second < 61
  );
}

if (!FormatRegistry.Has("date-time")) {
  FormatRegistry.Set("date-time", isRfc3339DateTime);
}

export const EDGE_CONTRACT_VERSION = "1.0.0" as const;
export const EDGE_CONTRACT_MAJOR = 1 as const;
export const HOST_CONTRACT_VERSION = "1.0.0" as const;
export const HOST_CONTRACT_MAJOR = 1 as const;
export const HOST_CONTRACT_V1_PATTERN =
  "^1\\.[0-9]+\\.[0-9]+(?:-[0-9A-Za-z.-]+)?(?:\\+[0-9A-Za-z.-]+)?$" as const;
const HostContractV1VersionSchema = Type.String({ pattern: HOST_CONTRACT_V1_PATTERN });

export function isCoachMonthlyCommissionSummary(
  value: unknown,
): value is CoachMonthlyCommissionSummary {
  return Value.Check(CoachMonthlyCommissionSummarySchema, value);
}

export function isCoachMonthlyCommissionReadModel(
  value: unknown,
): value is CoachMonthlyCommissionReadModel {
  if (!Value.Check(CoachMonthlyCommissionReadModelSchema, value) || !hasOnlySafeIntegers(value)) {
    return false;
  }

  const segmentKeys = new Set<string>();
  for (const [index, segment] of value.segments.entries()) {
    if (segment.toMinute !== null && segment.toMinute <= segment.fromMinute) return false;
    const key = `${segment.fromMinute}:${segment.toMinute ?? "MAX"}:${segment.rateBps}`;
    if (segmentKeys.has(key)) return false;
    segmentKeys.add(key);

    const previous = value.segments[index - 1];
    if (previous && segment.fromMinute < previous.fromMinute) return false;
  }

  const segmentEffectiveSeconds = safeIntegerSum(
    value.segments.map((segment) => segment.effectiveSeconds),
  );
  const segmentNetServiceFeeFen = safeIntegerSum(
    value.segments.map((segment) => segment.netServiceFeeFen),
  );
  const segmentCommissionFen = safeIntegerSum(
    value.segments.map((segment) => segment.commissionFen),
  );
  if (
    segmentEffectiveSeconds === null ||
    segmentNetServiceFeeFen === null ||
    segmentCommissionFen === null ||
    segmentEffectiveSeconds > value.effectiveSeconds ||
    segmentNetServiceFeeFen > value.netServiceFeeFen ||
    segmentCommissionFen > value.commissionFen
  ) {
    return false;
  }

  if (value.calculationMode === null) {
    return (
      value.segments.length === 0 &&
      value.currentRateBps === null &&
      value.minutesToNextTier === null
    );
  }
  return (
    value.currentRateBps !== null &&
    ((value.effectiveSeconds === 0 && value.netServiceFeeFen === 0) || value.segments.length > 0)
  );
}

export function isCoachMonthlyCommissionReadModelList(
  value: unknown,
  expectedMonth?: string,
  expectedCoachId?: string,
): value is CoachMonthlyCommissionReadModel[] {
  if (!Array.isArray(value) || value.length > 100_000) return false;
  if (expectedCoachId !== undefined && value.length !== 1) return false;

  const coachIds = new Set<string>();
  for (const item of value) {
    if (
      !isCoachMonthlyCommissionReadModel(item) ||
      (expectedMonth !== undefined && item.month !== expectedMonth) ||
      (expectedCoachId !== undefined && item.coachId !== expectedCoachId) ||
      coachIds.has(item.coachId)
    ) {
      return false;
    }
    coachIds.add(item.coachId);
  }
  return true;
}

function hasOnlySafeIntegers(value: unknown): boolean {
  if (typeof value === "number") return Number.isSafeInteger(value);
  if (Array.isArray(value)) return value.every(hasOnlySafeIntegers);
  if (value !== null && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every(hasOnlySafeIntegers);
  }
  return true;
}

function coachConfigurationCollectionKeysAreUnique(
  configuration: CoachConfigurationSnapshot,
): boolean {
  return (
    uniqueStrings(configuration.levels.map((level) => level.id)) &&
    uniqueStrings(configuration.levels.map((level) => level.code)) &&
    uniqueStrings(configuration.profiles.map((profile) => profile.id)) &&
    uniqueStrings(configuration.profiles.map((profile) => profile.coachNumber)) &&
    uniqueStrings(
      configuration.serviceRules.map((rule) => `${rule.ruleId}\u0000${rule.version}`),
    ) &&
    uniqueStrings(
      (configuration.serviceRuleHistory ?? []).map((rule) => `${rule.ruleId}\u0000${rule.version}`),
    ) &&
    uniqueStrings(
      (configuration.priceOverrides ?? []).map(
        (priceOverride) => `${priceOverride.id}\u0000${priceOverride.version}`,
      ),
    ) &&
    uniqueStrings(
      (configuration.priceOverrideHistory ?? []).map(
        (priceOverride) => `${priceOverride.id}\u0000${priceOverride.version}`,
      ),
    )
  );
}

export function isCoachConfigurationSnapshot(value: unknown): value is CoachConfigurationSnapshot {
  return (
    Value.Check(CoachConfigurationSnapshotSchema, value) &&
    hasOnlySafeIntegers(value) &&
    coachConfigurationCollectionKeysAreUnique(value)
  );
}

export function isCoachConfigurationImportResult(
  value: unknown,
): value is CoachConfigurationImportResult {
  if (!Value.Check(CoachConfigurationImportResultSchema, value) || !hasOnlySafeIntegers(value)) {
    return false;
  }
  if (value.valid !== (value.errors.length === 0)) return false;
  if (value.applied !== (value.mode === "APPLY" && value.valid)) return false;
  return true;
}

function stableContractValue(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableContractValue).join(",")}]`;
  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableContractValue(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

function normalizedCoachConfiguration(
  configuration: CoachConfigurationSnapshot,
): CoachConfigurationSnapshot {
  const byIdentityAndVersion = <T extends { version: number }>(
    values: readonly T[],
    identity: (value: T) => string,
  ): T[] =>
    [...values].sort(
      (left, right) =>
        identity(left).localeCompare(identity(right)) || left.version - right.version,
    );
  return {
    ...configuration,
    levels: byIdentityAndVersion(configuration.levels, (level) => level.id),
    profiles: byIdentityAndVersion(configuration.profiles, (profile) => profile.id),
    serviceRules: byIdentityAndVersion(configuration.serviceRules, (rule) => rule.ruleId),
    ...(configuration.serviceRuleHistory
      ? {
          serviceRuleHistory: byIdentityAndVersion(
            configuration.serviceRuleHistory,
            (rule) => rule.ruleId,
          ),
        }
      : {}),
    ...(configuration.priceOverrides
      ? {
          priceOverrides: byIdentityAndVersion(
            configuration.priceOverrides,
            (priceOverride) => priceOverride.id,
          ),
        }
      : {}),
    ...(configuration.priceOverrideHistory
      ? {
          priceOverrideHistory: byIdentityAndVersion(
            configuration.priceOverrideHistory,
            (priceOverride) => priceOverride.id,
          ),
        }
      : {}),
  };
}

export function coachConfigurationSnapshotsEqual(
  left: CoachConfigurationSnapshot,
  right: CoachConfigurationSnapshot,
): boolean {
  return (
    stableContractValue(normalizedCoachConfiguration(left)) ===
    stableContractValue(normalizedCoachConfiguration(right))
  );
}

export function isCoachProfileList(value: unknown): value is CoachProfile[] {
  if (!Array.isArray(value) || value.length > 1_000) return false;
  if (!value.every((profile) => Value.Check(CoachProfileSchema, profile))) return false;
  const profiles = value as CoachProfile[];
  return (
    profiles.every((profile) => Number.isSafeInteger(profile.version)) &&
    uniqueStrings(profiles.map((profile) => profile.id)) &&
    uniqueStrings(profiles.map((profile) => profile.coachNumber))
  );
}

export function isCoachAssignableServiceOptionList(
  value: unknown,
): value is CoachAssignableServiceOption[] {
  if (!Array.isArray(value) || value.length > 1_000) return false;
  if (!value.every((option) => Value.Check(CoachAssignableServiceOptionSchema, option))) {
    return false;
  }
  const options = value as CoachAssignableServiceOption[];
  return (
    options.every(
      (option) => Number.isSafeInteger(option.version) && Number.isSafeInteger(option.priceFen),
    ) && uniqueStrings(options.map((option) => JSON.stringify([option.coachId, option.ruleId])))
  );
}

export function isCoachServiceRuleList(value: unknown): value is CoachServiceRule[] {
  if (!Array.isArray(value) || value.length > 1_000) return false;
  if (!value.every((rule) => Value.Check(CoachServiceRuleSchema, rule))) return false;
  const rules = value as CoachServiceRule[];
  return (
    rules.every(
      (rule) =>
        Number.isSafeInteger(rule.version) &&
        (rule.commissionRuleVersion === undefined ||
          Number.isSafeInteger(rule.commissionRuleVersion)) &&
        (rule.priceOverrideVersion === undefined ||
          rule.priceOverrideVersion === null ||
          Number.isSafeInteger(rule.priceOverrideVersion)),
    ) &&
    uniqueStrings(rules.map((rule) => rule.ruleId)) &&
    uniqueStrings(rules.map((rule) => `${rule.ruleId}:${rule.version}`))
  );
}

export function isCoachSummaryRow(value: unknown): value is CoachSummaryRow {
  if (!Value.Check(CoachSummaryRowSchema, value)) return false;
  const row = value as CoachSummaryRow;
  return (
    [
      row.serviceCount,
      row.serviceMinutes,
      row.grossServiceFeeFen,
      row.adjustmentFen,
      row.netServiceFeeFen,
      row.commissionBaseFen,
      row.commissionFen,
      row.offlineServiceCount,
    ].every(Number.isSafeInteger) &&
    row.netServiceFeeFen === row.grossServiceFeeFen + row.adjustmentFen
  );
}

function isCanonicalCalendarDate(value: string): boolean {
  if (!DATE.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function uniqueStrings(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function contiguousPositivePositions(values: readonly number[]): boolean {
  return (
    values.every(Number.isSafeInteger) &&
    [...values]
      .sort((left, right) => left - right)
      .every((position, index) => position === index + 1)
  );
}

export function isCoachRuntimeSnapshot(value: unknown): value is CoachRuntimeSnapshot {
  if (!Value.Check(CoachRuntimeSnapshotSchema, value)) return false;
  const snapshot = value as CoachRuntimeSnapshot;
  const profileIds = new Set(snapshot.profiles.map((profile) => profile.id));
  return (
    isCanonicalCalendarDate(snapshot.businessDate) &&
    Number.isSafeInteger(snapshot.pendingSyncRecords) &&
    Number.isSafeInteger(snapshot.conflictRecords) &&
    Number.isSafeInteger(snapshot.reviewRecords) &&
    uniqueStrings(snapshot.profiles.map((profile) => profile.id)) &&
    uniqueStrings(snapshot.shifts.map((shift) => shift.id)) &&
    uniqueStrings(snapshot.shifts.map((shift) => shift.coachId)) &&
    uniqueStrings(snapshot.queue.map((entry) => entry.id)) &&
    uniqueStrings(snapshot.queue.map((entry) => entry.coachId)) &&
    contiguousPositivePositions(snapshot.queue.map((entry) => entry.position)) &&
    uniqueStrings(snapshot.services.map((service) => service.id)) &&
    snapshot.shifts.every(
      (shift) => shift.businessDate === snapshot.businessDate && profileIds.has(shift.coachId),
    ) &&
    snapshot.queue.every(
      (entry) =>
        entry.businessDate === snapshot.businessDate &&
        profileIds.has(entry.coachId) &&
        Number.isSafeInteger(entry.position),
    ) &&
    snapshot.services.every(
      (service) =>
        isCanonicalCalendarDate(service.businessDate) &&
        profileIds.has(service.coachId) &&
        (service.businessDate === snapshot.businessDate ||
          service.status === "ASSIGNED" ||
          service.status === "ACTIVE"),
    )
  );
}

export function isCoachRuntimeReadModel(value: unknown): value is CoachRuntimeReadModel {
  if (!Value.Check(CoachRuntimeReadModelSchema, value)) return false;
  const runtime = value as CoachRuntimeReadModel;
  const coachRotationEnabled = runtime.coachRotationEnabled === true;
  const profileIds = new Set(runtime.profiles.map((profile) => profile.id));
  const assignmentAvailability = runtime.assignmentAvailability;
  const assignmentAvailabilityValid =
    assignmentAvailability === undefined ||
    (assignmentAvailability.length === runtime.profiles.length &&
      uniqueStrings(assignmentAvailability.map((item) => item.coachId)) &&
      assignmentAvailability.every((item) => {
        if (!profileIds.has(item.coachId)) return false;
        if (item.code === "QUEUED") {
          return (
            item.requestedAssignable &&
            item.requestedReason === null &&
            (coachRotationEnabled
              ? item.rotationAssignable && item.rotationReason === null
              : !item.rotationAssignable && item.rotationReason !== null)
          );
        }
        if (item.code === "AVAILABLE") {
          return (
            item.requestedAssignable &&
            !item.rotationAssignable &&
            item.requestedReason === null &&
            item.rotationReason !== null
          );
        }
        return (
          !item.requestedAssignable &&
          !item.rotationAssignable &&
          item.requestedReason !== null &&
          item.rotationReason !== null
        );
      }));
  return (
    isCanonicalCalendarDate(runtime.businessDate) &&
    (runtime.syncSummary === null ||
      (Number.isSafeInteger(runtime.syncSummary.pendingRecords) &&
        Number.isSafeInteger(runtime.syncSummary.conflictRecords) &&
        Number.isSafeInteger(runtime.syncSummary.reviewRecords))) &&
    uniqueStrings(runtime.profiles.map((profile) => profile.id)) &&
    uniqueStrings(runtime.shifts.map((shift) => shift.coachId)) &&
    uniqueStrings(runtime.queue.map((entry) => entry.id)) &&
    uniqueStrings(runtime.queue.map((entry) => entry.coachId)) &&
    contiguousPositivePositions(runtime.queue.map((entry) => entry.position)) &&
    uniqueStrings(runtime.services.map((service) => service.id)) &&
    assignmentAvailabilityValid &&
    runtime.shifts.every(
      (shift) => shift.businessDate === runtime.businessDate && profileIds.has(shift.coachId),
    ) &&
    runtime.queue.every(
      (entry) =>
        entry.businessDate === runtime.businessDate &&
        profileIds.has(entry.coachId) &&
        Number.isSafeInteger(entry.position),
    ) &&
    runtime.services.every(
      (service) =>
        isCanonicalCalendarDate(service.businessDate) &&
        profileIds.has(service.coachId) &&
        (service.businessDate === runtime.businessDate ||
          service.status === "ASSIGNED" ||
          service.status === "ACTIVE"),
    )
  );
}

export function isCoachRuntimeShiftReadModel(value: unknown): value is CoachRuntimeShiftReadModel {
  return (
    Value.Check(CoachRuntimeShiftReadModelSchema, value) &&
    isCanonicalCalendarDate((value as CoachRuntimeShiftReadModel).businessDate)
  );
}

export function isCoachRuntimeQueueReadModel(value: unknown): value is CoachRuntimeQueueReadModel {
  return (
    Value.Check(CoachRuntimeQueueReadModelSchema, value) &&
    isCanonicalCalendarDate((value as CoachRuntimeQueueReadModel).businessDate) &&
    Number.isSafeInteger((value as CoachRuntimeQueueReadModel).position)
  );
}

export function isCoachRuntimeServiceReadModel(
  value: unknown,
): value is CoachRuntimeServiceReadModel {
  return (
    Value.Check(CoachRuntimeServiceReadModelSchema, value) &&
    isCanonicalCalendarDate((value as CoachRuntimeServiceReadModel).businessDate) &&
    Number.isSafeInteger((value as CoachRuntimeServiceReadModel).version)
  );
}

export function isCoachServiceStopRequest(value: unknown): value is CoachServiceStopRequest {
  if (!Value.Check(CoachServiceStopRequestSchema, value)) return false;
  const request = value as CoachServiceStopRequest;
  const pending = request.status === "PENDING";
  const requestedAt = Date.parse(request.requestedAt);
  return (
    isCanonicalCalendarDate(request.businessDate) &&
    request.requestNumber.slice(3, 11) === request.businessDate.replaceAll("-", "") &&
    isCanonicalIsoTimestamp(request.requestedAt) &&
    Number.isSafeInteger(request.serviceVersionAtRequest) &&
    (pending
      ? request.resolvedAt === null &&
        request.resolvedByDisplayName === null &&
        request.resolutionNote === null &&
        request.completedServiceVersion === null
      : request.resolvedAt !== null &&
        isCanonicalIsoTimestamp(request.resolvedAt) &&
        Date.parse(request.resolvedAt) >= requestedAt &&
        request.resolvedByDisplayName !== null &&
        request.resolutionNote !== null &&
        (request.status === "COMPLETED"
          ? Number.isSafeInteger(request.completedServiceVersion) &&
            (request.completedServiceVersion as number) > request.serviceVersionAtRequest
          : request.completedServiceVersion === null))
  );
}

export function isCoachServiceStopRequestList(
  value: unknown,
): value is CoachServiceStopRequestList {
  if (!Value.Check(CoachServiceStopRequestListSchema, value)) return false;
  const list = value as CoachServiceStopRequestList;
  const generatedAt = Date.parse(list.generatedAt);
  return (
    isCanonicalIsoTimestamp(list.generatedAt) &&
    (!list.terminalHistoryTruncated || list.items.length === 200) &&
    uniqueStrings(list.items.map((item) => item.requestId)) &&
    uniqueStrings(list.items.map((item) => item.requestNumber)) &&
    list.items.every(isCoachServiceStopRequest) &&
    list.items.every(
      (item) =>
        Date.parse(item.requestedAt) <= generatedAt &&
        (item.resolvedAt === null || Date.parse(item.resolvedAt) <= generatedAt),
    ) &&
    list.items.every((item, index, items) => {
      if (index === 0) return true;
      const previous = items[index - 1];
      return (
        previous !== undefined &&
        (item.requestedAt < previous.requestedAt ||
          (item.requestedAt === previous.requestedAt && item.requestId < previous.requestId))
      );
    })
  );
}

export function isCoachSessionStopPreview(value: unknown): value is CoachSessionStopPreview {
  if (!Value.Check(CoachSessionStopPreviewSchema, value)) return false;
  const preview = value as CoachSessionStopPreview;
  const serviceIds = new Set<string>();
  let total = 0;
  for (const service of preview.services) {
    if (
      serviceIds.has(service.id) ||
      !isCanonicalCalendarDate(service.businessDate) ||
      ![service.estimatedBilledMinutes, service.estimatedServiceFeeFen, service.version].every(
        Number.isSafeInteger,
      ) ||
      (service.status === "ACTIVE" ? service.startedAt === null : service.startedAt !== null)
    ) {
      return false;
    }
    serviceIds.add(service.id);
    total += service.estimatedServiceFeeFen;
    if (!Number.isSafeInteger(total)) return false;
  }
  return total === preview.estimatedServiceFeeFen;
}

export function isCoachServiceAssignmentOperatorResult(
  value: unknown,
): value is CoachServiceAssignmentOperatorResult {
  return (
    Value.Check(CoachServiceAssignmentOperatorResultSchema, value) &&
    Number.isSafeInteger(value.priceFen) &&
    value.assignedAt === value.startedAt
  );
}

export function isCoachServiceRecord(value: unknown): value is CoachServiceRecord {
  if (!Value.Check(CoachServiceRecordSchema, value)) return false;
  const service = value as CoachServiceRecord;
  return (
    isCanonicalCalendarDate(service.businessDate) &&
    [
      service.billedMinutes,
      service.effectiveSeconds ?? 0,
      service.accruedServiceFeeFen,
      service.commissionBaseFen,
      service.commissionFen,
      service.version,
      service.ruleSnapshot.version,
      service.ruleSnapshot.minimumMinutes,
      service.ruleSnapshot.billingStepMinutes,
      service.ruleSnapshot.priceFen,
      service.ruleSnapshot.commissionRateBps,
      service.ruleSnapshot.fixedCommissionFen,
    ].every(Number.isSafeInteger)
  );
}

export function isCoachOrderLine(value: unknown): value is CoachOrderLine {
  return Value.Check(CoachOrderLineSchema, value);
}

export function isCoachServiceReplacementOperatorResult(
  value: unknown,
): value is CoachServiceReplacementOperatorResult {
  if (!Value.Check(CoachServiceReplacementOperatorResultSchema, value)) return false;
  const result = value as CoachServiceReplacementOperatorResult;
  return (
    isCanonicalCalendarDate(result.previousService.businessDate) &&
    isCanonicalCalendarDate(result.replacementService.businessDate) &&
    [
      result.previousService.billedMinutes,
      result.previousService.serviceFeeFen,
      result.previousService.version,
      result.replacementService.version,
      ...(result.commissionFen === null ? [] : [result.commissionFen]),
    ].every(Number.isSafeInteger) &&
    result.previousService.replacementServiceId === result.replacementService.id &&
    result.replacementService.originalServiceId === result.previousService.id &&
    result.replacementService.version === 2 &&
    result.previousService.endedAt === result.replacementService.startedAt &&
    result.replacementService.assignedAt === result.replacementService.startedAt
  );
}

export function isCoachServiceReplacementPreviewReadModel(
  value: unknown,
): value is CoachServiceReplacementPreviewReadModel {
  if (!Value.Check(CoachServiceReplacementPreviewReadModelSchema, value)) return false;
  const preview = value as CoachServiceReplacementPreviewReadModel;
  const effectiveAt = Date.parse(preview.effectiveAt);
  const expiresAt = Date.parse(preview.expiresAt);
  return (
    Number.isFinite(effectiveAt) &&
    Number.isFinite(expiresAt) &&
    expiresAt > effectiveAt &&
    [
      preview.expectedServiceVersion,
      preview.expectedEffectiveSeconds,
      preview.expectedBilledMinutes,
      preview.expectedServiceFeeFen,
      preview.expectedCommissionBaseFen,
      preview.expectedCommissionFen,
      preview.replacementRuleVersion,
      preview.replacementPriceFen,
    ].every(Number.isSafeInteger)
  );
}

export interface CoachSummaryTotals {
  serviceCount: number;
  serviceMinutes: number;
  grossServiceFeeFen: number;
  adjustmentFen: number;
  netServiceFeeFen: number;
  commissionBaseFen: number;
  commissionFen: number;
  offlineServiceCount: number;
}

export function coachSummaryTotals(rows: readonly CoachSummaryRow[]): CoachSummaryTotals | null {
  const totals: CoachSummaryTotals = {
    serviceCount: 0,
    serviceMinutes: 0,
    grossServiceFeeFen: 0,
    adjustmentFen: 0,
    netServiceFeeFen: 0,
    commissionBaseFen: 0,
    commissionFen: 0,
    offlineServiceCount: 0,
  };
  const fields = Object.keys(totals) as (keyof CoachSummaryTotals)[];
  for (const row of rows) {
    for (const field of fields) {
      const next = totals[field] + row[field];
      if (!Number.isSafeInteger(next)) return null;
      totals[field] = next;
    }
  }
  return totals;
}

export const DeploymentShapeSchema = Type.Union([
  Type.Literal("INTEGRATED"),
  Type.Literal("SPLIT"),
]);
export type DeploymentShape = Static<typeof DeploymentShapeSchema>;

export const EdgeNodeBindingSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    componentId: Type.String({ minLength: 1 }),
    credentialId: Type.String({ minLength: 8 }),
    credentialVersion: Type.Integer({ minimum: 1 }),
    deploymentShape: DeploymentShapeSchema,
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type EdgeNodeBinding = Static<typeof EdgeNodeBindingSchema>;

export function isEdgeNodeBinding(value: unknown): value is EdgeNodeBinding {
  return Value.Check(EdgeNodeBindingSchema, value) && isCanonicalIsoTimestamp(value.updatedAt);
}

export const RegisterEdgeNodeRequestSchema = Type.Object(
  {
    componentId: Type.String({ minLength: 1 }),
    credentialId: Type.String({ minLength: 8 }),
    deploymentShape: DeploymentShapeSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const MigrateEdgeNodeRequestSchema = Type.Object(
  {
    newComponentId: Type.String({ minLength: 1 }),
    newCredentialId: Type.String({ minLength: 8 }),
    targetShape: DeploymentShapeSchema,
    expectedCredentialVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const KioskBindingStatusSchema = Type.Union([
  Type.Literal("UNPAIRED"),
  Type.Literal("ACTIVE"),
  Type.Literal("REVOKED"),
]);
export type KioskBindingStatus = Static<typeof KioskBindingStatusSchema>;

export const TableKioskManagementRecordSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    edgeNodeRegistered: Type.Boolean(),
    deploymentShape: Type.Union([DeploymentShapeSchema, Type.Null()]),
    bindingStatus: KioskBindingStatusSchema,
    componentId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    bindingVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    pairedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    revokedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    pendingComponentId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    pairingExpiresAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type TableKioskManagementRecord = Static<typeof TableKioskManagementRecordSchema>;

export function isTableKioskManagementRecord(value: unknown): value is TableKioskManagementRecord {
  return Value.Check(TableKioskManagementRecordSchema, value);
}

export const TableKioskManagementReadModelSchema = Type.Object(
  {
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    tableNodeId: Type.String({ minLength: 1, maxLength: 128 }),
    edgeNodeRegistered: Type.Boolean(),
    deploymentShape: Type.Union([DeploymentShapeSchema, Type.Null()]),
    bindingStatus: KioskBindingStatusSchema,
    bindingVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    pairedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    revokedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    pendingComponentId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    pairingExpiresAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type TableKioskManagementReadModel = Static<typeof TableKioskManagementReadModelSchema>;

export const TableKioskManagementReadModelListSchema = Type.Array(
  TableKioskManagementReadModelSchema,
  { maxItems: 1_000 },
);

function isCanonicalIsoTimestamp(value: string | null): boolean {
  if (value === null) return true;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

export function isTableKioskManagementReadModelList(
  value: unknown,
): value is TableKioskManagementReadModel[] {
  if (!Value.Check(TableKioskManagementReadModelListSchema, value)) return false;
  const tableNumbers = new Set<string>();
  const tableNodeIds = new Set<string>();
  for (const row of value) {
    if (tableNumbers.has(row.tableNumber) || tableNodeIds.has(row.tableNodeId)) return false;
    tableNumbers.add(row.tableNumber);
    tableNodeIds.add(row.tableNodeId);
    if (row.edgeNodeRegistered !== (row.deploymentShape !== null)) return false;
    if (
      !row.edgeNodeRegistered &&
      (row.bindingStatus !== "UNPAIRED" ||
        row.bindingVersion !== null ||
        row.pendingComponentId !== null ||
        row.pairingExpiresAt !== null)
    ) {
      return false;
    }
    if (
      !isCanonicalIsoTimestamp(row.pairedAt) ||
      !isCanonicalIsoTimestamp(row.revokedAt) ||
      !isCanonicalIsoTimestamp(row.pairingExpiresAt)
    ) {
      return false;
    }
    if ((row.pendingComponentId === null) !== (row.pairingExpiresAt === null)) return false;
    if (
      (row.bindingStatus === "UNPAIRED" &&
        (row.bindingVersion !== null || row.pairedAt !== null || row.revokedAt !== null)) ||
      (row.bindingStatus === "ACTIVE" &&
        (row.bindingVersion === null || row.pairedAt === null || row.revokedAt !== null)) ||
      (row.bindingStatus === "REVOKED" &&
        (row.bindingVersion === null || row.pairedAt === null || row.revokedAt === null))
    ) {
      return false;
    }
    if (
      row.pairedAt !== null &&
      row.revokedAt !== null &&
      Date.parse(row.revokedAt) < Date.parse(row.pairedAt)
    ) {
      return false;
    }
  }
  return true;
}

export const DeviceIncidentActionSchema = Type.Union([
  Type.Literal("LIGHT_ON"),
  Type.Literal("LIGHT_OFF"),
  Type.Literal("PRINT_RECEIPT"),
  Type.Literal("REPRINT_RECEIPT"),
]);
export type DeviceIncidentAction = Static<typeof DeviceIncidentActionSchema>;

export const DeviceIncidentStatusSchema = Type.Union([
  Type.Literal("FAILED"),
  Type.Literal("UNKNOWN"),
]);
export type DeviceIncidentStatus = Static<typeof DeviceIncidentStatusSchema>;

export const DeviceIncidentReadModelSchema = Type.Object(
  {
    incidentId: Type.String({ minLength: 1, maxLength: 128 }),
    action: DeviceIncidentActionSchema,
    status: DeviceIncidentStatusSchema,
    subject: Type.String({ minLength: 2, maxLength: 120 }),
    reason: Type.String({ minLength: 2, maxLength: 120 }),
    businessResult: Type.String({ minLength: 2, maxLength: 160 }),
    nextStep: Type.String({ minLength: 2, maxLength: 160 }),
    manualResolutionAllowed: Type.Boolean(),
    evidence: Type.Union([Type.Literal("MOCK"), Type.Literal("REAL_HARDWARE")]),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type DeviceIncidentReadModel = Static<typeof DeviceIncidentReadModelSchema>;

export const DeviceIncidentStatusReadModelSchema = Type.Object(
  {
    attention: Type.Array(DeviceIncidentReadModelSchema, { maxItems: 20 }),
  },
  { additionalProperties: false },
);
export type DeviceIncidentStatusReadModel = Static<typeof DeviceIncidentStatusReadModelSchema>;

export function deviceIncidentReason(
  action: DeviceIncidentAction,
  status: DeviceIncidentStatus,
): string {
  if (action === "LIGHT_ON") {
    return status === "FAILED"
      ? "开灯没有成功，请先人工开灯并联系管理员检查设备"
      : "开灯结果尚未确认，请先到球台人工确认灯光状态";
  }
  if (action === "LIGHT_OFF") {
    return status === "FAILED"
      ? "关灯没有成功，请先人工关灯并联系管理员检查设备"
      : "关灯结果尚未确认，请先到球台人工确认灯光状态";
  }
  return status === "FAILED"
    ? "打印没有成功，请检查打印机并按订单重新打印"
    : "打印结果尚未确认，请先检查打印机和纸质小票";
}

export function deviceIncidentBusinessResult(action: DeviceIncidentAction): string {
  return action === "LIGHT_ON" || action === "LIGHT_OFF"
    ? "台单和球台经营结果已经保留，灯光异常不会撤销或改写本次业务。"
    : "订单和付款结果已经保留，打印异常不会重复收费或改写订单。";
}

export function deviceIncidentNextStep(
  action: DeviceIncidentAction,
  status: DeviceIncidentStatus,
): string {
  if (action === "LIGHT_ON") {
    return status === "FAILED"
      ? "请人工开灯并核对球台，处理完成后由店长或收银员关闭提醒。"
      : "请先到球台确认灯光状态；必要时人工开灯，再由店长或收银员关闭提醒。";
  }
  if (action === "LIGHT_OFF") {
    return status === "FAILED"
      ? "请人工关灯并核对球台，处理完成后由店长或收银员关闭提醒。"
      : "请先到球台确认灯光状态；必要时人工关灯，再由店长或收银员关闭提醒。";
  }
  return status === "FAILED"
    ? "请检查打印机和纸质小票；需要时按订单补打，处理完成后关闭提醒。"
    : "请先检查打印机和纸质小票；不能确认时按订单补打，处理完成后关闭提醒。";
}

export const DeviceIncidentManualResolutionRequestSchema = Type.Object(
  {
    expectedUpdatedAt: Type.String({ format: "date-time" }),
    note: Type.String({ minLength: 2, maxLength: 120 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type DeviceIncidentManualResolutionRequest = Static<
  typeof DeviceIncidentManualResolutionRequestSchema
>;

export const DeviceIncidentManualResolutionResultSchema = Type.Object(
  {
    incidentId: Type.String({ minLength: 1, maxLength: 128 }),
    action: DeviceIncidentActionSchema,
    subject: Type.String({ minLength: 2, maxLength: 120 }),
    resolution: Type.Literal("MANUAL_CONFIRMED"),
    resolvedByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    resolvedAt: Type.String({ format: "date-time" }),
    note: Type.String({ minLength: 2, maxLength: 120 }),
  },
  { additionalProperties: false },
);
export type DeviceIncidentManualResolutionResult = Static<
  typeof DeviceIncidentManualResolutionResultSchema
>;

function isCanonicalDeviceIncidentDateTime(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

export function isDeviceIncidentManualResolutionRequest(
  value: unknown,
): value is DeviceIncidentManualResolutionRequest {
  return (
    Value.Check(DeviceIncidentManualResolutionRequestSchema, value) &&
    value.note === value.note.trim() &&
    value.idempotencyKey === value.idempotencyKey.trim() &&
    isCanonicalDeviceIncidentDateTime(value.expectedUpdatedAt)
  );
}

export function isDeviceIncidentManualResolutionResult(
  value: unknown,
): value is DeviceIncidentManualResolutionResult {
  return (
    Value.Check(DeviceIncidentManualResolutionResultSchema, value) &&
    value.subject === value.subject.trim() &&
    value.resolvedByDisplayName === value.resolvedByDisplayName.trim() &&
    value.note === value.note.trim() &&
    isCanonicalDeviceIncidentDateTime(value.resolvedAt)
  );
}

export function isDeviceIncidentStatusReadModel(
  value: unknown,
): value is DeviceIncidentStatusReadModel {
  if (!Value.Check(DeviceIncidentStatusReadModelSchema, value)) return false;
  const incidentIds = new Set<string>();
  for (const incident of value.attention) {
    if (incidentIds.has(incident.incidentId)) return false;
    incidentIds.add(incident.incidentId);
    const updatedAt = Date.parse(incident.updatedAt);
    if (!Number.isFinite(updatedAt) || new Date(updatedAt).toISOString() !== incident.updatedAt) {
      return false;
    }
    if (incident.reason !== deviceIncidentReason(incident.action, incident.status)) return false;
    if (incident.subject !== incident.subject.trim()) return false;
    if (incident.businessResult !== deviceIncidentBusinessResult(incident.action)) return false;
    if (incident.nextStep !== deviceIncidentNextStep(incident.action, incident.status))
      return false;
  }
  return true;
}

export const ReceiptReprintStatusSchema = Type.Union([
  Type.Literal("QUEUED"),
  Type.Literal("NEEDS_ATTENTION"),
]);
export type ReceiptReprintStatus = Static<typeof ReceiptReprintStatusSchema>;

export const ReceiptReprintResultSchema = Type.Object(
  {
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    status: ReceiptReprintStatusSchema,
    evidence: Type.Union([Type.Literal("MOCK"), Type.Literal("REAL_HARDWARE")]),
  },
  { additionalProperties: false },
);
export type ReceiptReprintResult = Static<typeof ReceiptReprintResultSchema>;

export function isReceiptReprintResult(value: unknown): value is ReceiptReprintResult {
  return Value.Check(ReceiptReprintResultSchema, value);
}

export const CreateKioskPairingRequestSchema = Type.Object(
  {
    expectedComponentId: Type.String({ minLength: 8, maxLength: 128 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const CreateKioskPairingResultSchema = Type.Object(
  {
    tableNodeId: Type.String({ minLength: 1 }),
    expectedComponentId: Type.String({ minLength: 8, maxLength: 128 }),
    pairingCode: Type.String({ minLength: 8, maxLength: 8, pattern: "^[A-Z0-9]{8}$" }),
    expiresAt: Type.String({ format: "date-time" }),
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type CreateKioskPairingResult = Static<typeof CreateKioskPairingResultSchema>;

export function isCreateKioskPairingResult(value: unknown): value is CreateKioskPairingResult {
  return (
    Value.Check(CreateKioskPairingResultSchema, value) && isCanonicalIsoTimestamp(value.expiresAt)
  );
}

export const ClaimKioskPairingRequestSchema = Type.Object(
  {
    componentId: Type.String({ minLength: 8, maxLength: 128 }),
    pairingCode: Type.String({ minLength: 8, maxLength: 8, pattern: "^[A-Z0-9]{8}$" }),
    // New Agents provide a locally derived recovery secret before the Gateway
    // consumes the one-time grant. Optional keeps a safe rolling-upgrade path
    // for existing Agents; only supplied secrets can be replayed after loss.
    bindingSecret: Type.Optional(
      Type.String({ minLength: 43, maxLength: 43, pattern: "^[A-Za-z0-9_-]{43}$" }),
    ),
  },
  { additionalProperties: false },
);

export const KioskBindingClaimResultSchema = Type.Object(
  {
    tableNodeId: Type.String({ minLength: 1 }),
    componentId: Type.String({ minLength: 8, maxLength: 128 }),
    bindingSecret: Type.String({ minLength: 32, maxLength: 128 }),
    bindingVersion: Type.Integer({ minimum: 1 }),
    status: Type.Literal("ACTIVE"),
    pairedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type KioskBindingClaimResult = Static<typeof KioskBindingClaimResultSchema>;

export const KioskBindingDeviceStatusSchema = Type.Object(
  {
    tableNodeId: Type.String({ minLength: 1 }),
    componentId: Type.Union([Type.String({ minLength: 8, maxLength: 128 }), Type.Null()]),
    bindingVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    status: KioskBindingStatusSchema,
    updatedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type KioskBindingDeviceStatus = Static<typeof KioskBindingDeviceStatusSchema>;

export const RevokeKioskBindingRequestSchema = Type.Object(
  {
    reason: Type.String({ minLength: 2, maxLength: 256 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    expectedBindingVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const HostContextSchema = Type.Object(
  {
    contractVersion: HostContractV1VersionSchema,
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    anonymousSessionId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    locale: Type.String({ minLength: 2 }),
    displayRegion: Type.Union([Type.Literal("PRIMARY"), Type.Literal("SECONDARY")]),
    deploymentShape: DeploymentShapeSchema,
  },
  { additionalProperties: true },
);
export type HostContext = Static<typeof HostContextSchema>;

export const AppHealthSchema = Type.Object(
  {
    contractVersion: Type.String({ minLength: 1 }),
    appVersion: Type.String({ minLength: 1 }),
    state: Type.Union([
      Type.Literal("STARTING"),
      Type.Literal("HEALTHY"),
      Type.Literal("DEGRADED"),
      Type.Literal("UNRESPONSIVE"),
      Type.Literal("STOPPED"),
      Type.Literal("INCOMPATIBLE"),
      Type.Literal("CIRCUIT_OPEN"),
    ]),
    cpuPercent: Type.Number({ minimum: 0 }),
    memoryBytes: Type.Integer({ minimum: 0 }),
    pidCount: Type.Integer({ minimum: 0 }),
    openFileCount: Type.Integer({ minimum: 0 }),
    errorCode: Type.Union([Type.String(), Type.Null()]),
    observedAt: Type.String({ format: "date-time" }),
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: true },
);
export type AppHealth = Static<typeof AppHealthSchema>;

export const DisplayIntentSchema = Type.Object(
  {
    contractVersion: HostContractV1VersionSchema,
    intent: Type.Union([Type.Literal("ENTER"), Type.Literal("EXIT")]),
    title: Type.Union([Type.String({ maxLength: 80 }), Type.Null()]),
    requestedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: true },
);
export type DisplayIntent = Static<typeof DisplayIntentSchema>;

export const ShellCommandSchema = Type.Object(
  {
    contractVersion: HostContractV1VersionSchema,
    commandId: Type.String({ minLength: 8 }),
    command: Type.Union([
      Type.Literal("START"),
      Type.Literal("STOP"),
      Type.Literal("ACTIVATE"),
      Type.Literal("BACKGROUND"),
      Type.Literal("HEALTH_CHECK"),
    ]),
    issuedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: true },
);
export type ShellCommand = Static<typeof ShellCommandSchema>;

export function hostContractMajor(version: string): number | null {
  const match =
    /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/.exec(
      version,
    );
  return match ? Number(match[1]) : null;
}

export function isHostContractCompatible(version: string): boolean {
  return hostContractMajor(version) === HOST_CONTRACT_MAJOR;
}

export function isHostContext(value: unknown): value is HostContext {
  return Value.Check(HostContextSchema, value);
}

export function isAppHealth(value: unknown): value is AppHealth {
  return Value.Check(AppHealthSchema, value);
}

export function isShellCommand(value: unknown): value is ShellCommand {
  return Value.Check(ShellCommandSchema, value);
}

export const TableStateSchema = Type.Union([
  Type.Literal("IDLE"),
  Type.Literal("IN_USE"),
  Type.Literal("AWAITING_CLEANING"),
  Type.Literal("DISABLED"),
]);
export type TableState = Static<typeof TableStateSchema>;

export const EvidenceLabelSchema = Type.Union([
  Type.Literal("SIMULATED"),
  Type.Literal("MOCK"),
  Type.Literal("MANUAL_FALLBACK"),
  Type.Literal("SANDBOX"),
  Type.Literal("REAL_HARDWARE"),
  Type.Literal("STORE_ACCEPTED"),
]);
export type EvidenceLabel = Static<typeof EvidenceLabelSchema>;

export const SessionPaymentTimingSchema = Type.Union([
  Type.Literal("POSTPAID"),
  Type.Literal("PREPAID"),
]);
export type SessionPaymentTiming = Static<typeof SessionPaymentTimingSchema>;

export const SessionFundingSummarySchema = Type.Object(
  {
    paymentTiming: SessionPaymentTimingSchema,
    customerIdentityType: Type.Union([Type.Literal("ANONYMOUS"), Type.Literal("MEMBER")]),
    paymentMethod: Type.Union([Type.Literal("MEMBER_BALANCE"), Type.Null()]),
    memberId: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    memberNumber: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    memberDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    prepaymentStatus: Type.Union([
      Type.Literal("NOT_REQUIRED"),
      Type.Literal("CONFIRMED"),
      Type.Literal("REVIEW_REQUIRED"),
    ]),
    prepaidFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    paidAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type SessionFundingSummary = Static<typeof SessionFundingSummarySchema>;

export const TableSnapshotSchema = Type.Object(
  {
    contractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNodeId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    area: Type.String({ minLength: 1 }),
    state: TableStateSchema,
    sessionId: Type.Union([Type.String(), Type.Null()]),
    sessionStartedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    billingState: Type.Optional(
      Type.Union([Type.Literal("RUNNING"), Type.Literal("PAUSED"), Type.Literal("ENDED")]),
    ),
    billingPausedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    pricingPlanName: Type.Union([Type.String(), Type.Null()]),
    estimatedFeeFen: Type.Integer({ minimum: 0 }),
    currentTotalFen: Type.Optional(Type.Integer({ minimum: 0 })),
    funding: Type.Optional(SessionFundingSummarySchema),
    sessionElapsedSeconds: Type.Optional(Type.Integer({ minimum: 0 })),
    sessionRiskLevel: Type.Optional(
      Type.Union([Type.Literal("NORMAL"), Type.Literal("WARNING"), Type.Literal("CRITICAL")]),
    ),
    longSessionWarningMinutes: Type.Optional(Type.Integer({ minimum: 60, maximum: 100_800 })),
    longSessionCriticalMinutes: Type.Optional(Type.Integer({ minimum: 61, maximum: 100_800 })),
    billingEvidence: EvidenceLabelSchema,
    authoritativeAt: Type.String({ format: "date-time" }),
    revision: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type TableSnapshot = Static<typeof TableSnapshotSchema>;

export const OpenSessionRequestSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    pricingPlanId: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    expectedPricingPlanVersion: Type.Optional(
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    ),
  },
  { additionalProperties: false },
);
export type OpenSessionRequest = Static<typeof OpenSessionRequestSchema>;

export const CloseSessionRequestSchema = Type.Object(
  {
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CloseSessionRequest = Static<typeof CloseSessionRequestSchema>;

export const ServiceRequestTypeSchema = Type.Union([
  Type.Literal("SERVICE"),
  Type.Literal("COACH"),
  Type.Literal("CHECKOUT"),
  Type.Literal("OPEN_TABLE"),
]);
export type ServiceRequestType = Static<typeof ServiceRequestTypeSchema>;

export const ServiceRequestStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("ACKNOWLEDGED"),
  Type.Literal("COMPLETED"),
  Type.Literal("CANCELLED"),
  Type.Literal("FAILED"),
]);
export type ServiceRequestStatus = Static<typeof ServiceRequestStatusSchema>;

export const ServiceRequestRecordSchema = Type.Object(
  {
    eventId: Type.String({ minLength: 8 }),
    tableNodeId: Type.String({ minLength: 1 }),
    sessionId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    type: ServiceRequestTypeSchema,
    status: ServiceRequestStatusSchema,
    assignedActorId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    assignedActorDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    failureMessage: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    requestedAt: Type.String({ format: "date-time" }),
    acknowledgedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    cancelledAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type ServiceRequestRecord = Static<typeof ServiceRequestRecordSchema>;

export const ServiceRequestReadModelSchema = Type.Object(
  {
    eventId: Type.String({ minLength: 8 }),
    tableNodeId: Type.String({ minLength: 1 }),
    sessionId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    type: ServiceRequestTypeSchema,
    status: ServiceRequestStatusSchema,
    assignedActorDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    assignedToCurrentActor: Type.Boolean(),
    failureMessage: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    requestedAt: Type.String({ format: "date-time" }),
    acknowledgedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    cancelledAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type ServiceRequestReadModel = Static<typeof ServiceRequestReadModelSchema>;

type ServiceRequestTimeline = Pick<
  ServiceRequestRecord,
  "status" | "requestedAt" | "acknowledgedAt" | "completedAt" | "cancelledAt" | "updatedAt"
>;

function isServiceRequestTimeline(value: ServiceRequestTimeline): boolean {
  const requestedAt = Date.parse(value.requestedAt);
  const acknowledgedAt = value.acknowledgedAt === null ? null : Date.parse(value.acknowledgedAt);
  const completedAt = value.completedAt === null ? null : Date.parse(value.completedAt);
  const cancelledAt = value.cancelledAt === null ? null : Date.parse(value.cancelledAt);
  const updatedAt = Date.parse(value.updatedAt);
  if (
    !Number.isFinite(requestedAt) ||
    !Number.isFinite(updatedAt) ||
    (acknowledgedAt !== null && !Number.isFinite(acknowledgedAt)) ||
    (completedAt !== null && !Number.isFinite(completedAt)) ||
    (cancelledAt !== null && !Number.isFinite(cancelledAt)) ||
    updatedAt < requestedAt ||
    (acknowledgedAt !== null && acknowledgedAt < requestedAt) ||
    (completedAt !== null &&
      (acknowledgedAt === null || completedAt < acknowledgedAt || updatedAt < completedAt)) ||
    (cancelledAt !== null && (cancelledAt < requestedAt || updatedAt < cancelledAt)) ||
    (acknowledgedAt !== null && updatedAt < acknowledgedAt)
  ) {
    return false;
  }
  switch (value.status) {
    case "PENDING":
      return acknowledgedAt === null && completedAt === null && cancelledAt === null;
    case "ACKNOWLEDGED":
      return acknowledgedAt !== null && completedAt === null && cancelledAt === null;
    case "COMPLETED":
      return acknowledgedAt !== null && completedAt !== null && cancelledAt === null;
    case "CANCELLED":
      return acknowledgedAt === null && completedAt === null && cancelledAt !== null;
    case "FAILED":
      return completedAt === null && cancelledAt === null;
  }
}

export function isServiceRequestRecord(value: unknown): value is ServiceRequestRecord {
  if (!Value.Check(ServiceRequestRecordSchema, value)) return false;
  const record = value as ServiceRequestRecord;
  const assigned = record.assignedActorId !== null;
  if (record.type === "OPEN_TABLE") {
    if (record.status === "COMPLETED" ? record.sessionId === null : record.sessionId !== null) {
      return false;
    }
  } else if (
    (record.status === "PENDING" ||
      record.status === "ACKNOWLEDGED" ||
      record.status === "CANCELLED") &&
    record.sessionId === null
  ) {
    return false;
  }
  if (record.status !== "FAILED" && record.failureMessage !== null) return false;
  if (assigned !== (record.assignedActorDisplayName !== null)) return false;
  if (record.status === "PENDING" && assigned) return false;
  if (record.status === "CANCELLED" && assigned) return false;
  if (record.status === "ACKNOWLEDGED" || record.status === "COMPLETED") {
    if (!assigned) return false;
  }
  if (record.status === "FAILED" && (record.acknowledgedAt !== null) !== assigned) return false;
  return isServiceRequestTimeline(record);
}

export function isServiceRequestReadModel(value: unknown): value is ServiceRequestReadModel {
  if (!Value.Check(ServiceRequestReadModelSchema, value)) return false;
  const record = value as ServiceRequestReadModel;
  const assigned = record.assignedActorDisplayName !== null;
  if (record.type === "OPEN_TABLE") {
    if (record.status === "COMPLETED" ? record.sessionId === null : record.sessionId !== null) {
      return false;
    }
  } else if (
    (record.status === "PENDING" ||
      record.status === "ACKNOWLEDGED" ||
      record.status === "CANCELLED") &&
    record.sessionId === null
  ) {
    return false;
  }
  if (record.status !== "FAILED" && record.failureMessage !== null) return false;
  if (record.status === "PENDING" && (assigned || record.assignedToCurrentActor)) return false;
  if (record.status === "CANCELLED" && (assigned || record.assignedToCurrentActor)) return false;
  if (record.status === "ACKNOWLEDGED" || record.status === "COMPLETED") {
    if (!assigned) return false;
  }
  if (record.status === "FAILED" && (record.acknowledgedAt !== null) !== assigned) return false;
  if (record.assignedToCurrentActor && !assigned) return false;
  return isServiceRequestTimeline(record);
}

export function isServiceRequestReadModelList(value: unknown): value is ServiceRequestReadModel[] {
  if (!Array.isArray(value) || !value.every(isServiceRequestReadModel)) return false;
  if (new Set(value.map((record) => record.eventId)).size !== value.length) return false;
  return value.every((record, index) => {
    const previous = value[index - 1];
    return (
      previous === undefined || Date.parse(previous.requestedAt) >= Date.parse(record.requestedAt)
    );
  });
}

export function isActiveServiceRequestReadModelList(
  value: unknown,
): value is ServiceRequestReadModel[] {
  return (
    isServiceRequestReadModelList(value) &&
    value.every((record) => record.status === "PENDING" || record.status === "ACKNOWLEDGED")
  );
}

export const ServiceRequestHistoryPageSchema = Type.Object(
  {
    items: Type.Array(ServiceRequestReadModelSchema),
    nextCursor: Type.Union([Type.String({ minLength: 32, maxLength: 1024 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type ServiceRequestHistoryPage = Static<typeof ServiceRequestHistoryPageSchema>;

export function isServiceRequestHistoryPage(value: unknown): value is ServiceRequestHistoryPage {
  if (!Value.Check(ServiceRequestHistoryPageSchema, value)) return false;
  const page = value as ServiceRequestHistoryPage;
  if (!isServiceRequestReadModelList(page.items)) return false;
  if (
    !page.items.every(
      (item) =>
        item.status === "COMPLETED" || item.status === "CANCELLED" || item.status === "FAILED",
    )
  ) {
    return false;
  }
  return page.nextCursor === null || page.items.length > 0;
}

export const TableServiceRequestAttentionStatusSchema = Type.Union([
  Type.Literal("WAITING"),
  Type.Literal("DUE_SOON"),
  Type.Literal("STORE_NOTIFIED"),
]);
export type TableServiceRequestAttentionStatus = Static<
  typeof TableServiceRequestAttentionStatusSchema
>;

export const TableServiceRequestAttentionSchema = Type.Object(
  {
    status: TableServiceRequestAttentionStatusSchema,
    dueAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    notifiedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type TableServiceRequestAttention = Static<typeof TableServiceRequestAttentionSchema>;

export const TableServiceRequestReadModelSchema = Type.Object(
  {
    eventId: Type.String({ minLength: 8 }),
    tableNodeId: Type.String({ minLength: 1 }),
    sessionId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    type: ServiceRequestTypeSchema,
    status: ServiceRequestStatusSchema,
    failureMessage: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    requestedAt: Type.String({ format: "date-time" }),
    acknowledgedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    cancelledAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    customerAttention: Type.Union([TableServiceRequestAttentionSchema, Type.Null()]),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type TableServiceRequestReadModel = Static<typeof TableServiceRequestReadModelSchema>;

export function isTableServiceRequestReadModelList(
  value: unknown,
  expectedTableNodeId?: string,
): value is TableServiceRequestReadModel[] {
  if (!Array.isArray(value) || value.length > 4) return false;
  if (!value.every((item) => Value.Check(TableServiceRequestReadModelSchema, item))) return false;
  const records = value as TableServiceRequestReadModel[];
  if (new Set(records.map((record) => record.eventId)).size !== records.length) return false;
  if (new Set(records.map((record) => record.type)).size !== records.length) return false;
  if (
    expectedTableNodeId !== undefined &&
    records.some((record) => record.tableNodeId !== expectedTableNodeId)
  ) {
    return false;
  }
  return records.every((record, index) => {
    if (record.type === "OPEN_TABLE") {
      if (record.status === "COMPLETED" ? record.sessionId === null : record.sessionId !== null) {
        return false;
      }
    } else if (record.sessionId === null) {
      return false;
    }
    if (record.status !== "FAILED" && record.failureMessage !== null) return false;
    if (!isServiceRequestTimeline(record)) return false;
    if (record.status !== "PENDING") {
      if (record.customerAttention !== null) return false;
    } else {
      const attention = record.customerAttention;
      if (attention === null) return false;
      const requestedAt = Date.parse(record.requestedAt);
      const dueAt = attention.dueAt === null ? null : Date.parse(attention.dueAt);
      const notifiedAt = attention.notifiedAt === null ? null : Date.parse(attention.notifiedAt);
      if (
        (dueAt !== null && (!Number.isFinite(dueAt) || dueAt < requestedAt)) ||
        (notifiedAt !== null && !Number.isFinite(notifiedAt)) ||
        (attention.status === "WAITING" && attention.notifiedAt !== null) ||
        (attention.status === "DUE_SOON" &&
          (attention.dueAt === null || attention.notifiedAt !== null)) ||
        (attention.status === "STORE_NOTIFIED" &&
          (dueAt === null || notifiedAt === null || attention.notifiedAt !== attention.dueAt))
      ) {
        return false;
      }
    }
    const previous = records[index - 1];
    if (!previous) return true;
    const previousTime = Date.parse(previous.requestedAt);
    const currentTime = Date.parse(record.requestedAt);
    return (
      previousTime > currentTime ||
      (previousTime === currentTime && previous.eventId.localeCompare(record.eventId) <= 0)
    );
  });
}

export const ServiceRequestTransitionSchema = Type.Object(
  { idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }) },
  { additionalProperties: false },
);

const CancelServiceRequestCommandFields = {
  eventId: Type.String({ minLength: 8, maxLength: 128 }),
  idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  occurredAt: Type.String({ format: "date-time" }),
};

export const CancelServiceRequestCommandSchema = Type.Union([
  Type.Object(
    {
      ...CancelServiceRequestCommandFields,
      sessionId: Type.Null(),
      type: Type.Literal("OPEN_TABLE"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...CancelServiceRequestCommandFields,
      sessionId: Type.String({ minLength: 1, maxLength: 128 }),
      type: Type.Union([Type.Literal("SERVICE"), Type.Literal("COACH"), Type.Literal("CHECKOUT")]),
    },
    { additionalProperties: false },
  ),
]);
export type CancelServiceRequestCommand = Static<typeof CancelServiceRequestCommandSchema>;

const EdgeEventCommonFields = {
  contractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
  eventId: Type.String({ minLength: 8 }),
  tenantId: Type.String({ minLength: 1 }),
  storeId: Type.String({ minLength: 1 }),
  deviceId: Type.String({ minLength: 1 }),
  tableNodeId: Type.String({ minLength: 1 }),
  bootSessionId: Type.String({ minLength: 1 }),
  deviceSequence: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  businessIdempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  occurredAt: Type.String({ format: "date-time" }),
};

export const EdgeEventSchema = Type.Union([
  Type.Object(
    {
      ...EdgeEventCommonFields,
      expectedTableRevision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      type: Type.Literal("OPEN_TABLE"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...EdgeEventCommonFields,
      sessionId: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
      type: Type.Union([Type.Literal("SERVICE"), Type.Literal("COACH"), Type.Literal("CHECKOUT")]),
    },
    { additionalProperties: false },
  ),
]);
export interface EdgeEvent {
  contractVersion: typeof EDGE_CONTRACT_VERSION;
  eventId: string;
  tenantId: string;
  storeId: string;
  deviceId: string;
  tableNodeId: string;
  sessionId?: string;
  expectedTableRevision?: number;
  bootSessionId: string;
  deviceSequence: number;
  businessIdempotencyKey: string;
  type: ServiceRequestType;
  occurredAt: string;
}

export const EdgeEventAckSchema = Type.Object({
  eventId: Type.String(),
  accepted: Type.Boolean(),
  duplicate: Type.Boolean(),
  cursor: Type.Integer({ minimum: 1 }),
});
export type EdgeEventAck = Static<typeof EdgeEventAckSchema>;

export const ApiErrorSchema = Type.Object({
  code: Type.String(),
  message: Type.String(),
  requestId: Type.String(),
  memberProfileUpdateGuardState: Type.Optional(Type.Literal("CLEARED")),
  memberCreateGuardState: Type.Optional(Type.Literal("CLEARED")),
  workforceWechatRebindGuardState: Type.Optional(Type.Literal("CLEARED")),
  workforcePhoneChangeGuardState: Type.Optional(Type.Literal("CLEARED")),
});
export type ApiError = Static<typeof ApiErrorSchema>;

const PRODUCT_VERSION_PATTERN = "^(0|[1-9]\\d{0,8})\\.(0|[1-9]\\d{0,8})\\.(0|[1-9]\\d{0,8})$";

export const MobileClientPolicySchema = Type.Object(
  {
    latestVersion: Type.String({ pattern: PRODUCT_VERSION_PATTERN }),
    minimumSupportedVersion: Type.String({ pattern: PRODUCT_VERSION_PATTERN }),
  },
  { additionalProperties: false },
);
export type MobileClientPolicy = Static<typeof MobileClientPolicySchema>;

export function compareProductVersions(left: string, right: string): number {
  const parse = (value: string): [number, number, number] => {
    const match = /^(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})$/.exec(value);
    if (!match) throw new Error(`invalid product version: ${value}`);
    return [Number(match[1]), Number(match[2]), Number(match[3])];
  };
  const [leftMajor, leftMinor, leftPatch] = parse(left);
  const [rightMajor, rightMinor, rightPatch] = parse(right);
  const differences = [leftMajor - rightMajor, leftMinor - rightMinor, leftPatch - rightPatch];
  for (const difference of differences) {
    if (difference !== 0) return difference > 0 ? 1 : -1;
  }
  return 0;
}

export const VersionInfoSchema = Type.Object({
  productVersion: Type.String(),
  buildNumber: Type.String(),
  gitCommit: Type.String({ minLength: 3, maxLength: 64 }),
  buildTime: Type.String({ minLength: 1, maxLength: 64 }),
  deploymentEnvironment: Type.String({ minLength: 1, maxLength: 32 }),
  schemaVersion: Type.Integer({ minimum: 0 }),
  edgeContractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
  enabledFeatures: Type.Array(Type.String()),
  evidence: EvidenceLabelSchema,
  mobileClientPolicy: Type.Optional(MobileClientPolicySchema),
});
export type VersionInfo = Static<typeof VersionInfoSchema>;

export const ProductCatalogImportRowSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    unitPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type ProductCatalogImportRow = Static<typeof ProductCatalogImportRowSchema>;

export const ProductCatalogPreviewChangedFieldSchema = Type.Union([
  Type.Literal("NAME"),
  Type.Literal("UNIT_PRICE"),
  Type.Literal("ENABLED"),
]);
export type ProductCatalogPreviewChangedField = Static<
  typeof ProductCatalogPreviewChangedFieldSchema
>;

export const ProductCatalogPreviewRowSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    unitPriceFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    enabled: Type.Boolean(),
    outcome: Type.Union([
      Type.Literal("CREATE"),
      Type.Literal("UPDATE"),
      Type.Literal("UNCHANGED"),
    ]),
    changedFields: Type.Array(ProductCatalogPreviewChangedFieldSchema, { maxItems: 3 }),
  },
  { additionalProperties: false },
);
export type ProductCatalogPreviewRow = Static<typeof ProductCatalogPreviewRowSchema>;

export const ProductCatalogPreviewResultSchema = Type.Object(
  {
    rows: Type.Array(ProductCatalogPreviewRowSchema, { maxItems: 500 }),
    errors: Type.Array(
      Type.Object(
        {
          line: Type.Integer({ minimum: 0 }),
          field: Type.String({ minLength: 1, maxLength: 64 }),
          message: Type.String({ minLength: 1, maxLength: 512 }),
        },
        { additionalProperties: false },
      ),
      { maxItems: 2_000 },
    ),
    fingerprint: Type.String({ pattern: "^[a-f0-9]{16}$" }),
    expectedConfigurationVersion: Type.Integer({ minimum: 1 }),
    comparisonEvidence: EvidenceLabelSchema,
    summary: Type.Object(
      {
        create: Type.Integer({ minimum: 0 }),
        update: Type.Integer({ minimum: 0 }),
        unchanged: Type.Integer({ minimum: 0 }),
      },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export type ProductCatalogPreviewResult = Static<typeof ProductCatalogPreviewResultSchema>;

export function isProductCatalogPreviewResult(
  value: unknown,
): value is ProductCatalogPreviewResult {
  if (!Value.Check(ProductCatalogPreviewResultSchema, value) || !hasOnlySafeIntegers(value)) {
    return false;
  }
  if (
    value.summary.create !== value.rows.filter((row) => row.outcome === "CREATE").length ||
    value.summary.update !== value.rows.filter((row) => row.outcome === "UPDATE").length ||
    value.summary.unchanged !== value.rows.filter((row) => row.outcome === "UNCHANGED").length ||
    new Set(value.rows.map((row) => row.skuId)).size !== value.rows.length
  ) {
    return false;
  }
  return value.rows.every((row) => {
    const changedFields = new Set(row.changedFields);
    return (
      changedFields.size === row.changedFields.length &&
      (row.outcome === "UPDATE" ? row.changedFields.length > 0 : row.changedFields.length === 0)
    );
  });
}

export const ProductCatalogImportRequestSchema = Type.Object(
  {
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    expectedConfigurationVersion: Type.Integer({ minimum: 1 }),
    rows: Type.Array(ProductCatalogImportRowSchema, { minItems: 1, maxItems: 500 }),
  },
  { additionalProperties: false },
);
export type ProductCatalogImportRequest = Static<typeof ProductCatalogImportRequestSchema>;

export const ProductCatalogImportResultSchema = Type.Object(
  {
    idempotencyKey: Type.String(),
    imported: Type.Integer({ minimum: 0 }),
    unchanged: Type.Integer({ minimum: 0 }),
    configurationVersion: Type.Integer({ minimum: 1 }),
    appliedAt: Type.String({ format: "date-time" }),
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: false },
);
export type ProductCatalogImportResult = Static<typeof ProductCatalogImportResultSchema>;

export function isProductCatalogImportResult(value: unknown): value is ProductCatalogImportResult {
  return (
    Value.Check(ProductCatalogImportResultSchema, value) &&
    hasOnlySafeIntegers(value) &&
    isCanonicalIsoTimestamp(value.appliedAt)
  );
}

export const ConnectivityModeSchema = Type.Union([
  Type.Literal("ONLINE"),
  Type.Literal("OFFLINE"),
  Type.Literal("RECOVERING"),
  Type.Literal("AUTHORITY_EXPIRED"),
]);
export type ConnectivityMode = Static<typeof ConnectivityModeSchema>;

export const SyncStageSchema = Type.Union([
  Type.Literal("IDLE"),
  Type.Literal("UPLOADING_OFFLINE_ORDERS"),
  Type.Literal("RECONCILING_PAYMENTS"),
  Type.Literal("PULLING_CONFIGURATION"),
  Type.Literal("CHECKING_CONFLICTS"),
]);
export type SyncStage = Static<typeof SyncStageSchema>;

export const PaymentMethodSchema = Type.Union([
  Type.Literal("CASH"),
  Type.Literal("STATIC_WECHAT"),
  Type.Literal("STATIC_ALIPAY"),
  Type.Literal("MEMBER_BALANCE"),
  Type.Literal("WECHAT_PAY"),
  Type.Literal("ALIPAY"),
]);
export type PaymentMethod = Static<typeof PaymentMethodSchema>;

export const PaymentStatusSchema = Type.Union([
  Type.Literal("CONFIRMED_LOCAL"),
  Type.Literal("PENDING_RECONCILIATION"),
  Type.Literal("PENDING_MEMBER_DEBIT"),
  Type.Literal("MEMBER_RECEIVABLE"),
  Type.Literal("MANUALLY_RECONCILED"),
  Type.Literal("RECONCILIATION_EXCEPTION"),
  Type.Literal("PARTIALLY_REFUNDED"),
  Type.Literal("REFUNDED"),
  Type.Literal("CONFIRMED_CLOUD"),
]);
export type PaymentStatus = Static<typeof PaymentStatusSchema>;

export const ConditionalModuleCodeSchema = Type.Union([
  Type.Literal("MEMBER"),
  Type.Literal("GROUP_BUY"),
  Type.Literal("PRODUCT_ORDER"),
  Type.Literal("RESERVATION"),
  Type.Literal("CUSTOMER_SELF_SERVICE_RESERVATION"),
  Type.Literal("COACH"),
  Type.Literal("CUSTOMER_STORAGE"),
  Type.Literal("CUSTOMER_RENTAL"),
]);
export type ConditionalModuleCode = Static<typeof ConditionalModuleCodeSchema>;

export const PriceTimeBandSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64 }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    startMinute: Type.Integer({ minimum: 0, maximum: 1_439 }),
    endMinute: Type.Integer({ minimum: 1, maximum: 1_440 }),
    daysOfWeek: Type.Optional(
      Type.Array(Type.Integer({ minimum: 0, maximum: 6 }), {
        minItems: 1,
        maxItems: 7,
        uniqueItems: true,
      }),
    ),
    hourlyRateFen: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type PriceTimeBand = Static<typeof PriceTimeBandSchema>;

export const PriceSnapshotSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    version: Type.Integer({ minimum: 1 }),
    name: Type.String({ minLength: 1 }),
    hourlyRateFen: Type.Integer({ minimum: 0 }),
    roundingMinutes: Type.Integer({ minimum: 1 }),
    minimumMinutes: Type.Integer({ minimum: 0 }),
    minimumAmountFen: Type.Optional(Type.Integer({ minimum: 0 })),
    billingMode: Type.Optional(Type.Union([Type.Literal("HOURLY"), Type.Literal("PACKAGE")])),
    packageMinutes: Type.Optional(Type.Integer({ minimum: 1, maximum: 10_080 })),
    packagePriceFen: Type.Optional(Type.Integer({ minimum: 0 })),
    roundingMode: Type.Optional(
      Type.Union([Type.Literal("UP"), Type.Literal("NEAREST"), Type.Literal("DOWN")]),
    ),
    roundingScope: Type.Optional(Type.Union([Type.Literal("SESSION"), Type.Literal("SEGMENT")])),
    pauseBillingMode: Type.Optional(Type.Union([Type.Literal("CHARGE"), Type.Literal("FREE")])),
    transferPricingMode: Type.Optional(
      Type.Union([Type.Literal("CONTINUE"), Type.Literal("REPRICE")]),
    ),
    timeZone: Type.Optional(Type.String({ minLength: 1, maxLength: 80 })),
    businessDayCutover: Type.Optional(Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" })),
    timeBands: Type.Optional(Type.Array(PriceTimeBandSchema, { maxItems: 64 })),
    effectiveAt: Type.String({ format: "date-time" }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type PriceSnapshot = Static<typeof PriceSnapshotSchema>;

function priceSnapshotTimeBandsAreCoherent(snapshot: PriceSnapshot): boolean {
  const timeBands = snapshot.timeBands ?? [];
  if (timeBands.length > 0 && (!snapshot.timeZone || !snapshot.businessDayCutover)) return false;
  if (snapshot.timeZone) {
    try {
      new Intl.DateTimeFormat("zh-CN", { timeZone: snapshot.timeZone }).format(new Date(0));
    } catch {
      return false;
    }
  }

  const timeBandIds = new Set<string>();
  for (const band of timeBands) {
    const normalizedId = band.id.trim().toLocaleLowerCase("zh-CN");
    if (
      normalizedId.length === 0 ||
      timeBandIds.has(normalizedId) ||
      !/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/u.test(band.id) ||
      band.name !== band.name.trim() ||
      band.startMinute >= band.endMinute ||
      ![band.startMinute, band.endMinute, band.hourlyRateFen].every(Number.isSafeInteger)
    ) {
      return false;
    }
    timeBandIds.add(normalizedId);
  }
  for (let day = 0; day < 7; day += 1) {
    const bandsForDay = timeBands
      .filter((band) => !band.daysOfWeek || band.daysOfWeek.includes(day))
      .sort((left, right) => left.startMinute - right.startMinute);
    for (const [index, band] of bandsForDay.entries()) {
      const previousBand = index > 0 ? bandsForDay[index - 1] : undefined;
      if (previousBand && previousBand.endMinute > band.startMinute) return false;
    }
  }
  return true;
}

function priceSnapshotCanBeSelected(snapshot: PriceSnapshot): boolean {
  return (
    /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/u.test(snapshot.id) &&
    snapshot.name === snapshot.name.trim() &&
    priceSnapshotTimeBandsAreCoherent(snapshot)
  );
}

export const FrontdeskPricingOptionsReadModelSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    tableId: Type.String({ minLength: 1, maxLength: 128 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    tableRevision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    configurationVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    defaultPricingPlanId: Type.String({ minLength: 1, maxLength: 64 }),
    options: Type.Array(PriceSnapshotSchema, { minItems: 1, maxItems: 64 }),
    authoritativeAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type FrontdeskPricingOptionsReadModel = Static<
  typeof FrontdeskPricingOptionsReadModelSchema
>;

export function isFrontdeskPricingOptionsReadModel(
  value: unknown,
): value is FrontdeskPricingOptionsReadModel {
  if (!Value.Check(FrontdeskPricingOptionsReadModelSchema, value)) return false;
  if (!isCanonicalIsoTimestamp(value.authoritativeAt)) return false;
  const authoritativeAt = Date.parse(value.authoritativeAt);
  const optionIds = new Set<string>();
  const optionNames = new Set<string>();
  for (const option of value.options) {
    const normalizedId = option.id.trim().toLocaleLowerCase("zh-CN");
    const normalizedName = option.name.trim().toLocaleLowerCase("zh-CN");
    const billingMode = option.billingMode ?? "HOURLY";
    if (
      optionIds.has(normalizedId) ||
      optionNames.has(normalizedName) ||
      !/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/u.test(option.id) ||
      option.name !== option.name.trim() ||
      [...option.name].length > 80 ||
      !isCanonicalIsoTimestamp(option.effectiveAt) ||
      Date.parse(option.effectiveAt) > authoritativeAt ||
      ![
        option.version,
        option.hourlyRateFen,
        option.roundingMinutes,
        option.minimumMinutes,
        option.minimumAmountFen ?? 0,
      ].every(Number.isSafeInteger) ||
      (billingMode === "PACKAGE" &&
        (option.packageMinutes === undefined || option.packagePriceFen === undefined)) ||
      (billingMode === "HOURLY" &&
        (option.packageMinutes !== undefined ||
          (option.packagePriceFen !== undefined && option.packagePriceFen !== 0))) ||
      !priceSnapshotCanBeSelected(option)
    ) {
      return false;
    }
    optionIds.add(normalizedId);
    optionNames.add(normalizedName);
  }
  return value.options.some((option) => option.id === value.defaultPricingPlanId);
}

export const BusinessHoursRuleSchema = Type.Object(
  {
    dayOfWeek: Type.Integer({ minimum: 0, maximum: 6 }),
    closed: Type.Boolean(),
    openTime: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    closeTime: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
  },
  { additionalProperties: false },
);
export type BusinessHoursRule = Static<typeof BusinessHoursRuleSchema>;

export const DEFAULT_RESERVATION_NO_SHOW_GRACE_MINUTES = 15;
export const MAX_RESERVATION_NO_SHOW_GRACE_MINUTES = 240;

export const VenueOperatingSettingsInputSchema = Type.Object(
  {
    storeName: Type.String({ minLength: 1, maxLength: 80 }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    businessHours: Type.Array(BusinessHoursRuleSchema, { minItems: 7, maxItems: 7 }),
    manualPaymentMethods: Type.Object(
      {
        cash: Type.Boolean(),
        staticWechat: Type.Boolean(),
        staticAlipay: Type.Boolean(),
      },
      { additionalProperties: false },
    ),
    longSessionWarningMinutes: Type.Optional(
      Type.Integer({ minimum: 60, maximum: 100_800, default: 720 }),
    ),
    longSessionCriticalMinutes: Type.Optional(
      Type.Integer({ minimum: 61, maximum: 100_800, default: 1_440 }),
    ),
    memberLifecycleDormantAfterDays: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 3_650, default: 30 }),
    ),
    memberLifecycleLostAfterDays: Type.Optional(
      Type.Integer({ minimum: 2, maximum: 7_300, default: 90 }),
    ),
    reservationNoShowGraceMinutes: Type.Optional(
      Type.Integer({
        minimum: 0,
        maximum: MAX_RESERVATION_NO_SHOW_GRACE_MINUTES,
        default: DEFAULT_RESERVATION_NO_SHOW_GRACE_MINUTES,
      }),
    ),
    coachRotationEnabled: Type.Optional(Type.Boolean({ default: false })),
  },
  { additionalProperties: false },
);
export type VenueOperatingSettingsInput = Static<typeof VenueOperatingSettingsInputSchema>;

export const OperatingPricingPlanInputSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    enabled: Type.Boolean(),
    hourlyRateFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    roundingMinutes: Type.Integer({ minimum: 1, maximum: 180 }),
    roundingMode: Type.Union([Type.Literal("UP"), Type.Literal("NEAREST"), Type.Literal("DOWN")]),
    roundingScope: Type.Union([Type.Literal("SESSION"), Type.Literal("SEGMENT")]),
    minimumMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    minimumAmountFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    billingMode: Type.Union([Type.Literal("HOURLY"), Type.Literal("PACKAGE")]),
    packageMinutes: Type.Union([Type.Null(), Type.Integer({ minimum: 1, maximum: 10_080 })]),
    packagePriceFen: Type.Union([Type.Null(), Type.Integer({ minimum: 0 })]),
    pauseBillingMode: Type.Union([Type.Literal("CHARGE"), Type.Literal("FREE")]),
    transferPricingMode: Type.Union([Type.Literal("CONTINUE"), Type.Literal("REPRICE")]),
    timeBands: Type.Array(PriceTimeBandSchema, { maxItems: 64 }),
  },
  { additionalProperties: false },
);
export type OperatingPricingPlanInput = Static<typeof OperatingPricingPlanInputSchema>;

const operatingPricingPlanNameMaximumLength = 80;

function normalizedOperatingPricingPlanName(value: string): string {
  return value.trim().toLocaleLowerCase("zh-CN");
}

function uniqueOperatingPricingPlanName(
  baseName: string,
  existingNames: readonly string[],
  suffix: (attempt: number) => string,
): string {
  const normalizedExistingNames = new Set(existingNames.map(normalizedOperatingPricingPlanName));
  const normalizedBaseName = baseName.trim() || "价格方案";
  for (let attempt = 1; attempt <= existingNames.length + 2; attempt += 1) {
    const candidateSuffix = suffix(attempt);
    const availableBaseLength = operatingPricingPlanNameMaximumLength - candidateSuffix.length;
    const candidate = `${normalizedBaseName.slice(0, availableBaseLength).trimEnd()}${candidateSuffix}`;
    if (!normalizedExistingNames.has(normalizedOperatingPricingPlanName(candidate)))
      return candidate;
  }
  throw new RangeError("无法生成唯一的价格方案名称");
}

export function createOperatingPricingPlanDraft(
  id: string,
  existingNames: readonly string[] = [],
): OperatingPricingPlanInput {
  return {
    id,
    name: uniqueOperatingPricingPlanName("新价格方案", existingNames, (attempt) =>
      attempt === 1 ? "" : ` ${attempt}`,
    ),
    enabled: false,
    hourlyRateFen: 3_800,
    roundingMinutes: 1,
    roundingMode: "UP",
    roundingScope: "SEGMENT",
    minimumMinutes: 0,
    minimumAmountFen: 0,
    billingMode: "HOURLY",
    packageMinutes: null,
    packagePriceFen: null,
    pauseBillingMode: "FREE",
    transferPricingMode: "CONTINUE",
    timeBands: [],
  };
}

export function cloneOperatingPricingPlanDraft(
  source: OperatingPricingPlanInput,
  input: {
    id: string;
    existingNames: readonly string[];
    timeBandIds: readonly string[];
  },
): OperatingPricingPlanInput {
  if (source.id === input.id) throw new RangeError("克隆价格方案必须使用新的方案编号");
  if (source.timeBands.length !== input.timeBandIds.length) {
    throw new RangeError("克隆价格方案必须为每个计费时段生成新的编号");
  }
  const normalizedTimeBandIds = input.timeBandIds.map((id) => id.trim().toLocaleLowerCase("zh-CN"));
  if (
    new Set(normalizedTimeBandIds).size !== normalizedTimeBandIds.length ||
    source.timeBands.some(
      (band, index) => normalizedTimeBandIds[index] === band.id.toLocaleLowerCase("zh-CN"),
    )
  ) {
    throw new RangeError("克隆价格方案的计费时段编号必须重新生成且不能重复");
  }
  const sourceName = source.name.trim().replace(/ 副本(?: \d+)?$/u, "") || "价格方案";
  return {
    ...source,
    id: input.id,
    name: uniqueOperatingPricingPlanName(
      sourceName,
      input.existingNames,
      (attempt) => ` 副本${attempt === 1 ? "" : ` ${attempt}`}`,
    ),
    enabled: false,
    timeBands: source.timeBands.map((band, index) => {
      const timeBandId = input.timeBandIds[index];
      if (timeBandId === undefined) {
        throw new RangeError("克隆价格方案必须为每个计费时段生成新的编号");
      }
      return {
        ...band,
        id: timeBandId,
        ...(band.daysOfWeek ? { daysOfWeek: [...band.daysOfWeek] } : {}),
      };
    }),
  };
}

export const TableOperatingConfigurationInputSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    tableNumber: Type.String({ minLength: 1, maxLength: 20 }),
    area: Type.String({ minLength: 1, maxLength: 40 }),
    tableType: Type.String({ minLength: 1, maxLength: 40 }),
    tableNodeId: Type.String({ minLength: 1, maxLength: 80 }),
    pricingPlanId: Type.String({ minLength: 1, maxLength: 64 }),
    lightCircuitId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type TableOperatingConfigurationInput = Static<
  typeof TableOperatingConfigurationInputSchema
>;

export const VenueDeviceMappingInputSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 80, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    kind: Type.Union([
      Type.Literal("LIGHT_CONTROLLER"),
      Type.Literal("PRINTER"),
      Type.Literal("TABLE_EDGE_NODE"),
    ]),
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    tableId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    address: Type.String({ minLength: 1, maxLength: 160 }),
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type VenueDeviceMappingInput = Static<typeof VenueDeviceMappingInputSchema>;

export const OperatingConfigurationDraftSchema = Type.Object(
  {
    settings: VenueOperatingSettingsInputSchema,
    selectedConditionalModules: Type.Optional(
      Type.Array(ConditionalModuleCodeSchema, { maxItems: 8, uniqueItems: true }),
    ),
    pricingPlans: Type.Array(OperatingPricingPlanInputSchema, { minItems: 1, maxItems: 64 }),
    tables: Type.Array(TableOperatingConfigurationInputSchema, { minItems: 1, maxItems: 256 }),
    deviceMappings: Type.Array(VenueDeviceMappingInputSchema, { maxItems: 512 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationDraft = Static<typeof OperatingConfigurationDraftSchema>;

export const OperatingConfigurationPreviewRequestSchema = Type.Object(
  { draft: OperatingConfigurationDraftSchema },
  { additionalProperties: false },
);
export type OperatingConfigurationPreviewRequest = Static<
  typeof OperatingConfigurationPreviewRequestSchema
>;

export const OperatingConfigurationApplyRequestSchema = Type.Object(
  {
    draft: OperatingConfigurationDraftSchema,
    expectedConfigurationVersion: Type.Integer({ minimum: 0 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationApplyRequest = Static<
  typeof OperatingConfigurationApplyRequestSchema
>;

export function isOperatingConfigurationApplyRequest(
  value: unknown,
): value is OperatingConfigurationApplyRequest {
  return Value.Check(OperatingConfigurationApplyRequestSchema, value);
}

export const OperatingConfigurationIssueSchema = Type.Object(
  {
    path: Type.String({ minLength: 1 }),
    message: Type.String({ minLength: 1 }),
  },
  { additionalProperties: false },
);

export const OperatingConfigurationPreviewResultSchema = Type.Object(
  {
    valid: Type.Boolean(),
    errors: Type.Array(OperatingConfigurationIssueSchema),
    warnings: Type.Array(OperatingConfigurationIssueSchema),
    summary: Type.Array(Type.String(), { minItems: 1, maxItems: 100 }),
    nextConfigurationVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationPreviewResult = Static<
  typeof OperatingConfigurationPreviewResultSchema
>;

export function isOperatingConfigurationPreviewResult(
  value: unknown,
): value is OperatingConfigurationPreviewResult {
  if (!Value.Check(OperatingConfigurationPreviewResultSchema, value)) return false;
  if (!Number.isSafeInteger(value.nextConfigurationVersion)) return false;
  if (value.valid !== (value.errors.length === 0)) return false;
  if (value.errors.length > 1_024 || value.warnings.length > 1_024) {
    return false;
  }
  return (
    [...value.errors, ...value.warnings].every(
      (issue) => issue.path.length <= 256 && issue.message.length <= 1_000,
    ) && value.summary.every((line) => line.length <= 1_000)
  );
}

export const OperatingConfigurationBundleSchema = Type.Object(
  {
    initialized: Type.Boolean(),
    configurationVersion: Type.Integer({ minimum: 0 }),
    draft: OperatingConfigurationDraftSchema,
    updatedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    updatedBy: Type.Union([Type.String(), Type.Null()]),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationBundle = Static<typeof OperatingConfigurationBundleSchema>;

function stableOperatingConfigurationJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableOperatingConfigurationJson(item)).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableOperatingConfigurationJson(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export function operatingConfigurationDraftsEqual(
  left: OperatingConfigurationDraft,
  right: OperatingConfigurationDraft,
): boolean {
  return stableOperatingConfigurationJson(left) === stableOperatingConfigurationJson(right);
}

function operatingConfigurationDraftReferencesAreConsistent(
  draft: OperatingConfigurationDraft,
): boolean {
  const selectedConditionalModules = draft.selectedConditionalModules ?? [];
  const normalizedUnique = (values: string[]): boolean => {
    const normalized = values.map((value) => value.trim().toLocaleLowerCase("zh-CN"));
    return new Set(normalized).size === normalized.length;
  };
  const days = draft.settings.businessHours.map((rule) => rule.dayOfWeek);
  if (new Set(days).size !== 7 || ![0, 1, 2, 3, 4, 5, 6].every((day) => days.includes(day))) {
    return false;
  }
  if (!draft.settings.manualPaymentMethods.cash) return false;
  if (
    draft.settings.longSessionWarningMinutes !== undefined &&
    draft.settings.longSessionCriticalMinutes !== undefined &&
    draft.settings.longSessionCriticalMinutes <= draft.settings.longSessionWarningMinutes
  ) {
    return false;
  }
  const dormantAfterDays = draft.settings.memberLifecycleDormantAfterDays ?? 30;
  const lostAfterDays = draft.settings.memberLifecycleLostAfterDays ?? 90;
  if (lostAfterDays <= dormantAfterDays) return false;
  if (
    selectedConditionalModules.includes("CUSTOMER_SELF_SERVICE_RESERVATION") &&
    !selectedConditionalModules.includes("RESERVATION")
  ) {
    return false;
  }
  if (
    selectedConditionalModules.includes("CUSTOMER_STORAGE") &&
    (!selectedConditionalModules.includes("MEMBER") ||
      !selectedConditionalModules.includes("PRODUCT_ORDER"))
  ) {
    return false;
  }
  if (
    selectedConditionalModules.includes("CUSTOMER_RENTAL") &&
    !selectedConditionalModules.includes("PRODUCT_ORDER")
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: draft.settings.timeZone }).format(new Date());
  } catch {
    return false;
  }
  if (
    !normalizedUnique(draft.pricingPlans.map((plan) => plan.id)) ||
    !normalizedUnique(draft.tables.map((table) => table.tableId)) ||
    !normalizedUnique(draft.tables.map((table) => table.tableNumber)) ||
    !normalizedUnique(draft.tables.map((table) => table.tableNodeId)) ||
    !normalizedUnique(draft.deviceMappings.map((mapping) => mapping.id))
  ) {
    return false;
  }
  const enabledPlans = new Set(
    draft.pricingPlans.filter((plan) => plan.enabled).map((plan) => plan.id),
  );
  if (enabledPlans.size === 0 || !draft.tables.some((table) => table.enabled)) return false;
  if (draft.tables.some((table) => table.enabled && !enabledPlans.has(table.pricingPlanId))) {
    return false;
  }
  const tableIds = new Set(draft.tables.map((table) => table.tableId));
  if (draft.deviceMappings.some((mapping) => mapping.tableId && !tableIds.has(mapping.tableId))) {
    return false;
  }
  for (const plan of draft.pricingPlans) {
    if (
      (plan.billingMode === "PACKAGE" &&
        (plan.packageMinutes === null || plan.packagePriceFen === null)) ||
      (plan.billingMode === "HOURLY" &&
        (plan.packageMinutes !== null || plan.packagePriceFen !== null))
    ) {
      return false;
    }
    for (let day = 0; day < 7; day += 1) {
      const bands = plan.timeBands
        .filter((band) => !band.daysOfWeek || band.daysOfWeek.includes(day))
        .sort((left, right) => left.startMinute - right.startMinute);
      for (const [index, band] of bands.entries()) {
        if (band.startMinute >= band.endMinute) return false;
        const previousBand = index > 0 ? bands[index - 1] : undefined;
        if (previousBand && previousBand.endMinute > band.startMinute) return false;
      }
    }
  }
  return true;
}

export function isOperatingConfigurationBundle(
  value: unknown,
): value is OperatingConfigurationBundle {
  if (!Value.Check(OperatingConfigurationBundleSchema, value)) return false;
  if (!Number.isSafeInteger(value.configurationVersion)) return false;
  if (!isCanonicalIsoTimestamp(value.updatedAt)) return false;
  if (value.updatedBy !== null && (value.updatedBy.length < 1 || value.updatedBy.length > 256)) {
    return false;
  }
  if (value.initialized && (value.updatedAt === null || value.updatedBy === null)) return false;
  return operatingConfigurationDraftReferencesAreConsistent(value.draft);
}

export const OperatingConfigurationApplyResultSchema = Type.Object(
  {
    configuration: OperatingConfigurationBundleSchema,
    auditId: Type.String({ minLength: 1 }),
  },
  { additionalProperties: false },
);
export type OperatingConfigurationApplyResult = Static<
  typeof OperatingConfigurationApplyResultSchema
>;

export function isOperatingConfigurationApplyResult(
  value: unknown,
): value is OperatingConfigurationApplyResult {
  return (
    Value.Check(OperatingConfigurationApplyResultSchema, value) &&
    value.auditId.length <= 256 &&
    isOperatingConfigurationBundle(value.configuration)
  );
}

export const BillingSegmentSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 128 }),
    kind: Type.Union([
      Type.Literal("HOURLY"),
      Type.Literal("PACKAGE"),
      Type.Literal("PACKAGE_OVERTIME"),
    ]),
    priceSnapshotId: Type.Optional(Type.String({ minLength: 1 })),
    priceSnapshotVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    priceBandId: Type.String({ minLength: 1, maxLength: 64 }),
    priceBandName: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    startedAt: Type.String({ format: "date-time" }),
    endedAt: Type.String({ format: "date-time" }),
    elapsedSeconds: Type.Integer({ minimum: 0 }),
    billedMinutes: Type.Integer({ minimum: 0 }),
    hourlyRateFen: Type.Integer({ minimum: 0 }),
    roundingMinutes: Type.Integer({ minimum: 1 }),
    roundingMode: Type.Union([
      Type.Literal("UP"),
      Type.Literal("NEAREST"),
      Type.Literal("DOWN"),
      Type.Literal("PACKAGE_FIXED"),
    ]),
    amountFen: Type.Integer({ minimum: 0 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type BillingSegment = Static<typeof BillingSegmentSchema>;

export const BillingBreakdownSchema = Type.Object(
  {
    priceSnapshotId: Type.String({ minLength: 1 }),
    priceSnapshotVersion: Type.Integer({ minimum: 1 }),
    calculatedAt: Type.String({ format: "date-time" }),
    elapsedSeconds: Type.Integer({ minimum: 0 }),
    pausedSeconds: Type.Integer({ minimum: 0 }),
    billableSeconds: Type.Integer({ minimum: 0 }),
    billedMinutes: Type.Integer({ minimum: 0 }),
    subtotalFen: Type.Integer({ minimum: 0 }),
    minimumAmountAdjustmentFen: Type.Integer({ minimum: 0 }),
    totalFen: Type.Integer({ minimum: 0 }),
    segments: Type.Array(BillingSegmentSchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type BillingBreakdown = Static<typeof BillingBreakdownSchema>;

export const ProductSnapshotSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1 }),
    storeProductCode: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    version: Type.Integer({ minimum: 1 }),
    name: Type.String({ minLength: 1 }),
    unitPriceFen: Type.Integer({ minimum: 0 }),
    category: Type.Optional(Type.String({ minLength: 1, maxLength: 40 })),
    unit: Type.Optional(Type.String({ minLength: 1, maxLength: 16 })),
    costFen: Type.Optional(Type.Integer({ minimum: 0 })),
    barcode: Type.Optional(Type.Union([Type.String({ minLength: 4, maxLength: 32 }), Type.Null()])),
    trackInventory: Type.Optional(Type.Boolean()),
    stockOnHand: Type.Optional(Type.Integer()),
    inventoryVersion: Type.Optional(Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER })),
    lowStockThreshold: Type.Optional(Type.Integer({ minimum: 0 })),
    allowNegativeStock: Type.Optional(Type.Boolean()),
    customerStorageEligible: Type.Optional(Type.Boolean()),
    customerRentalEnabled: Type.Optional(Type.Boolean()),
    customerRentalUnitPriceFen: Type.Optional(
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    ),
    customerRentalTotalQuantity: Type.Optional(Type.Integer({ minimum: 0, maximum: 999_999 })),
    enabled: Type.Boolean(),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type ProductSnapshot = Static<typeof ProductSnapshotSchema>;

export function isProductSnapshotList(value: unknown): value is ProductSnapshot[] {
  if (!Array.isArray(value) || value.length > 10_000) return false;
  const skuIds = new Set<string>();
  for (const product of value) {
    if (!Value.Check(ProductSnapshotSchema, product) || skuIds.has(product.skuId)) return false;
    if (
      !Number.isSafeInteger(product.version) ||
      !Number.isSafeInteger(product.unitPriceFen) ||
      (product.costFen !== undefined && !Number.isSafeInteger(product.costFen)) ||
      (product.stockOnHand !== undefined && !Number.isSafeInteger(product.stockOnHand)) ||
      (product.inventoryVersion !== undefined && !Number.isSafeInteger(product.inventoryVersion)) ||
      (product.lowStockThreshold !== undefined &&
        !Number.isSafeInteger(product.lowStockThreshold)) ||
      (product.customerRentalUnitPriceFen !== undefined &&
        !Number.isSafeInteger(product.customerRentalUnitPriceFen)) ||
      (product.customerRentalTotalQuantity !== undefined &&
        !Number.isSafeInteger(product.customerRentalTotalQuantity)) ||
      (product.customerRentalEnabled === true &&
        (!(product.customerRentalUnitPriceFen ?? 0) || !(product.customerRentalTotalQuantity ?? 0)))
    ) {
      return false;
    }
    skuIds.add(product.skuId);
  }
  return true;
}

export const ProductSaleCatalogItemSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 128 }),
    name: Type.String({ minLength: 1, maxLength: 120 }),
    unitPriceFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    inventoryManaged: Type.Boolean(),
    stockOnHand: Type.Union([
      Type.Integer({ minimum: Number.MIN_SAFE_INTEGER, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    canSell: Type.Boolean(),
    unavailableReason: Type.Union([Type.String({ minLength: 1, maxLength: 120 }), Type.Null()]),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type ProductSaleCatalogItem = Static<typeof ProductSaleCatalogItemSchema>;

export function isProductSaleCatalogItemList(value: unknown): value is ProductSaleCatalogItem[] {
  if (!Array.isArray(value) || value.length > 10_000) return false;
  const skuIds = new Set<string>();
  for (const item of value) {
    if (!Value.Check(ProductSaleCatalogItemSchema, item) || skuIds.has(item.skuId)) return false;
    if (!item.inventoryManaged) {
      if (item.stockOnHand !== null || !item.canSell || item.unavailableReason !== null)
        return false;
    } else if (item.stockOnHand === null) {
      return false;
    } else if (item.canSell) {
      if (item.unavailableReason !== null) return false;
    } else {
      if (
        item.stockOnHand > 0 ||
        item.unavailableReason !== `缺货：库存 ${item.stockOnHand}${item.unit}，无法销售`
      ) {
        return false;
      }
    }
    skuIds.add(item.skuId);
  }
  return true;
}

export const ProductSaleBarcodeLookupResultSchema = Type.Object(
  {
    barcode: Type.String({ minLength: 4, maxLength: 32, pattern: "^[0-9A-Za-z._-]+$" }),
    product: Type.Union([ProductSaleCatalogItemSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type ProductSaleBarcodeLookupResult = Static<typeof ProductSaleBarcodeLookupResultSchema>;

export function isProductSaleBarcodeLookupResult(
  value: unknown,
): value is ProductSaleBarcodeLookupResult {
  if (!Value.Check(ProductSaleBarcodeLookupResultSchema, value)) return false;
  return value.product === null || isProductSaleCatalogItemList([value.product]);
}

export const OfflineConfigurationSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    timeZone: Type.String({ minLength: 1 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    version: Type.Integer({ minimum: 1 }),
    effectiveAt: Type.String({ format: "date-time" }),
    selectedConditionalModules: Type.Array(ConditionalModuleCodeSchema, { uniqueItems: true }),
    pricing: PriceSnapshotSchema,
    pricingPlans: Type.Optional(Type.Array(PriceSnapshotSchema, { minItems: 1, maxItems: 64 })),
    operatingSettings: Type.Optional(VenueOperatingSettingsInputSchema),
    tableConfigurations: Type.Optional(
      Type.Array(TableOperatingConfigurationInputSchema, { maxItems: 256 }),
    ),
    deviceMappings: Type.Optional(Type.Array(VenueDeviceMappingInputSchema, { maxItems: 512 })),
    products: Type.Array(ProductSnapshotSchema),
    coachConfiguration: Type.Optional(CoachConfigurationSnapshotSchema),
    tables: Type.Array(TableSnapshotSchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OfflineConfiguration = Static<typeof OfflineConfigurationSchema>;

export function isOfflineConfiguration(value: unknown): value is OfflineConfiguration {
  if (!Value.Check(OfflineConfigurationSchema, value)) return false;
  if (!Number.isSafeInteger(value.version) || !isCanonicalIsoTimestamp(value.effectiveAt)) {
    return false;
  }
  const pricingPlans = value.pricingPlans ?? [value.pricing];
  const planIds = pricingPlans.map((plan) => plan.id.trim().toLocaleLowerCase("zh-CN"));
  const uniquePlanIds = new Set(planIds);
  if (
    (value.selectedConditionalModules.includes("CUSTOMER_SELF_SERVICE_RESERVATION") &&
      !value.selectedConditionalModules.includes("RESERVATION")) ||
    (value.selectedConditionalModules.includes("CUSTOMER_STORAGE") &&
      (!value.selectedConditionalModules.includes("MEMBER") ||
        !value.selectedConditionalModules.includes("PRODUCT_ORDER"))) ||
    (value.selectedConditionalModules.includes("CUSTOMER_RENTAL") &&
      !value.selectedConditionalModules.includes("PRODUCT_ORDER")) ||
    uniquePlanIds.size !== planIds.length ||
    !uniquePlanIds.has(value.pricing.id.trim().toLocaleLowerCase("zh-CN")) ||
    !pricingPlans.every(priceSnapshotCanBeSelected)
  ) {
    return false;
  }
  return (
    value.tableConfigurations?.every(
      (table) =>
        !table.enabled || uniquePlanIds.has(table.pricingPlanId.trim().toLocaleLowerCase("zh-CN")),
    ) ?? true
  );
}

export const BusinessEventTypeSchema = Type.Union([
  Type.Literal("SESSION_OPENED"),
  Type.Literal("SESSION_TRANSFERRED"),
  Type.Literal("SESSION_START_ADJUSTED"),
  Type.Literal("SESSION_PAUSED"),
  Type.Literal("SESSION_RESUMED"),
  Type.Literal("SESSION_ENDED_EARLY"),
  Type.Literal("SESSION_LONG_RUNNING_RESOLVED"),
  Type.Literal("SESSION_VOIDED"),
  Type.Literal("CUSTOMER_RESERVATION_CHANGED"),
  Type.Literal("ANNOUNCEMENT_CHANGED"),
  Type.Literal("WORKFORCE_ATTENDANCE_UPDATED"),
  Type.Literal("WORKFORCE_PAYROLL_UPDATED"),
  Type.Literal("STORE_OPERATING_DAY_OPENED"),
  Type.Literal("STORE_OPERATING_DAY_CLOSING_STARTED"),
  Type.Literal("STORE_OPERATING_DAY_LEGACY_ACKNOWLEDGED"),
  Type.Literal("STORE_OPERATING_DAY_CLOSED"),
  Type.Literal("ORDER_ITEM_ADDED"),
  Type.Literal("KIOSK_PRODUCT_RESERVATION_CHANGED"),
  Type.Literal("ORDER_ITEM_RETURNED"),
  Type.Literal("STORE_PACKAGE_ORDER_ITEM_ADDED"),
  Type.Literal("STORE_PACKAGE_ORDER_ITEM_RETURNED"),
  Type.Literal("CUSTOMER_STORAGE_CREATED"),
  Type.Literal("CUSTOMER_STORAGE_PICKED_UP"),
  Type.Literal("CUSTOMER_STORAGE_EXCEPTION_REPORTED"),
  Type.Literal("CUSTOMER_STORAGE_EXCEPTION_RESOLVED"),
  Type.Literal("CUSTOMER_RENTAL_CREATED"),
  Type.Literal("CUSTOMER_RENTAL_RETURNED"),
  Type.Literal("CUSTOMER_RENTAL_EXCEPTION_REPORTED"),
  Type.Literal("CUSTOMER_RENTAL_EXCEPTION_RESOLVED"),
  Type.Literal("CHECKOUT_SPLIT_PLAN_CHANGED"),
  Type.Literal("CHECKOUT_SPLIT_SHARE_PAID"),
  Type.Literal("COMBINED_CHECKOUT_COMPLETED"),
  Type.Literal("COMBINED_CHECKOUT_RECONCILED"),
  Type.Literal("SESSION_CHECKED_OUT"),
  Type.Literal("TABLE_CLEANING_CONFIRMED"),
  Type.Literal("TABLE_RELEASED"),
  // Historical read/replay compatibility only. Current closeout uses the two events above.
  Type.Literal("TABLE_CLEARED"),
  Type.Literal("PAYMENT_REFUNDED"),
  Type.Literal("PAYMENT_RECONCILED"),
  Type.Literal("MEMBER_DEBIT_RECONCILED"),
  Type.Literal("COACH_SHIFT_CHANGED"),
  Type.Literal("COACH_QUEUE_CHANGED"),
  Type.Literal("COACH_SERVICE_CHANGED"),
  Type.Literal("COACH_SERVICE_ADJUSTED"),
  Type.Literal("COACH_SERVICE_CORRECTED"),
  Type.Literal("COACH_SERVICE_CORRECTION_NOTICE_CHANGED"),
  Type.Literal("COACH_COMMISSION_MONTH_SETTLED"),
  Type.Literal("GROUP_BUY_FULFILLED"),
  Type.Literal("GROUP_BUY_RECONCILED"),
  Type.Literal("GROUP_BUY_RECONCILIATION_SIGNED_OFF"),
  Type.Literal("PURCHASE_RECEIPT_RECORDED"),
  Type.Literal("PURCHASE_ORDER_SHORT_CLOSED"),
  Type.Literal("PURCHASE_RETURN_RECORDED"),
  Type.Literal("PURCHASE_PAYABLE_RECONCILED"),
  Type.Literal("SUPPLIER_PAYMENT_RECORDED"),
  Type.Literal("SUPPLIER_BATCH_PAYMENT_RECORDED"),
  Type.Literal("SUPPLIER_CREDIT_RECORDED"),
  Type.Literal("SUPPLIER_REFUND_RECORDED"),
  Type.Literal("SUPPLIER_PAYMENT_REVERSED"),
]);
export type BusinessEventType = Static<typeof BusinessEventTypeSchema>;

export const GatewayLeaseSchema = Type.Object(
  {
    leaseId: Type.String({ minLength: 1 }),
    gatewayId: Type.String({ minLength: 1 }),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    fencingToken: Type.Integer({ minimum: 1 }),
    issuedAt: Type.String({ format: "date-time" }),
    expiresAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type GatewayLease = Static<typeof GatewayLeaseSchema>;

export const SyncEventResultSchema = Type.Object(
  {
    eventId: Type.String(),
    status: Type.Union([
      Type.Literal("ACCEPTED"),
      Type.Literal("DUPLICATE"),
      Type.Literal("OUT_OF_ORDER"),
      Type.Literal("CONFLICT"),
      Type.Literal("REJECTED"),
    ]),
    code: Type.Union([Type.String(), Type.Null()]),
    message: Type.Union([Type.String(), Type.Null()]),
  },
  { additionalProperties: false },
);
export type SyncEventResult = Static<typeof SyncEventResultSchema>;

export const SyncBatchResponseSchema = Type.Object(
  { results: Type.Array(SyncEventResultSchema), receivedAt: Type.String({ format: "date-time" }) },
  { additionalProperties: false },
);
export type SyncBatchResponse = Static<typeof SyncBatchResponseSchema>;

export const OrderItemLineTypeSchema = Type.Union([Type.Literal("PRODUCT"), Type.Literal("GIFT")]);
export type OrderItemLineType = Static<typeof OrderItemLineTypeSchema>;

export const OrderItemPricingSourceSchema = Type.Union([
  Type.Literal("CATALOG_PRICE"),
  Type.Literal("AUTHORIZED_STORE_GIFT"),
]);
export type OrderItemPricingSource = Static<typeof OrderItemPricingSourceSchema>;

export const OrderItemRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    skuId: Type.String({ minLength: 1 }),
    productVersion: Type.Optional(Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER })),
    name: Type.String({ minLength: 1 }),
    unitPriceFen: Type.Integer({ minimum: 0 }),
    lineType: Type.Optional(OrderItemLineTypeSchema),
    pricingSource: Type.Optional(OrderItemPricingSourceSchema),
    listUnitPriceFen: Type.Optional(Type.Integer({ minimum: 0 })),
    unitDiscountFen: Type.Optional(Type.Integer({ minimum: 0 })),
    adjustmentReason: Type.Optional(Type.String({ minLength: 2, maxLength: 200 })),
    authorizedByRole: Type.Optional(Type.Union([Type.Literal("MANAGER"), Type.Literal("OWNER")])),
    authorizedById: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    unitCostFen: Type.Optional(Type.Integer({ minimum: 0 })),
    costSnapshotKnown: Type.Optional(Type.Boolean()),
    inventoryCostSnapshot: Type.Optional(TrackedSaleInventoryCostSnapshotSchema),
    sourceReservationId: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    quantity: Type.Integer({ minimum: 1 }),
    returnedQuantity: Type.Integer({ minimum: 0 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OrderItemRecord = Static<typeof OrderItemRecordSchema>;

export function orderItemPricingFactsAreConsistent(item: OrderItemRecord): boolean {
  const optionalPricingFields = [
    "lineType",
    "pricingSource",
    "listUnitPriceFen",
    "unitDiscountFen",
    "adjustmentReason",
    "authorizedByRole",
    "authorizedById",
  ] as const;
  const presentCount = optionalPricingFields.filter((field) => Object.hasOwn(item, field)).length;
  if (presentCount === 0) return true;
  if (
    item.lineType === undefined ||
    item.pricingSource === undefined ||
    item.listUnitPriceFen === undefined ||
    item.unitDiscountFen === undefined
  ) {
    return false;
  }
  if (
    !Number.isSafeInteger(item.listUnitPriceFen) ||
    !Number.isSafeInteger(item.unitDiscountFen) ||
    item.listUnitPriceFen < 0 ||
    item.unitDiscountFen < 0 ||
    item.listUnitPriceFen - item.unitDiscountFen !== item.unitPriceFen
  ) {
    return false;
  }
  if (item.lineType === "PRODUCT") {
    return (
      item.pricingSource === "CATALOG_PRICE" &&
      item.listUnitPriceFen === item.unitPriceFen &&
      item.unitDiscountFen === 0 &&
      item.adjustmentReason === undefined &&
      item.authorizedByRole === undefined &&
      item.authorizedById === undefined
    );
  }
  return (
    item.lineType === "GIFT" &&
    item.pricingSource === "AUTHORIZED_STORE_GIFT" &&
    item.listUnitPriceFen > 0 &&
    item.unitPriceFen === 0 &&
    item.unitDiscountFen === item.listUnitPriceFen &&
    typeof item.adjustmentReason === "string" &&
    item.adjustmentReason === item.adjustmentReason.trim() &&
    [...item.adjustmentReason].length >= 2 &&
    [...item.adjustmentReason].length <= 200 &&
    (item.authorizedByRole === "MANAGER" || item.authorizedByRole === "OWNER") &&
    typeof item.authorizedById === "string" &&
    item.authorizedById === item.authorizedById.trim() &&
    [...item.authorizedById].length >= 1 &&
    [...item.authorizedById].length <= 128
  );
}

export const PaymentRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    method: PaymentMethodSchema,
    amountFen: Type.Integer({ minimum: 0 }),
    status: PaymentStatusSchema,
    evidence: Type.Union([
      Type.Literal("MANUAL_FALLBACK"),
      Type.Literal("STORE_ACCEPTED"),
      Type.Literal("PROVIDER_ACCEPTED"),
    ]),
    recordedAt: Type.String({ format: "date-time" }),
    offlineOrigin: Type.Boolean(),
    memberId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    referenceId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    officialPaymentAttemptId: Type.Optional(
      Type.Union([Type.String({ minLength: 8, maxLength: 128 }), Type.Null()]),
    ),
    providerCode: Type.Optional(
      Type.Union([Type.String({ minLength: 2, maxLength: 32 }), Type.Null()]),
    ),
    providerTransactionReference: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);
export type PaymentRecord = Static<typeof PaymentRecordSchema>;

export const RefundRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    paymentId: Type.String({ minLength: 1 }),
    amountFen: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    evidence: Type.Literal("MANUAL_FALLBACK"),
    actorId: Type.String({ minLength: 1 }),
    authorizerId: Type.String({ minLength: 1 }),
    approvalId: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    approvalSnapshotSha256: Type.Optional(Type.String({ pattern: "^[a-f0-9]{64}$" })),
    createdAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type RefundRecord = Static<typeof RefundRecordSchema>;

export const CashShiftOpenTableSchema = Type.Object(
  {
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    sessionId: Type.String({ minLength: 1 }),
    startedAt: Type.String({ format: "date-time" }),
    currentAmountFen: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type CashShiftOpenTable = Static<typeof CashShiftOpenTableSchema>;

export const CashShiftInventoryDifferenceSchema = Type.Object(
  {
    movementId: Type.String({ minLength: 1 }),
    skuId: Type.String({ minLength: 1 }),
    productName: Type.String({ minLength: 1 }),
    quantityBefore: Type.Integer(),
    quantityAfter: Type.Integer(),
    quantityDelta: Type.Integer(),
    reason: Type.String({ minLength: 1 }),
    actorId: Type.String({ minLength: 1 }),
    occurredAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CashShiftInventoryDifference = Static<typeof CashShiftInventoryDifferenceSchema>;

export const CashShiftTimelineEntrySchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    type: Type.Union([
      Type.Literal("STARTED"),
      Type.Literal("HANDOVER_SUBMITTED"),
      Type.Literal("HANDOVER_REJECTED"),
      Type.Literal("RECEIVED"),
      Type.Literal("CASH_IN_RECORDED"),
      Type.Literal("CASH_OUT_RECORDED"),
    ]),
    actorId: Type.String({ minLength: 1 }),
    actorDisplayName: Type.String({ minLength: 1 }),
    occurredAt: Type.String({ format: "date-time" }),
    note: Type.String(),
  },
  { additionalProperties: false },
);
export type CashShiftTimelineEntry = Static<typeof CashShiftTimelineEntrySchema>;

export const CASH_SHIFT_OPENING_CASH_MAX_FEN = Number.MAX_SAFE_INTEGER;

export const CashShiftRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: Type.Union([
      Type.Literal("OPEN"),
      Type.Literal("HANDOVER_PENDING"),
      Type.Literal("CLOSED"),
    ]),
    openedBy: Type.String({ minLength: 1 }),
    openedByDisplayName: Type.String({ minLength: 1 }),
    openingCashFen: Type.Integer({ minimum: 0, maximum: CASH_SHIFT_OPENING_CASH_MAX_FEN }),
    startNote: Type.String(),
    startedAt: Type.String({ format: "date-time" }),
    cashReceiptsFen: Type.Integer({ minimum: 0 }),
    cashRefundsFen: Type.Integer({ minimum: 0 }),
    cashInFen: Type.Integer({ minimum: 0 }),
    cashOutFen: Type.Integer({ minimum: 0 }),
    expectedCashFen: Type.Integer(),
    countedCashFen: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    cashDifferenceFen: Type.Union([Type.Integer(), Type.Null()]),
    exceptionNote: Type.String(),
    openTables: Type.Array(CashShiftOpenTableSchema),
    inventoryDifferences: Type.Array(CashShiftInventoryDifferenceSchema),
    handoverSubmittedBy: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    handoverSubmittedByDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    handoverSubmittedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    receivedBy: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    receivedByDisplayName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    receivedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    receiveNote: Type.String(),
    version: Type.Integer({ minimum: 1 }),
    timeline: Type.Array(CashShiftTimelineEntrySchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type CashShiftRecord = Static<typeof CashShiftRecordSchema>;

export const StoreOpeningModeSchema = Type.Union([
  Type.Literal("ONLINE"),
  Type.Literal("LIMITED_OFFLINE"),
]);
export type StoreOpeningMode = Static<typeof StoreOpeningModeSchema>;

export const StoreOperatingDayStatusSchema = Type.Union([
  Type.Literal("OPEN"),
  Type.Literal("CLOSING"),
  Type.Literal("CLOSED"),
]);
export type StoreOperatingDayStatus = Static<typeof StoreOperatingDayStatusSchema>;

export const StoreClosingActionSchema = Type.Union([
  Type.Literal("START_CLOSING"),
  Type.Literal("ACKNOWLEDGE_LEGACY"),
  Type.Literal("COMPLETE_CLOSING"),
]);
export type StoreClosingAction = Static<typeof StoreClosingActionSchema>;

export const StoreClosingBlockerCodeSchema = Type.Union([
  Type.Literal("ACTIVE_TABLE_SESSION"),
  Type.Literal("ACTIVE_COACH_SERVICE"),
  Type.Literal("PENDING_CLEANING_OR_LIGHT"),
  Type.Literal("OPEN_CASH_SHIFT"),
  Type.Literal("PENDING_PAYMENT_REVIEW"),
  Type.Literal("OPEN_INVENTORY_WORK"),
  Type.Literal("CONNECTIVITY_AUTHORITY"),
]);
export type StoreClosingBlockerCode = Static<typeof StoreClosingBlockerCodeSchema>;

export const StoreClosingBlockerSchema = Type.Object(
  {
    code: StoreClosingBlockerCodeSchema,
    status: Type.Union([Type.Literal("READY"), Type.Literal("BLOCKED")]),
    count: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    title: Type.String({ minLength: 1, maxLength: 80 }),
    detail: Type.String({ minLength: 1, maxLength: 500 }),
    actionPath: Type.String({ minLength: 1, maxLength: 200 }),
  },
  { additionalProperties: false },
);
export type StoreClosingBlocker = Static<typeof StoreClosingBlockerSchema>;

export const StoreClosingLegacyAcknowledgementSchema = Type.Object(
  {
    acknowledgedBy: Type.String({ minLength: 1, maxLength: 128 }),
    acknowledgedByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    acknowledgedAt: Type.String({ format: "date-time" }),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    requestId: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type StoreClosingLegacyAcknowledgement = Static<
  typeof StoreClosingLegacyAcknowledgementSchema
>;

export const StoreClosingCompletionSchema = Type.Object(
  {
    closedBy: Type.String({ minLength: 1, maxLength: 128 }),
    closedByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    closedAt: Type.String({ format: "date-time" }),
    note: Type.String({ maxLength: 500 }),
    requestId: Type.String({ minLength: 8, maxLength: 128 }),
    closingSummarySha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type StoreClosingCompletion = Static<typeof StoreClosingCompletionSchema>;

export const StoreOperatingDayClosingSchema = Type.Object(
  {
    startedBy: Type.String({ minLength: 1, maxLength: 128 }),
    startedByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    startedAt: Type.String({ format: "date-time" }),
    note: Type.String({ maxLength: 500 }),
    requestId: Type.String({ minLength: 8, maxLength: 128 }),
    legacyAcknowledgement: Type.Union([StoreClosingLegacyAcknowledgementSchema, Type.Null()]),
    completion: Type.Union([StoreClosingCompletionSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type StoreOperatingDayClosing = Static<typeof StoreOperatingDayClosingSchema>;

export const StoreOpeningCheckCodeSchema = Type.Union([
  Type.Literal("OPERATING_CONFIGURATION"),
  Type.Literal("ENABLED_TABLES"),
  Type.Literal("PREVIOUS_DAY"),
  Type.Literal("CONNECTIVITY_AUTHORITY"),
  Type.Literal("CASH_SHIFT"),
  Type.Literal("PENDING_REVIEW"),
]);
export type StoreOpeningCheckCode = Static<typeof StoreOpeningCheckCodeSchema>;

export const StoreOpeningCheckSchema = Type.Object(
  {
    code: StoreOpeningCheckCodeSchema,
    status: Type.Union([Type.Literal("READY"), Type.Literal("WARNING"), Type.Literal("BLOCKED")]),
    title: Type.String({ minLength: 1, maxLength: 80 }),
    detail: Type.String({ minLength: 1, maxLength: 500 }),
    actionPath: Type.Union([Type.String({ minLength: 1, maxLength: 200 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type StoreOpeningCheck = Static<typeof StoreOpeningCheckSchema>;

export const StoreOpeningRestrictionSchema = Type.Object(
  {
    code: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Z][A-Z0-9_]*$" }),
    label: Type.String({ minLength: 1, maxLength: 120 }),
    reason: Type.String({ minLength: 1, maxLength: 500 }),
  },
  { additionalProperties: false },
);
export type StoreOpeningRestriction = Static<typeof StoreOpeningRestrictionSchema>;

export const StoreOperatingDayRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 128 }),
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: StoreOperatingDayStatusSchema,
    version: Type.Integer({ minimum: 1, maximum: 2_147_483_647 }),
    openingMode: StoreOpeningModeSchema,
    openedBy: Type.String({ minLength: 1 }),
    openedByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    openedAt: Type.String({ format: "date-time" }),
    openingNote: Type.String({ maxLength: 500 }),
    operatingConfigurationVersion: Type.Integer({ minimum: 1, maximum: 2_147_483_647 }),
    openingRequestId: Type.String({ minLength: 8, maxLength: 128 }),
    closing: Type.Optional(StoreOperatingDayClosingSchema),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type StoreOperatingDayRecord = Static<typeof StoreOperatingDayRecordSchema>;

export const StoreClosingReadinessSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    businessDate: Type.Union([Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }), Type.Null()]),
    generatedAt: Type.String({ format: "date-time" }),
    currentDay: Type.Union([StoreOperatingDayRecordSchema, Type.Null()]),
    crossDay: Type.Boolean(),
    canStartClosing: Type.Boolean(),
    requiresLegacyAcknowledgement: Type.Boolean(),
    canCompleteClosing: Type.Boolean(),
    blockers: Type.Array(StoreClosingBlockerSchema, { minItems: 7, maxItems: 7 }),
    closingSummarySha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type StoreClosingReadiness = Static<typeof StoreClosingReadinessSchema>;

export const StoreStartClosingCommandSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    expectedVersion: Type.Integer({ minimum: 1, maximum: 2_147_483_647 }),
    closingSummarySha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    note: Type.String({ maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type StoreStartClosingCommand = Static<typeof StoreStartClosingCommandSchema>;

export const StoreAcknowledgeClosingLegacyCommandSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    expectedVersion: Type.Integer({ minimum: 2, maximum: 2_147_483_647 }),
    closingSummarySha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type StoreAcknowledgeClosingLegacyCommand = Static<
  typeof StoreAcknowledgeClosingLegacyCommandSchema
>;

export const StoreCompleteClosingCommandSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    expectedVersion: Type.Integer({ minimum: 2, maximum: 2_147_483_647 }),
    closingSummarySha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    note: Type.String({ maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type StoreCompleteClosingCommand = Static<typeof StoreCompleteClosingCommandSchema>;

export const StoreOpeningReadinessSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1 }),
    storeId: Type.String({ minLength: 1 }),
    businessDate: Type.Union([Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }), Type.Null()]),
    generatedAt: Type.String({ format: "date-time" }),
    openingMode: StoreOpeningModeSchema,
    canOpen: Type.Boolean(),
    alreadyOpen: Type.Boolean(),
    requiresOfflineConfirmation: Type.Boolean(),
    operatingConfigurationVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: 2_147_483_647 }),
      Type.Null(),
    ]),
    currentDay: Type.Union([StoreOperatingDayRecordSchema, Type.Null()]),
    priorUnclosedDay: Type.Union([StoreOperatingDayRecordSchema, Type.Null()]),
    checks: Type.Array(StoreOpeningCheckSchema, { minItems: 6, maxItems: 6 }),
    limitedOfflineRestrictions: Type.Array(StoreOpeningRestrictionSchema, {
      maxItems: 64,
    }),
    readinessSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type StoreOpeningReadiness = Static<typeof StoreOpeningReadinessSchema>;

export const StoreOpenCommandSchema = Type.Object(
  {
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    readinessSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    operatingConfigurationVersion: Type.Integer({ minimum: 1, maximum: 2_147_483_647 }),
    offlineConfirmed: Type.Boolean(),
    note: Type.String({ maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type StoreOpenCommand = Static<typeof StoreOpenCommandSchema>;

const STORE_OPENING_CHECK_CODES: readonly StoreOpeningCheckCode[] = [
  "OPERATING_CONFIGURATION",
  "ENABLED_TABLES",
  "PREVIOUS_DAY",
  "CONNECTIVITY_AUTHORITY",
  "CASH_SHIFT",
  "PENDING_REVIEW",
];

const STORE_CLOSING_BLOCKER_CODES: readonly StoreClosingBlockerCode[] = [
  "ACTIVE_TABLE_SESSION",
  "ACTIVE_COACH_SERVICE",
  "PENDING_CLEANING_OR_LIGHT",
  "OPEN_CASH_SHIFT",
  "PENDING_PAYMENT_REVIEW",
  "OPEN_INVENTORY_WORK",
  "CONNECTIVITY_AUTHORITY",
];

export const STORE_CLOSING_ACTION_PATHS: Readonly<Record<StoreClosingBlockerCode, string>> =
  Object.freeze({
    ACTIVE_TABLE_SESSION: "/frontdesk",
    ACTIVE_COACH_SERVICE: "/coach",
    PENDING_CLEANING_OR_LIGHT: "/frontdesk",
    OPEN_CASH_SHIFT: "/shifts",
    PENDING_PAYMENT_REVIEW: "/review",
    OPEN_INVENTORY_WORK: "/products",
    CONNECTIVITY_AUTHORITY: "/system",
  });

export function isStoreOperatingDayRecord(value: unknown): value is StoreOperatingDayRecord {
  if (!Value.Check(StoreOperatingDayRecordSchema, value)) return false;
  const record = value as StoreOperatingDayRecord;
  if (
    !isCanonicalCalendarDate(record.businessDate) ||
    !isCanonicalIsoTimestamp(record.openedAt) ||
    record.openingNote !== record.openingNote.trim()
  ) {
    return false;
  }
  if (record.status === "OPEN") return record.closing === undefined;
  const closing = record.closing;
  if (
    closing === undefined ||
    !isCanonicalIsoTimestamp(closing.startedAt) ||
    closing.note !== closing.note.trim() ||
    closing.startedAt < record.openedAt
  ) {
    return false;
  }
  const acknowledgement = closing.legacyAcknowledgement;
  if (
    acknowledgement !== null &&
    (!isCanonicalIsoTimestamp(acknowledgement.acknowledgedAt) ||
      acknowledgement.note !== acknowledgement.note.trim() ||
      acknowledgement.acknowledgedAt < closing.startedAt)
  ) {
    return false;
  }
  if (record.status === "CLOSING") return closing.completion === null;
  const completion = closing.completion;
  return (
    completion !== null &&
    isCanonicalIsoTimestamp(completion.closedAt) &&
    completion.note === completion.note.trim() &&
    completion.closedAt >= closing.startedAt &&
    (acknowledgement === null || completion.closedAt >= acknowledgement.acknowledgedAt)
  );
}

export function isStoreClosingReadiness(value: unknown): value is StoreClosingReadiness {
  if (!Value.Check(StoreClosingReadinessSchema, value)) return false;
  const readiness = value as StoreClosingReadiness;
  if (
    !isCanonicalIsoTimestamp(readiness.generatedAt) ||
    (readiness.businessDate !== null && !isCanonicalCalendarDate(readiness.businessDate)) ||
    (readiness.currentDay !== null &&
      (!isStoreOperatingDayRecord(readiness.currentDay) ||
        readiness.currentDay.tenantId !== readiness.tenantId ||
        readiness.currentDay.storeId !== readiness.storeId ||
        readiness.currentDay.status === "CLOSED"))
  ) {
    return false;
  }
  const blockerCodes = readiness.blockers.map((blocker) => blocker.code);
  if (
    blockerCodes.some((code, index) => code !== STORE_CLOSING_BLOCKER_CODES[index]) ||
    readiness.blockers.some(
      (blocker) =>
        blocker.status !== (blocker.count === 0 ? "READY" : "BLOCKED") ||
        blocker.actionPath !== STORE_CLOSING_ACTION_PATHS[blocker.code],
    )
  ) {
    return false;
  }
  const expectedCrossDay =
    readiness.businessDate !== null &&
    readiness.currentDay !== null &&
    readiness.businessDate > readiness.currentDay.businessDate;
  const expectedCanStart = readiness.currentDay?.status === "OPEN";
  const expectedAcknowledgement =
    readiness.currentDay?.status === "CLOSING" &&
    expectedCrossDay &&
    readiness.currentDay.closing?.legacyAcknowledgement === null;
  const expectedCanComplete =
    readiness.currentDay?.status === "CLOSING" &&
    !expectedAcknowledgement &&
    readiness.blockers.every((blocker) => blocker.status === "READY");
  return (
    readiness.crossDay === expectedCrossDay &&
    readiness.canStartClosing === expectedCanStart &&
    readiness.requiresLegacyAcknowledgement === expectedAcknowledgement &&
    readiness.canCompleteClosing === expectedCanComplete
  );
}

export function isStoreOpeningReadiness(value: unknown): value is StoreOpeningReadiness {
  if (!Value.Check(StoreOpeningReadinessSchema, value)) return false;
  const readiness = value as StoreOpeningReadiness;
  if (
    !isCanonicalIsoTimestamp(readiness.generatedAt) ||
    (readiness.businessDate !== null && !isCanonicalCalendarDate(readiness.businessDate)) ||
    (readiness.businessDate === null &&
      (readiness.operatingConfigurationVersion !== null ||
        readiness.currentDay !== null ||
        readiness.priorUnclosedDay !== null ||
        readiness.alreadyOpen ||
        readiness.canOpen ||
        readiness.checks[0]?.status !== "BLOCKED")) ||
    (readiness.businessDate !== null && readiness.operatingConfigurationVersion === null) ||
    (readiness.currentDay !== null &&
      (!isStoreOperatingDayRecord(readiness.currentDay) ||
        readiness.currentDay.tenantId !== readiness.tenantId ||
        readiness.currentDay.storeId !== readiness.storeId ||
        readiness.currentDay.businessDate !== readiness.businessDate)) ||
    (readiness.priorUnclosedDay !== null &&
      (!isStoreOperatingDayRecord(readiness.priorUnclosedDay) ||
        readiness.priorUnclosedDay.tenantId !== readiness.tenantId ||
        readiness.priorUnclosedDay.storeId !== readiness.storeId ||
        readiness.businessDate === null ||
        readiness.priorUnclosedDay.businessDate >= readiness.businessDate ||
        readiness.priorUnclosedDay.status === "CLOSED"))
  ) {
    return false;
  }
  const codes = readiness.checks.map((check) => check.code);
  if (
    codes.some((code, index) => code !== STORE_OPENING_CHECK_CODES[index]) ||
    readiness.requiresOfflineConfirmation !== (readiness.openingMode === "LIMITED_OFFLINE") ||
    (readiness.openingMode === "ONLINE"
      ? readiness.limitedOfflineRestrictions.length !== 0
      : readiness.limitedOfflineRestrictions.length === 0) ||
    new Set(readiness.limitedOfflineRestrictions.map((restriction) => restriction.code)).size !==
      readiness.limitedOfflineRestrictions.length ||
    readiness.alreadyOpen !== (readiness.currentDay?.status === "OPEN") ||
    readiness.canOpen !==
      (!readiness.alreadyOpen && readiness.checks.every((check) => check.status !== "BLOCKED"))
  ) {
    return false;
  }
  return true;
}

export function isCashShiftRecord(value: unknown): value is CashShiftRecord {
  if (!Value.Check(CashShiftRecordSchema, value)) return false;
  const shift = value as CashShiftRecord;
  const moneyValues = [
    shift.openingCashFen,
    shift.cashReceiptsFen,
    shift.cashRefundsFen,
    shift.cashInFen,
    shift.cashOutFen,
    shift.expectedCashFen,
    shift.countedCashFen,
    shift.cashDifferenceFen,
  ].filter((amount): amount is number => amount !== null);
  if (
    !moneyValues.every(Number.isSafeInteger) ||
    !Number.isSafeInteger(shift.version) ||
    !shift.openTables.every(
      (table) => Number.isSafeInteger(table.currentAmountFen) && isRfc3339DateTime(table.startedAt),
    ) ||
    !shift.inventoryDifferences.every(
      (difference) =>
        Number.isSafeInteger(difference.quantityBefore) &&
        Number.isSafeInteger(difference.quantityAfter) &&
        Number.isSafeInteger(difference.quantityDelta) &&
        isRfc3339DateTime(difference.occurredAt),
    ) ||
    !shift.timeline.every((entry) => isRfc3339DateTime(entry.occurredAt)) ||
    !isRfc3339DateTime(shift.startedAt)
  ) {
    return false;
  }
  const afterReceipts = shift.openingCashFen + shift.cashReceiptsFen;
  const afterCashIn = afterReceipts + shift.cashInFen;
  const afterRefunds = afterCashIn - shift.cashRefundsFen;
  const expectedCashFen = afterRefunds - shift.cashOutFen;
  if (
    !Number.isSafeInteger(afterReceipts) ||
    !Number.isSafeInteger(afterCashIn) ||
    !Number.isSafeInteger(afterRefunds) ||
    !Number.isSafeInteger(expectedCashFen) ||
    shift.expectedCashFen !== expectedCashFen
  ) {
    return false;
  }
  if (
    shift.countedCashFen === null
      ? shift.cashDifferenceFen !== null
      : shift.cashDifferenceFen !== shift.countedCashFen - shift.expectedCashFen
  ) {
    return false;
  }
  const started = shift.timeline[0];
  if (
    !started ||
    started.type !== "STARTED" ||
    started.actorId !== shift.openedBy ||
    started.occurredAt !== shift.startedAt ||
    started.note !== shift.startNote
  ) {
    return false;
  }
  return true;
}

const CASH_SHIFT_READ_CAPACITY = 50;
const CASH_SHIFT_DETAIL_CAPACITY = 10_000;

function cashShiftTimelineEntryMatches(
  entry: CashShiftRecord["timeline"][number] | undefined,
  expected: {
    type: CashShiftRecord["timeline"][number]["type"];
    actorId: string;
    actorDisplayName: string;
    occurredAt: string;
    note: string;
  },
): boolean {
  return (
    entry?.type === expected.type &&
    entry.actorId === expected.actorId &&
    entry.actorDisplayName === expected.actorDisplayName &&
    entry.occurredAt === expected.occurredAt &&
    entry.note === expected.note
  );
}

function cashShiftStateIsConsistent(shift: CashShiftRecord): boolean {
  if (
    !cashShiftTimelineEntryMatches(shift.timeline[0], {
      type: "STARTED",
      actorId: shift.openedBy,
      actorDisplayName: shift.openedByDisplayName,
      occurredAt: shift.startedAt,
      note: shift.startNote,
    }) ||
    shift.timeline.some((entry, index) => {
      const previous = shift.timeline[index - 1];
      return previous ? Date.parse(entry.occurredAt) < Date.parse(previous.occurredAt) : false;
    }) ||
    shift.version < shift.timeline.length
  ) {
    return false;
  }

  let cursor = 1;
  let latestHandover: CashShiftTimelineEntry | null = null;
  const consumeCashMovements = () => {
    while (
      shift.timeline[cursor]?.type === "CASH_IN_RECORDED" ||
      shift.timeline[cursor]?.type === "CASH_OUT_RECORDED"
    ) {
      cursor += 1;
    }
  };
  consumeCashMovements();
  while (cursor < shift.timeline.length) {
    const handover = shift.timeline[cursor];
    if (handover?.type !== "HANDOVER_SUBMITTED") return false;
    latestHandover = handover;
    cursor += 1;
    if (shift.timeline[cursor]?.type === "HANDOVER_REJECTED") {
      cursor += 1;
      latestHandover = null;
      consumeCashMovements();
      continue;
    }
    break;
  }

  if (shift.status === "OPEN") {
    return (
      cursor === shift.timeline.length &&
      latestHandover === null &&
      shift.countedCashFen === null &&
      shift.cashDifferenceFen === null &&
      shift.exceptionNote === "" &&
      shift.handoverSubmittedBy === null &&
      shift.handoverSubmittedByDisplayName === null &&
      shift.handoverSubmittedAt === null &&
      shift.receivedBy === null &&
      shift.receivedByDisplayName === null &&
      shift.receivedAt === null &&
      shift.receiveNote === ""
    );
  }
  if (
    latestHandover === null ||
    shift.countedCashFen === null ||
    shift.cashDifferenceFen === null ||
    shift.handoverSubmittedBy === null ||
    shift.handoverSubmittedByDisplayName === null ||
    shift.handoverSubmittedAt === null
  ) {
    return false;
  }
  const handoverEventMatches = cashShiftTimelineEntryMatches(latestHandover, {
    type: "HANDOVER_SUBMITTED",
    actorId: shift.handoverSubmittedBy,
    actorDisplayName: shift.handoverSubmittedByDisplayName,
    occurredAt: shift.handoverSubmittedAt,
    note: shift.exceptionNote,
  });
  if (shift.status === "HANDOVER_PENDING") {
    return (
      cursor === shift.timeline.length &&
      handoverEventMatches &&
      shift.receivedBy === null &&
      shift.receivedByDisplayName === null &&
      shift.receivedAt === null &&
      shift.receiveNote === ""
    );
  }
  if (
    shift.receivedBy === null ||
    shift.receivedByDisplayName === null ||
    shift.receivedAt === null
  ) {
    return false;
  }
  return (
    cursor + 1 === shift.timeline.length &&
    handoverEventMatches &&
    shift.receivedBy !== shift.handoverSubmittedBy &&
    cashShiftTimelineEntryMatches(shift.timeline[cursor], {
      type: "RECEIVED",
      actorId: shift.receivedBy,
      actorDisplayName: shift.receivedByDisplayName,
      occurredAt: shift.receivedAt,
      note: shift.receiveNote,
    })
  );
}

export function isCashShiftRecordList(value: unknown): value is CashShiftRecord[] {
  if (
    !Array.isArray(value) ||
    value.length > CASH_SHIFT_READ_CAPACITY ||
    !value.every(isCashShiftRecord)
  ) {
    return false;
  }
  const shiftIds = new Set<string>();
  const timelineIds = new Set<string>();
  let activeShifts = 0;
  for (const shift of value) {
    if (
      shiftIds.has(shift.id) ||
      !cashShiftStateIsConsistent(shift) ||
      shift.openTables.length > CASH_SHIFT_DETAIL_CAPACITY ||
      shift.inventoryDifferences.length > CASH_SHIFT_DETAIL_CAPACITY ||
      !isCanonicalIsoTimestamp(shift.startedAt) ||
      !isCanonicalIsoTimestamp(shift.handoverSubmittedAt) ||
      !isCanonicalIsoTimestamp(shift.receivedAt) ||
      !shift.timeline.every((entry) => isCanonicalIsoTimestamp(entry.occurredAt)) ||
      !shift.openTables.every((table) => isCanonicalIsoTimestamp(table.startedAt)) ||
      !shift.inventoryDifferences.every((difference) =>
        isCanonicalIsoTimestamp(difference.occurredAt),
      )
    ) {
      return false;
    }
    shiftIds.add(shift.id);
    if (shift.status !== "CLOSED") activeShifts += 1;
    if (activeShifts > 1) return false;

    const tableIds = new Set<string>();
    const tableNumbers = new Set<string>();
    const sessionIds = new Set<string>();
    for (const table of shift.openTables) {
      if (
        tableIds.has(table.tableId) ||
        tableNumbers.has(table.tableNumber) ||
        sessionIds.has(table.sessionId)
      ) {
        return false;
      }
      tableIds.add(table.tableId);
      tableNumbers.add(table.tableNumber);
      sessionIds.add(table.sessionId);
    }

    const movementIds = new Set<string>();
    for (const difference of shift.inventoryDifferences) {
      if (
        movementIds.has(difference.movementId) ||
        difference.quantityAfter - difference.quantityBefore !== difference.quantityDelta
      ) {
        return false;
      }
      movementIds.add(difference.movementId);
    }
    for (const entry of shift.timeline) {
      if (timelineIds.has(entry.id)) return false;
      timelineIds.add(entry.id);
    }
  }
  return true;
}

export const OrderDetailSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    orderNumber: Type.Optional(Type.String({ minLength: 1 })),
    aggregateVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    sessionId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: Type.Union([
      Type.Literal("OPEN"),
      Type.Literal("SETTLED"),
      Type.Literal("REFUNDED"),
      Type.Literal("CONFLICT"),
    ]),
    priceSnapshot: PriceSnapshotSchema,
    priceSnapshots: Type.Optional(Type.Array(PriceSnapshotSchema, { minItems: 1 })),
    billingBreakdown: Type.Optional(BillingBreakdownSchema),
    billingState: Type.Optional(
      Type.Union([Type.Literal("RUNNING"), Type.Literal("PAUSED"), Type.Literal("ENDED")]),
    ),
    billingPausedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    voidedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    voidReason: Type.Optional(Type.Union([Type.String({ minLength: 2 }), Type.Null()])),
    startedAt: Type.String({ format: "date-time" }),
    endedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    tableFeeFen: Type.Integer({ minimum: 0 }),
    productTotalFen: Type.Integer({ minimum: 0 }),
    packageTotalFen: Type.Optional(Type.Integer({ minimum: 0 })),
    rentalTotalFen: Type.Optional(Type.Integer({ minimum: 0 })),
    coachServiceTotalFen: Type.Integer(),
    totalFen: Type.Integer({ minimum: 0 }),
    paidFen: Type.Integer({ minimum: 0 }),
    refundedFen: Type.Integer({ minimum: 0 }),
    items: Type.Array(OrderItemRecordSchema),
    packageItems: Type.Optional(Type.Array(StorePackageOrderItemSchema, { maxItems: 1_000 })),
    rentalItems: Type.Optional(Type.Array(CustomerRentalChargeItemSchema, { maxItems: 1_000 })),
    coachServiceItems: Type.Array(CoachOrderLineSchema),
    groupBuyFulfillments: Type.Optional(Type.Array(GroupBuyFulfillmentRecordSchema)),
    funding: Type.Optional(SessionFundingSummarySchema),
    payments: Type.Array(PaymentRecordSchema),
    refunds: Type.Array(RefundRecordSchema),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("SYNCED"),
      Type.Literal("CONFLICT"),
      Type.Literal("FAILED"),
      Type.Literal("ISOLATED"),
    ]),
  },
  { additionalProperties: false },
);
export type OrderDetail = Static<typeof OrderDetailSchema>;

export const StorePackageOrderItemAddResultSchema = Type.Object(
  {
    order: OrderDetailSchema,
    packageItem: StorePackageOrderItemSchema,
  },
  { additionalProperties: false },
);
export type StorePackageOrderItemAddResult = Static<typeof StorePackageOrderItemAddResultSchema>;

export const StorePackageOrderItemReturnResultSchema = Type.Object(
  {
    order: OrderDetailSchema,
    packageItem: StorePackageOrderItemSchema,
    returnRecord: StorePackageOrderItemReturnRecordSchema,
  },
  { additionalProperties: false },
);
export type StorePackageOrderItemReturnResult = Static<
  typeof StorePackageOrderItemReturnResultSchema
>;

export const CoachSessionStopResultSchema = Type.Object(
  {
    order: OrderDetailSchema,
    table: Type.Optional(TableSnapshotSchema),
    coachServiceClosure: Type.Union([Type.Literal("COMPLETED"), Type.Literal("LEGACY_UNRECORDED")]),
    completedCoachServices: Type.Array(CoachSessionStopCompletedServiceSchema, {
      maxItems: 100_000,
    }),
    serviceSetToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    occurredAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CoachSessionStopResult = Static<typeof CoachSessionStopResultSchema>;

export function isCoachSessionStopResult(value: unknown): value is CoachSessionStopResult {
  if (!Value.Check(CoachSessionStopResultSchema, value)) return false;
  const result = value as CoachSessionStopResult;
  if (
    !isOrderDetail(result.order) ||
    result.order.billingState !== "ENDED" ||
    result.order.endedAt !== result.occurredAt ||
    (result.table !== undefined &&
      (result.table.tableId !== result.order.tableId ||
        result.table.tableNumber !== result.order.tableNumber ||
        result.table.sessionId !== result.order.sessionId ||
        result.table.state !== "IN_USE" ||
        result.table.billingState !== "ENDED" ||
        result.table.billingPausedAt !== null ||
        result.table.estimatedFeeFen !== result.order.tableFeeFen ||
        result.table.currentTotalFen !== result.order.totalFen ||
        result.table.authoritativeAt !== result.occurredAt)) ||
    (result.coachServiceClosure === "LEGACY_UNRECORDED" &&
      (result.table !== undefined ||
        result.completedCoachServices.length !== 0 ||
        result.serviceSetToken !== "0".repeat(64)))
  ) {
    return false;
  }
  const serviceIds = new Set<string>();
  for (const service of result.completedCoachServices as CoachSessionStopCompletedService[]) {
    if (
      serviceIds.has(service.id) ||
      !isCanonicalCalendarDate(service.businessDate) ||
      ![service.billedMinutes, service.serviceFeeFen, service.version].every(
        Number.isSafeInteger,
      ) ||
      service.endedAt !== result.occurredAt ||
      (service.status === "COMPLETED"
        ? service.startedAt === null
        : service.startedAt !== null || service.billedMinutes !== 0 || service.serviceFeeFen !== 0)
    ) {
      return false;
    }
    serviceIds.add(service.id);
    if (
      service.serviceFeeFen > 0 &&
      !result.order.coachServiceItems.some(
        (line) => line.serviceId === service.id && line.amountFen === service.serviceFeeFen,
      )
    ) {
      return false;
    }
  }
  return true;
}

export const OrderItemSaleReturnRecordSchema = Type.Object(
  {
    kind: Type.Literal("ORDER_ITEM_SALE_RETURN"),
    returnId: Type.String({ minLength: 1, maxLength: 128 }),
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    itemId: Type.String({ minLength: 1, maxLength: 128 }),
    skuId: Type.String({ minLength: 1, maxLength: 128 }),
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    returnedQuantityBefore: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    returnedQuantityAfter: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    orderAmountReductionFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    paymentResolution: Type.Literal("UNPAID_ORDER_REDUCTION"),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    inventoryReturnSnapshot: Type.Union([TrackedSaleInventoryReturnSnapshotSchema, Type.Null()]),
    occurredAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type OrderItemSaleReturnRecord = Static<typeof OrderItemSaleReturnRecordSchema>;

export function isOrderItemSaleReturnRecord(value: unknown): value is OrderItemSaleReturnRecord {
  if (!Value.Check(OrderItemSaleReturnRecordSchema, value)) return false;
  const record = value as OrderItemSaleReturnRecord;
  return (
    isOrderNumberForOrderId(record.orderNumber, record.orderId, record.businessDate) &&
    record.returnedQuantityAfter === record.returnedQuantityBefore + record.quantity &&
    record.inventoryEffect ===
      saleReturnInventoryEffectForDisposition(record.physicalDisposition) &&
    (record.physicalDisposition === "SALEABLE_RETURNED"
      ? isTrackedSaleInventoryReturnSnapshot(record.inventoryReturnSnapshot) &&
        record.inventoryReturnSnapshot.skuId === record.skuId &&
        record.inventoryReturnSnapshot.returnedBaseQuantity === record.quantity &&
        record.inventoryReturnSnapshot.returnedQuantityBefore === record.returnedQuantityBefore &&
        record.inventoryReturnSnapshot.returnedQuantityAfter === record.returnedQuantityAfter
      : record.inventoryReturnSnapshot === null)
  );
}

export const OrderItemSaleReturnResultSchema = Type.Object(
  {
    returnRecord: OrderItemSaleReturnRecordSchema,
    order: OrderDetailSchema,
  },
  { additionalProperties: false },
);
export type OrderItemSaleReturnResult = Static<typeof OrderItemSaleReturnResultSchema>;

export const OrderItemSaleReturnOperatorResultSchema = Type.Object(
  {
    returnId: Type.String({ minLength: 1, maxLength: 128 }),
    itemId: Type.String({ minLength: 1, maxLength: 128 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    orderAmountReductionFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    inventoryQuantityBefore: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    inventoryQuantityAfter: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    occurredAt: Type.String({ format: "date-time" }),
    order: OrderDetailSchema,
  },
  { additionalProperties: false },
);
export type OrderItemSaleReturnOperatorResult = Static<
  typeof OrderItemSaleReturnOperatorResultSchema
>;

export function isOrderItemSaleReturnOperatorResult(
  value: unknown,
): value is OrderItemSaleReturnOperatorResult {
  if (!Value.Check(OrderItemSaleReturnOperatorResultSchema, value)) return false;
  const result = value as OrderItemSaleReturnOperatorResult;
  if (
    result.inventoryEffect !== saleReturnInventoryEffectForDisposition(result.physicalDisposition)
  ) {
    return false;
  }
  return result.physicalDisposition === "SALEABLE_RETURNED"
    ? result.inventoryQuantityBefore !== null &&
        result.inventoryQuantityAfter === result.inventoryQuantityBefore + result.quantity
    : result.inventoryQuantityBefore === null && result.inventoryQuantityAfter === null;
}

const OrderHistoryMoneyFenSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const OrderHistoryReadModelSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 128 }),
    orderNumber: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    status: Type.Union([
      Type.Literal("OPEN"),
      Type.Literal("SETTLED"),
      Type.Literal("REFUNDED"),
      Type.Literal("CONFLICT"),
    ]),
    startedAt: Type.String({ format: "date-time" }),
    tableFeeFen: OrderHistoryMoneyFenSchema,
    productTotalFen: OrderHistoryMoneyFenSchema,
    coachServiceTotalFen: Type.Integer({
      minimum: -Number.MAX_SAFE_INTEGER,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    rentalTotalFen: Type.Optional(OrderHistoryMoneyFenSchema),
    rentalItems: Type.Optional(Type.Array(CustomerRentalChargeItemSchema, { maxItems: 1_000 })),
    totalFen: OrderHistoryMoneyFenSchema,
    paidFen: OrderHistoryMoneyFenSchema,
    refundedFen: OrderHistoryMoneyFenSchema,
    groupBuyFulfillments: Type.Array(
      Type.Object(
        {
          id: Type.String({ minLength: 1, maxLength: 128 }),
          provider: Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]),
          voucherCoveredFen: OrderHistoryMoneyFenSchema,
          customerSupplementFen: OrderHistoryMoneyFenSchema,
        },
        { additionalProperties: false },
      ),
      { maxItems: 100 },
    ),
    payments: Type.Array(
      Type.Object(
        {
          id: Type.String({ minLength: 1, maxLength: 128 }),
          method: PaymentMethodSchema,
          status: PaymentStatusSchema,
        },
        { additionalProperties: false },
      ),
      { maxItems: 100 },
    ),
    refunds: Type.Array(
      Type.Object(
        {
          id: Type.String({ minLength: 1, maxLength: 128 }),
          amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          reason: Type.String({ minLength: 2, maxLength: 200 }),
          operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
          authorizerDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
          createdAt: Type.String({ format: "date-time" }),
        },
        { additionalProperties: false },
      ),
      { maxItems: 100 },
    ),
    offlineOrigin: Type.Boolean(),
    syncState: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("SYNCED"),
      Type.Literal("CONFLICT"),
      Type.Literal("FAILED"),
      Type.Literal("ISOLATED"),
    ]),
  },
  { additionalProperties: false },
);
export type OrderHistoryReadModel = Static<typeof OrderHistoryReadModelSchema>;

const OrderHistoryReadModelListSchema = Type.Array(OrderHistoryReadModelSchema, {
  maxItems: 5_000,
});

export function isOrderHistoryReadModelList(value: unknown): value is OrderHistoryReadModel[] {
  if (!Value.Check(OrderHistoryReadModelListSchema, value)) return false;
  const orderIds = new Set<string>();
  for (const order of value) {
    if (orderIds.has(order.id)) return false;
    orderIds.add(order.id);
    if (
      !isOrderNumberForOrderId(order.orderNumber, order.id, order.businessDate) ||
      ![
        order.tableFeeFen,
        order.productTotalFen,
        order.coachServiceTotalFen,
        order.rentalTotalFen ?? 0,
        order.totalFen,
        order.paidFen,
        order.refundedFen,
      ].every(Number.isSafeInteger) ||
      order.refundedFen > order.paidFen ||
      !isRfc3339DateTime(order.startedAt)
    ) {
      return false;
    }
    const paymentIds = new Set(order.payments.map((payment) => payment.id));
    const refundIds = new Set(order.refunds.map((refund) => refund.id));
    const fulfillmentIds = new Set(order.groupBuyFulfillments.map((item) => item.id));
    const rentalItems = order.rentalItems ?? [];
    const rentalItemIds = new Set(rentalItems.map((item) => item.id));
    if (
      paymentIds.size !== order.payments.length ||
      refundIds.size !== order.refunds.length ||
      fulfillmentIds.size !== order.groupBuyFulfillments.length ||
      rentalItemIds.size !== rentalItems.length ||
      !rentalItems.every(
        (item) =>
          isCustomerRentalChargeItem(item) &&
          item.orderId === order.id &&
          item.orderNumber === order.orderNumber &&
          item.businessDate === order.businessDate &&
          item.tableNumber === order.tableNumber,
      ) ||
      !order.refunds.every(
        (refund) => Number.isSafeInteger(refund.amountFen) && isRfc3339DateTime(refund.createdAt),
      )
    ) {
      return false;
    }
    const refundTotalFen = order.refunds.reduce((sum, refund) => sum + refund.amountFen, 0);
    if (!Number.isSafeInteger(refundTotalFen) || refundTotalFen !== order.refundedFen) return false;
    const rentalTotalFen = rentalItems.reduce((sum, item) => sum + item.receivableFen, 0);
    if (!Number.isSafeInteger(rentalTotalFen) || rentalTotalFen !== (order.rentalTotalFen ?? 0)) {
      return false;
    }
  }
  return true;
}

const OrderStrongReportDateSchema = Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" });
const OrderStrongReportMoneyFenSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const OrderStrongReportSignedMoneyFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const OrderStrongReportCountSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const ORDER_STRONG_REPORT_ANOMALY_CODES = [
  "ORDER_OPEN",
  "PAYMENT_PENDING",
  "PAYMENT_EXCEPTION",
  "UNEXPLAINED_DIFFERENCE",
  "ORDER_CONFLICT",
  "SYNC_FAILED",
  "SYNC_ISOLATED",
  "OPENING_SOURCE_UNKNOWN",
] as const;
export type OrderStrongReportAnomalyCode = (typeof ORDER_STRONG_REPORT_ANOMALY_CODES)[number];

const OrderStrongReportAnomalyCodeSchema = Type.Union(
  ORDER_STRONG_REPORT_ANOMALY_CODES.map((code) => Type.Literal(code)),
);
const OrderStrongReportPaymentMethodSchema = Type.Union([
  Type.Literal("CASH"),
  Type.Literal("STATIC_WECHAT"),
  Type.Literal("STATIC_ALIPAY"),
  Type.Literal("MEMBER_BALANCE"),
]);
export const ORDER_STRONG_REPORT_OPENING_METHODS = [
  "DIRECT",
  "RESERVATION",
  "GROUP_BUY_ONLINE",
  "UNKNOWN",
] as const;
export type OrderStrongReportOpeningMethod = (typeof ORDER_STRONG_REPORT_OPENING_METHODS)[number];
const OrderStrongReportOpeningMethodSchema = Type.Union(
  ORDER_STRONG_REPORT_OPENING_METHODS.map((method) => Type.Literal(method)),
);
export const OrderStrongReportOpeningSourceSchema = Type.Object(
  {
    method: OrderStrongReportOpeningMethodSchema,
    referenceNumber: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    reason: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type OrderStrongReportOpeningSource = Static<typeof OrderStrongReportOpeningSourceSchema>;

export function isOrderStrongReportOpeningSource(
  value: unknown,
): value is OrderStrongReportOpeningSource {
  if (!Value.Check(OrderStrongReportOpeningSourceSchema, value)) return false;
  if (value.referenceNumber !== null && value.referenceNumber !== value.referenceNumber.trim()) {
    return false;
  }
  if (value.reason !== null && value.reason !== value.reason.trim()) return false;
  switch (value.method) {
    case "DIRECT":
      return value.referenceNumber === null && value.reason === null;
    case "RESERVATION":
    case "GROUP_BUY_ONLINE":
      return value.referenceNumber !== null && value.reason === null;
    case "UNKNOWN":
      return value.referenceNumber === null && value.reason !== null;
  }
}

export const OrderStrongReportShiftScopeSchema = Type.Object(
  {
    shiftId: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: OrderStrongReportDateSchema,
    status: Type.Literal("OPEN"),
    openedByDisplayName: Type.String({ minLength: 1, maxLength: 128 }),
    startedAt: Type.String({ format: "date-time" }),
    endedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type OrderStrongReportShiftScope = Static<typeof OrderStrongReportShiftScopeSchema>;

export const OrderStrongReportFilterSchema = Type.Object(
  {
    offlineOnly: Type.Boolean(),
    dateFrom: Type.Union([OrderStrongReportDateSchema, Type.Null()]),
    dateTo: Type.Union([OrderStrongReportDateSchema, Type.Null()]),
    orderQuery: Type.String({ maxLength: 128 }),
    tableNumber: Type.String({ maxLength: 32 }),
    productQuery: Type.String({ maxLength: 80 }),
    memberSearch: Type.String({ maxLength: 80 }),
    paymentMethod: Type.Union([OrderStrongReportPaymentMethodSchema, Type.Null()]),
    openingMethod: Type.Union([OrderStrongReportOpeningMethodSchema, Type.Null()]),
    shiftId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type OrderStrongReportFilter = Static<typeof OrderStrongReportFilterSchema>;

export const OrderStrongReportAccidentalVoidSchema = Type.Object(
  {
    voidedAt: Type.String({ format: "date-time" }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
  },
  { additionalProperties: false },
);
export type OrderStrongReportAccidentalVoid = Static<typeof OrderStrongReportAccidentalVoidSchema>;

export const OrderStrongReportRowSchema = Type.Object(
  {
    orderId: Type.String({ minLength: 1, maxLength: 128 }),
    receivableFen: OrderStrongReportMoneyFenSchema,
    paidReturnAdjustmentFen: OrderStrongReportMoneyFenSchema,
    confirmedReceiptFen: OrderStrongReportMoneyFenSchema,
    pendingReconciliationFen: OrderStrongReportMoneyFenSchema,
    exceptionReconciliationFen: OrderStrongReportMoneyFenSchema,
    unsettledFen: OrderStrongReportMoneyFenSchema,
    refundedFen: OrderStrongReportMoneyFenSchema,
    unexplainedDifferenceFen: OrderStrongReportSignedMoneyFenSchema,
    billableSeconds: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    openingSource: OrderStrongReportOpeningSourceSchema,
    accidentalVoid: Type.Union([OrderStrongReportAccidentalVoidSchema, Type.Null()]),
    anomalyCodes: Type.Array(OrderStrongReportAnomalyCodeSchema, {
      maxItems: ORDER_STRONG_REPORT_ANOMALY_CODES.length,
    }),
  },
  { additionalProperties: false },
);
export type OrderStrongReportRow = Static<typeof OrderStrongReportRowSchema>;

export const OrderStrongReportSummarySchema = Type.Object(
  {
    orderCount: OrderStrongReportCountSchema,
    offlineOrderCount: OrderStrongReportCountSchema,
    receivableFen: OrderStrongReportMoneyFenSchema,
    paidReturnAdjustmentFen: OrderStrongReportMoneyFenSchema,
    confirmedReceiptFen: OrderStrongReportMoneyFenSchema,
    pendingReconciliationFen: OrderStrongReportMoneyFenSchema,
    exceptionReconciliationFen: OrderStrongReportMoneyFenSchema,
    unsettledOrderCount: OrderStrongReportCountSchema,
    unsettledFen: OrderStrongReportMoneyFenSchema,
    refundedOrderCount: OrderStrongReportCountSchema,
    refundedFen: OrderStrongReportMoneyFenSchema,
    conflictOrderCount: OrderStrongReportCountSchema,
    anomalyOrderCount: OrderStrongReportCountSchema,
    unexplainedDifferenceFen: OrderStrongReportSignedMoneyFenSchema,
    averageOrderReceivableFen: Type.Union([OrderStrongReportMoneyFenSchema, Type.Null()]),
    refundOrderRateBasisPoints: Type.Union([
      Type.Integer({ minimum: 0, maximum: 10_000 }),
      Type.Null(),
    ]),
    tableUsageAvailability: Type.Union([
      Type.Literal("AVAILABLE"),
      Type.Literal("PARTIAL"),
      Type.Literal("NOT_AVAILABLE"),
    ]),
    totalBillableSeconds: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    tableUsageKnownOrders: OrderStrongReportCountSchema,
    tableUsageUnknownOrders: OrderStrongReportCountSchema,
    onAccountAvailability: Type.Literal("NOT_AVAILABLE"),
    onAccountReason: Type.Literal("当前没有按订单关联的应收账本，不能把未结订单当作挂账。"),
    cancellationRateAvailability: Type.Literal("AVAILABLE"),
    cancellationOrderCount: OrderStrongReportCountSchema,
    cancellationRateBasisPoints: Type.Union([
      Type.Integer({ minimum: 0, maximum: 10_000 }),
      Type.Null(),
    ]),
    cancellationRateReason: Type.Literal(
      "只统计已留下误开台作废时间和原因的订单，不把退款、未结或普通结账订单算作撤单。",
    ),
  },
  { additionalProperties: false },
);
export type OrderStrongReportSummary = Static<typeof OrderStrongReportSummarySchema>;

export const OrderStrongReportReadModelSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    generatedAt: Type.String({ format: "date-time" }),
    filters: OrderStrongReportFilterSchema,
    shift: Type.Union([OrderStrongReportShiftScopeSchema, Type.Null()]),
    summary: OrderStrongReportSummarySchema,
    orders: Type.Array(OrderHistoryReadModelSchema, { maxItems: 5_000 }),
    rows: Type.Array(OrderStrongReportRowSchema, { maxItems: 5_000 }),
    fileName: Type.String({ minLength: 1, maxLength: 96 }),
    csv: Type.String({ minLength: 1, maxLength: 16_777_216 }),
    contentSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type OrderStrongReportReadModel = Static<typeof OrderStrongReportReadModelSchema>;

const ORDER_STRONG_REPORT_CONFIRMED_PAYMENT_STATUSES = new Set<PaymentStatus>([
  "CONFIRMED_LOCAL",
  "CONFIRMED_CLOUD",
  "MANUALLY_RECONCILED",
  "PARTIALLY_REFUNDED",
  "REFUNDED",
]);

function orderStrongReportSafeSum(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) throw new Error("ORDER_STRONG_REPORT_MONEY_INVALID");
    total += value;
    if (!Number.isSafeInteger(total)) throw new Error("ORDER_STRONG_REPORT_MONEY_OVERFLOW");
  }
  return total;
}

function expectedOrderStrongReportAnomalyCodes(input: {
  status: OrderHistoryReadModel["status"];
  syncState: OrderHistoryReadModel["syncState"];
  pendingReconciliationFen: number;
  exceptionReconciliationFen: number;
  unexplainedDifferenceFen: number;
  openingMethod: OrderStrongReportOpeningMethod;
}): OrderStrongReportAnomalyCode[] {
  const codes: OrderStrongReportAnomalyCode[] = [];
  if (input.status === "OPEN") codes.push("ORDER_OPEN");
  if (input.pendingReconciliationFen > 0) codes.push("PAYMENT_PENDING");
  if (input.exceptionReconciliationFen > 0) codes.push("PAYMENT_EXCEPTION");
  if (input.unexplainedDifferenceFen !== 0) codes.push("UNEXPLAINED_DIFFERENCE");
  if (input.status === "CONFLICT" || input.syncState === "CONFLICT") {
    codes.push("ORDER_CONFLICT");
  }
  if (input.syncState === "FAILED") codes.push("SYNC_FAILED");
  if (input.syncState === "ISOLATED") codes.push("SYNC_ISOLATED");
  if (input.openingMethod === "UNKNOWN") codes.push("OPENING_SOURCE_UNKNOWN");
  return codes;
}

export function buildOrderStrongReportRows(
  details: readonly OrderDetail[],
  orders: readonly OrderHistoryReadModel[],
  paidReturnAdjustmentFenByOrderId: ReadonlyMap<string, number>,
  openingSourceByOrderId: ReadonlyMap<string, OrderStrongReportOpeningSource>,
): OrderStrongReportRow[] {
  if (details.length !== orders.length) throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
  const detailById = new Map(details.map((detail) => [detail.id, detail]));
  if (
    detailById.size !== details.length ||
    paidReturnAdjustmentFenByOrderId.size !== orders.length ||
    openingSourceByOrderId.size !== orders.length
  ) {
    throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
  }
  return orders.map((order) => {
    const detail = detailById.get(order.id);
    const paidReturnAdjustmentFen = paidReturnAdjustmentFenByOrderId.get(order.id);
    const openingSource = openingSourceByOrderId.get(order.id);
    if (!detail) throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
    const accidentalVoid = orderStrongReportAccidentalVoid(detail, order);
    if (
      !isOrderDetailReadModel(detail) ||
      typeof paidReturnAdjustmentFen !== "number" ||
      !Number.isSafeInteger(paidReturnAdjustmentFen) ||
      paidReturnAdjustmentFen < 0 ||
      !isOrderStrongReportOpeningSource(openingSource)
    ) {
      throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
    }
    const confirmedReceiptFen = orderStrongReportSafeSum(
      detail.payments
        .filter((payment) => ORDER_STRONG_REPORT_CONFIRMED_PAYMENT_STATUSES.has(payment.status))
        .map((payment) => payment.amountFen),
    );
    const pendingReconciliationFen = orderStrongReportSafeSum(
      detail.payments
        .filter((payment) => payment.status === "PENDING_RECONCILIATION")
        .map((payment) => payment.amountFen),
    );
    const exceptionReconciliationFen = orderStrongReportSafeSum(
      detail.payments
        .filter((payment) => payment.status === "RECONCILIATION_EXCEPTION")
        .map((payment) => payment.amountFen),
    );
    const classifiedPaymentFen = orderStrongReportSafeSum([
      confirmedReceiptFen,
      pendingReconciliationFen,
      exceptionReconciliationFen,
    ]);
    const unsettledFen =
      order.status === "OPEN" ? Math.max(0, order.totalFen - classifiedPaymentFen) : 0;
    const unexplainedDifferenceFen = orderStrongReportSafeSum([
      order.totalFen,
      paidReturnAdjustmentFen,
      -confirmedReceiptFen,
      -pendingReconciliationFen,
      -exceptionReconciliationFen,
      -unsettledFen,
    ]);
    return {
      orderId: order.id,
      receivableFen: order.totalFen,
      paidReturnAdjustmentFen,
      confirmedReceiptFen,
      pendingReconciliationFen,
      exceptionReconciliationFen,
      unsettledFen,
      refundedFen: order.refundedFen,
      unexplainedDifferenceFen,
      billableSeconds: detail.billingBreakdown?.billableSeconds ?? null,
      openingSource,
      accidentalVoid,
      anomalyCodes: expectedOrderStrongReportAnomalyCodes({
        status: order.status,
        syncState: order.syncState,
        pendingReconciliationFen,
        exceptionReconciliationFen,
        unexplainedDifferenceFen,
        openingMethod: openingSource.method,
      }),
    };
  });
}

function orderStrongReportAccidentalVoid(
  detail: OrderDetail,
  order: OrderHistoryReadModel,
): OrderStrongReportAccidentalVoid | null {
  const voidedAt = detail.voidedAt ?? null;
  const voidReason = detail.voidReason ?? null;
  if (voidedAt === null) {
    if (voidReason !== null) throw new Error("ORDER_STRONG_REPORT_ACCIDENTAL_VOID_INVALID");
    return null;
  }
  if (
    voidReason === null ||
    detail.status !== "SETTLED" ||
    order.status !== "SETTLED" ||
    !isCanonicalIsoTimestamp(voidedAt) ||
    voidReason !== voidReason.trim() ||
    voidReason.length < 2 ||
    voidReason.length > 200
  ) {
    throw new Error("ORDER_STRONG_REPORT_ACCIDENTAL_VOID_INVALID");
  }
  return { voidedAt, reason: voidReason };
}

export function summarizeOrderStrongReport(
  orders: readonly OrderHistoryReadModel[],
  rows: readonly OrderStrongReportRow[],
): OrderStrongReportSummary {
  if (orders.length !== rows.length) throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
  const rowById = new Map(rows.map((row) => [row.orderId, row]));
  if (rowById.size !== rows.length || orders.some((order) => !rowById.has(order.id))) {
    throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
  }
  const billableSeconds = rows.flatMap((row) =>
    row.billableSeconds === null ? [] : [row.billableSeconds],
  );
  const tableUsageUnknownOrders = rows.length - billableSeconds.length;
  const totalBillableSeconds = orderStrongReportSafeSum(billableSeconds);
  const orderCount = orders.length;
  const receivableFen = orderStrongReportSafeSum(rows.map((row) => row.receivableFen));
  const refundedOrderCount = rows.filter((row) => row.refundedFen > 0).length;
  const cancellationOrderCount = rows.filter((row) => row.accidentalVoid !== null).length;
  return {
    orderCount,
    offlineOrderCount: orders.filter((order) => order.offlineOrigin).length,
    receivableFen,
    paidReturnAdjustmentFen: orderStrongReportSafeSum(
      rows.map((row) => row.paidReturnAdjustmentFen),
    ),
    confirmedReceiptFen: orderStrongReportSafeSum(rows.map((row) => row.confirmedReceiptFen)),
    pendingReconciliationFen: orderStrongReportSafeSum(
      rows.map((row) => row.pendingReconciliationFen),
    ),
    exceptionReconciliationFen: orderStrongReportSafeSum(
      rows.map((row) => row.exceptionReconciliationFen),
    ),
    unsettledOrderCount: orders.filter((order) => order.status === "OPEN").length,
    unsettledFen: orderStrongReportSafeSum(rows.map((row) => row.unsettledFen)),
    refundedOrderCount,
    refundedFen: orderStrongReportSafeSum(rows.map((row) => row.refundedFen)),
    conflictOrderCount: orders.filter(
      (order) => order.status === "CONFLICT" || order.syncState === "CONFLICT",
    ).length,
    anomalyOrderCount: rows.filter((row) => row.anomalyCodes.length > 0).length,
    unexplainedDifferenceFen: orderStrongReportSafeSum(
      rows.map((row) => row.unexplainedDifferenceFen),
    ),
    averageOrderReceivableFen: orderCount === 0 ? null : Math.round(receivableFen / orderCount),
    refundOrderRateBasisPoints:
      orderCount === 0 ? null : Math.round((refundedOrderCount * 10_000) / orderCount),
    tableUsageAvailability:
      tableUsageUnknownOrders === 0
        ? "AVAILABLE"
        : billableSeconds.length === 0
          ? "NOT_AVAILABLE"
          : "PARTIAL",
    totalBillableSeconds:
      billableSeconds.length === 0 && orderCount > 0 ? null : totalBillableSeconds,
    tableUsageKnownOrders: billableSeconds.length,
    tableUsageUnknownOrders,
    onAccountAvailability: "NOT_AVAILABLE",
    onAccountReason: "当前没有按订单关联的应收账本，不能把未结订单当作挂账。",
    cancellationRateAvailability: "AVAILABLE",
    cancellationOrderCount,
    cancellationRateBasisPoints:
      orderCount === 0 ? null : Math.round((cancellationOrderCount * 10_000) / orderCount),
    cancellationRateReason:
      "只统计已留下误开台作废时间和原因的订单，不把退款、未结或普通结账订单算作撤单。",
  };
}

function orderStrongReportCsvText(value: string): string {
  const neutralized = /^[=+\-@\t\r]/u.test(value) ? `'${value}` : value;
  return `"${neutralized.replaceAll('"', '""')}"`;
}

function orderStrongReportCsvNumber(value: number | null): string {
  if (value === null) return '""';
  if (!Number.isSafeInteger(value)) throw new Error("ORDER_STRONG_REPORT_CSV_NUMBER_INVALID");
  return `"${value}"`;
}

function orderStrongReportFilterLabel(
  filters: OrderStrongReportFilter,
  shift: OrderStrongReportShiftScope | null,
): string {
  const parts = [
    filters.dateFrom || filters.dateTo
      ? `营业日 ${filters.dateFrom ?? "最早记录"} 至 ${filters.dateTo ?? "最新记录"}`
      : "全部营业日",
    filters.offlineOnly ? "仅离线订单" : "全部来源",
  ];
  if (filters.orderQuery) parts.push(`业务单号或历史记录标识含“${filters.orderQuery}”`);
  if (filters.tableNumber) parts.push(`桌号含“${filters.tableNumber}”`);
  if (filters.productQuery) {
    parts.push(`下单时商品名称或商品编号含“${filters.productQuery}”`);
  }
  if (filters.memberSearch) parts.push(`会员搜索“${filters.memberSearch}”`);
  if (filters.paymentMethod) {
    parts.push(`收款方式 ${paymentMethodTerm(filters.paymentMethod).label}`);
  }
  if (filters.openingMethod) {
    parts.push(`开台方式 ${orderOpeningMethodTerm(filters.openingMethod).label}`);
  }
  if (shift) {
    parts.push(
      `现金班次 ${shift.businessDate} ${shift.openedByDisplayName}（${shift.startedAt} 至 ${shift.endedAt}）`,
    );
  } else {
    parts.push("不限定现金班次");
  }
  return parts.join("；");
}

export function orderStrongReportFileName(filters: OrderStrongReportFilter): string {
  const range =
    filters.dateFrom === filters.dateTo && filters.dateFrom !== null
      ? filters.dateFrom
      : `${filters.dateFrom ?? "全部"}-${filters.dateTo ?? "全部"}`;
  return `订单核账-${range}.csv`;
}

export function buildOrderStrongReportCsv(input: {
  generatedAt: string;
  filters: OrderStrongReportFilter;
  shift: OrderStrongReportShiftScope | null;
  summary: OrderStrongReportSummary;
  orders: readonly OrderHistoryReadModel[];
  rows: readonly OrderStrongReportRow[];
}): string {
  const rowById = new Map(input.rows.map((row) => [row.orderId, row]));
  const lines = [
    `${orderStrongReportCsvText("报表条件")},${orderStrongReportCsvText(orderStrongReportFilterLabel(input.filters, input.shift))}`,
    `${orderStrongReportCsvText("生成时间")},${orderStrongReportCsvText(input.generatedAt)}`,
    `${orderStrongReportCsvText("订单数")},${orderStrongReportCsvNumber(input.summary.orderCount)}`,
    `${orderStrongReportCsvText("系统应收（分）")},${orderStrongReportCsvNumber(input.summary.receivableFen)}`,
    `${orderStrongReportCsvText("已付款退货应收调减（分）")},${orderStrongReportCsvNumber(input.summary.paidReturnAdjustmentFen)}`,
    `${orderStrongReportCsvText("已确认实收（分）")},${orderStrongReportCsvNumber(input.summary.confirmedReceiptFen)}`,
    `${orderStrongReportCsvText("等待渠道核对（分）")},${orderStrongReportCsvNumber(input.summary.pendingReconciliationFen)}`,
    `${orderStrongReportCsvText("收款核对异常（分）")},${orderStrongReportCsvNumber(input.summary.exceptionReconciliationFen)}`,
    `${orderStrongReportCsvText("未结应收（分）")},${orderStrongReportCsvNumber(input.summary.unsettledFen)}`,
    `${orderStrongReportCsvText("退款（分）")},${orderStrongReportCsvNumber(input.summary.refundedFen)}`,
    `${orderStrongReportCsvText("未说明金额差异（分）")},${orderStrongReportCsvNumber(input.summary.unexplainedDifferenceFen)}`,
    `${orderStrongReportCsvText("平均每单应收（分）")},${orderStrongReportCsvNumber(input.summary.averageOrderReceivableFen)}`,
    `${orderStrongReportCsvText("有退款订单占比（基点）")},${orderStrongReportCsvNumber(input.summary.refundOrderRateBasisPoints)}`,
    `${orderStrongReportCsvText("计费台时（秒）")},${orderStrongReportCsvNumber(input.summary.totalBillableSeconds)}`,
    `${orderStrongReportCsvText("台时已知订单数")},${orderStrongReportCsvNumber(input.summary.tableUsageKnownOrders)}`,
    `${orderStrongReportCsvText("台时暂不可用订单数")},${orderStrongReportCsvNumber(input.summary.tableUsageUnknownOrders)}`,
    `${orderStrongReportCsvText("异常订单数")},${orderStrongReportCsvNumber(input.summary.anomalyOrderCount)}`,
    `${orderStrongReportCsvText("挂账")},${orderStrongReportCsvText(`暂不可用：${input.summary.onAccountReason}`)}`,
    `${orderStrongReportCsvText("误开台作废订单数")},${orderStrongReportCsvNumber(input.summary.cancellationOrderCount)}`,
    `${orderStrongReportCsvText("误开台作废率（基点）")},${orderStrongReportCsvNumber(input.summary.cancellationRateBasisPoints)}`,
    `${orderStrongReportCsvText("误开台作废口径")},${orderStrongReportCsvText(input.summary.cancellationRateReason)}`,
  ];
  lines.push("");
  lines.push(
    [
      "业务单号或历史记录标识",
      "营业日",
      "桌号",
      "状态",
      "状态说明",
      "订单下一步",
      "记录同步状态",
      "同步状态说明",
      "同步下一步",
      "开台方式",
      "开台方式说明",
      "开台核对下一步",
      "来源单号",
      "来源说明",
      "收款记录",
      "系统应收（分）",
      "已付款退货应收调减（分）",
      "已确认实收（分）",
      "等待渠道核对（分）",
      "收款核对异常（分）",
      "未结应收（分）",
      "退款（分）",
      "未说明金额差异（分）",
      "计费台时（秒）",
      "误开台作废",
      "作废时间",
      "作废原因",
      "异常定位",
      "异常说明与下一步",
    ]
      .map(orderStrongReportCsvText)
      .join(","),
  );
  for (const order of input.orders) {
    const row = rowById.get(order.id);
    if (!row) throw new Error("ORDER_STRONG_REPORT_ORDER_SET_DRIFT");
    const status = orderStatusTerm(order.status);
    const syncState = syncStateTerm(order.syncState);
    const openingMethod = orderOpeningMethodTerm(row.openingSource.method);
    const paymentSummary =
      order.payments.length === 0
        ? "尚未收款"
        : order.payments
            .map((payment) => {
              const method = paymentMethodTerm(payment.method);
              const paymentStatus = paymentStatusTerm(payment.status);
              return `${method.label}：${paymentStatus.label}。${paymentStatus.explanation}${paymentStatus.nextStep}`;
            })
            .join("；");
    lines.push(
      [
        orderStrongReportCsvText(order.orderNumber ?? order.id),
        orderStrongReportCsvText(order.businessDate),
        orderStrongReportCsvText(order.tableNumber),
        orderStrongReportCsvText(status.label),
        orderStrongReportCsvText(status.explanation),
        orderStrongReportCsvText(status.nextStep),
        orderStrongReportCsvText(syncState.label),
        orderStrongReportCsvText(syncState.explanation),
        orderStrongReportCsvText(syncState.nextStep),
        orderStrongReportCsvText(openingMethod.label),
        orderStrongReportCsvText(openingMethod.explanation),
        orderStrongReportCsvText(openingMethod.nextStep),
        orderStrongReportCsvText(row.openingSource.referenceNumber ?? ""),
        orderStrongReportCsvText(row.openingSource.reason ?? ""),
        orderStrongReportCsvText(paymentSummary),
        orderStrongReportCsvNumber(row.receivableFen),
        orderStrongReportCsvNumber(row.paidReturnAdjustmentFen),
        orderStrongReportCsvNumber(row.confirmedReceiptFen),
        orderStrongReportCsvNumber(row.pendingReconciliationFen),
        orderStrongReportCsvNumber(row.exceptionReconciliationFen),
        orderStrongReportCsvNumber(row.unsettledFen),
        orderStrongReportCsvNumber(row.refundedFen),
        orderStrongReportCsvNumber(row.unexplainedDifferenceFen),
        row.billableSeconds === null
          ? orderStrongReportCsvText("暂不可用")
          : orderStrongReportCsvNumber(row.billableSeconds),
        orderStrongReportCsvText(row.accidentalVoid === null ? "否" : "是"),
        orderStrongReportCsvText(row.accidentalVoid?.voidedAt ?? ""),
        orderStrongReportCsvText(row.accidentalVoid?.reason ?? ""),
        orderStrongReportCsvText(
          row.anomalyCodes.map((code) => orderAnomalyTerm(code).label).join("；") || "无",
        ),
        orderStrongReportCsvText(
          row.anomalyCodes
            .map((code) => {
              const anomaly = orderAnomalyTerm(code);
              return `${anomaly.explanation}${anomaly.nextStep}`;
            })
            .join("；") || "无",
        ),
      ].join(","),
    );
  }
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

function orderStrongReportFilterIsCanonical(filters: OrderStrongReportFilter): boolean {
  const canonicalDate = (value: string | null): boolean => {
    if (value === null) return true;
    const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
    return (
      Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
    );
  };
  return (
    canonicalDate(filters.dateFrom) &&
    canonicalDate(filters.dateTo) &&
    (filters.dateFrom === null || filters.dateTo === null || filters.dateFrom <= filters.dateTo) &&
    filters.orderQuery === filters.orderQuery.trim() &&
    filters.tableNumber === filters.tableNumber.trim() &&
    filters.productQuery === filters.productQuery.trim() &&
    filters.memberSearch === filters.memberSearch.trim() &&
    (filters.shiftId === null || filters.shiftId === filters.shiftId.trim())
  );
}

function orderStrongReportFiltersEqual(
  left: OrderStrongReportFilter,
  right: OrderStrongReportFilter,
): boolean {
  return (
    left.offlineOnly === right.offlineOnly &&
    left.dateFrom === right.dateFrom &&
    left.dateTo === right.dateTo &&
    left.orderQuery === right.orderQuery &&
    left.tableNumber === right.tableNumber &&
    left.productQuery === right.productQuery &&
    left.memberSearch === right.memberSearch &&
    left.paymentMethod === right.paymentMethod &&
    left.openingMethod === right.openingMethod &&
    left.shiftId === right.shiftId
  );
}

function orderStrongReportShiftIsCanonical(
  shift: OrderStrongReportShiftScope | null,
  filters: OrderStrongReportFilter,
  generatedAt: string,
): boolean {
  if (shift === null) return filters.shiftId === null;
  if (
    filters.shiftId !== shift.shiftId ||
    shift.openedByDisplayName !== shift.openedByDisplayName.trim() ||
    !isCanonicalIsoTimestamp(shift.startedAt) ||
    !isCanonicalIsoTimestamp(shift.endedAt) ||
    shift.startedAt > shift.endedAt ||
    shift.endedAt > generatedAt ||
    shift.endedAt !== generatedAt
  ) {
    return false;
  }
  const businessDateMilliseconds = Date.parse(`${shift.businessDate}T00:00:00.000Z`);
  return (
    Number.isFinite(businessDateMilliseconds) &&
    new Date(businessDateMilliseconds).toISOString().slice(0, 10) === shift.businessDate
  );
}

export function isOrderStrongReportReadModel(
  value: unknown,
  expected?: {
    tenantId: string;
    storeId: string;
    filters?: OrderStrongReportFilter;
  },
): value is OrderStrongReportReadModel {
  if (
    !Value.Check(OrderStrongReportReadModelSchema, value) ||
    !isCanonicalIsoTimestamp(value.generatedAt) ||
    !orderStrongReportFilterIsCanonical(value.filters) ||
    !orderStrongReportShiftIsCanonical(value.shift, value.filters, value.generatedAt) ||
    !isOrderHistoryReadModelList(value.orders) ||
    value.orders.length !== value.rows.length ||
    (expected !== undefined &&
      (value.tenantId !== expected.tenantId ||
        value.storeId !== expected.storeId ||
        (expected.filters !== undefined &&
          !orderStrongReportFiltersEqual(value.filters, expected.filters))))
  ) {
    return false;
  }
  for (const [index, order] of value.orders.entries()) {
    const row = value.rows[index];
    let expectedUnsettledFen: number;
    let expectedUnexplainedDifferenceFen: number;
    try {
      const classifiedPaymentFen = orderStrongReportSafeSum([
        row?.confirmedReceiptFen ?? Number.NaN,
        row?.pendingReconciliationFen ?? Number.NaN,
        row?.exceptionReconciliationFen ?? Number.NaN,
      ]);
      expectedUnsettledFen =
        order.status === "OPEN" ? Math.max(0, order.totalFen - classifiedPaymentFen) : 0;
      expectedUnexplainedDifferenceFen = orderStrongReportSafeSum([
        row?.receivableFen ?? Number.NaN,
        row?.paidReturnAdjustmentFen ?? Number.NaN,
        -(row?.confirmedReceiptFen ?? Number.NaN),
        -(row?.pendingReconciliationFen ?? Number.NaN),
        -(row?.exceptionReconciliationFen ?? Number.NaN),
        -expectedUnsettledFen,
      ]);
    } catch {
      return false;
    }
    if (
      row === undefined ||
      row.orderId !== order.id ||
      row.receivableFen !== order.totalFen ||
      row.refundedFen !== order.refundedFen ||
      !isOrderStrongReportOpeningSource(row.openingSource) ||
      (row.accidentalVoid !== null &&
        (order.status !== "SETTLED" ||
          !isCanonicalIsoTimestamp(row.accidentalVoid.voidedAt) ||
          row.accidentalVoid.voidedAt > value.generatedAt ||
          row.accidentalVoid.reason !== row.accidentalVoid.reason.trim())) ||
      row.unsettledFen !== expectedUnsettledFen ||
      row.unexplainedDifferenceFen !== expectedUnexplainedDifferenceFen ||
      new Set(row.anomalyCodes).size !== row.anomalyCodes.length ||
      JSON.stringify(row.anomalyCodes) !==
        JSON.stringify(
          expectedOrderStrongReportAnomalyCodes({
            status: order.status,
            syncState: order.syncState,
            pendingReconciliationFen: row.pendingReconciliationFen,
            exceptionReconciliationFen: row.exceptionReconciliationFen,
            unexplainedDifferenceFen: row.unexplainedDifferenceFen,
            openingMethod: row.openingSource.method,
          }),
        )
    ) {
      return false;
    }
  }
  let expectedSummary: OrderStrongReportSummary;
  try {
    expectedSummary = summarizeOrderStrongReport(value.orders, value.rows);
  } catch {
    return false;
  }
  const summaryRecord = value.summary as Record<string, unknown>;
  const summaryMatches = Object.entries(expectedSummary).every(
    ([key, expectedValue]) => summaryRecord[key] === expectedValue,
  );
  return (
    summaryMatches &&
    value.fileName === orderStrongReportFileName(value.filters) &&
    value.csv ===
      buildOrderStrongReportCsv({
        generatedAt: value.generatedAt,
        filters: value.filters,
        shift: value.shift,
        summary: value.summary,
        orders: value.orders,
        rows: value.rows,
      })
  );
}

export const ManualReviewItemSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    type: Type.Union([
      Type.Literal("PAYMENT"),
      Type.Literal("ORDER_CONFLICT"),
      Type.Literal("COACH_CONFLICT"),
      Type.Literal("MEMBER_RECHARGE"),
      Type.Literal("MEMBER_RECEIVABLE"),
      Type.Literal("CUSTOMER_STORAGE"),
      Type.Literal("CUSTOMER_RENTAL"),
    ]),
    objectId: Type.String({ minLength: 1 }),
    orderId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    reasonCode: Type.String({ minLength: 1 }),
    title: Type.String({ minLength: 1 }),
    detail: Type.String({ minLength: 1 }),
    amountFen: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    status: Type.Union([Type.Literal("PENDING"), Type.Literal("RESOLVED")]),
    createdAt: Type.String({ format: "date-time" }),
    resolvedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    offlineOrigin: Type.Boolean(),
    businessContext: Type.Optional(
      Type.Object(
        {
          eventType: Type.String({ minLength: 1 }),
          eventLabel: Type.String({ minLength: 1 }),
          tableNumber: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          sessionId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          orderId: Type.String({ minLength: 1 }),
          businessDate: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          occurredAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
          synchronizedAt: Type.String({ format: "date-time" }),
          actorId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
          difference: Type.String({ minLength: 1 }),
          recommendedAction: Type.String({ minLength: 1 }),
        },
        { additionalProperties: false },
      ),
    ),
    resolutionDisposition: Type.Optional(
      Type.Union([
        Type.Literal("KEEP_ISOLATED"),
        Type.Literal("TRANSFER_TO_FINANCE"),
        Type.Literal("TRANSFER_TO_OPERATIONS"),
      ]),
    ),
    memberRecharge: Type.Optional(
      Type.Object(
        {
          memberName: Type.String({ minLength: 1, maxLength: 80 }),
          paidAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
          method: Type.Union([Type.Literal("STATIC_WECHAT"), Type.Literal("STATIC_ALIPAY")]),
          rechargeStatus: Type.Union([
            Type.Literal("PENDING_RECONCILIATION"),
            Type.Literal("CONFIRMED_RECONCILED"),
            Type.Literal("RECONCILIATION_EXCEPTION"),
            Type.Literal("RECONCILIATION_CLOSED"),
          ]),
          originalRequestNote: Type.String({ maxLength: 500 }),
          latestReconciliationNote: Type.Union([Type.String({ maxLength: 500 }), Type.Null()]),
          reconciledAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
        },
        { additionalProperties: false },
      ),
    ),
    memberReceivable: Type.Optional(MemberReceivableWorkItemSnapshotSchema),
    customerStorage: Type.Optional(
      Type.Object(
        {
          storageNumber: Type.String({ pattern: "^JC-[0-9]{8}-[0-9A-F]{12}$" }),
          memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
          memberName: Type.String({ minLength: 1, maxLength: 80 }),
          productName: Type.String({ minLength: 1, maxLength: 120 }),
          remainingQuantity: Type.Integer({ minimum: 1, maximum: 999_999 }),
          unit: Type.String({ minLength: 1, maxLength: 16 }),
          exceptionType: Type.Union([
            Type.Literal("LOST"),
            Type.Literal("DAMAGED"),
            Type.Literal("DISPOSAL"),
          ]),
          note: Type.String({ minLength: 2, maxLength: 300 }),
          storageVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          expiresAt: Type.String({ format: "date-time" }),
        },
        { additionalProperties: false },
      ),
    ),
    customerRental: Type.Optional(
      Type.Object(
        {
          rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
          orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
          tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
          productName: Type.String({ minLength: 1, maxLength: 120 }),
          remainingQuantity: Type.Integer({ minimum: 1, maximum: 999_999 }),
          unit: Type.String({ minLength: 1, maxLength: 16 }),
          exceptionType: Type.Union([
            Type.Literal("LOST"),
            Type.Literal("DAMAGED"),
            Type.Literal("OVERDUE"),
          ]),
          note: Type.String({ minLength: 2, maxLength: 300 }),
          reportedByActorId: Type.String({ minLength: 1, maxLength: 128 }),
          rentalVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          expectedReturnAt: Type.String({ format: "date-time" }),
        },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);
export type ManualReviewItem = Static<typeof ManualReviewItemSchema>;

export function isTableSnapshot(value: unknown): value is TableSnapshot {
  return Value.Check(TableSnapshotSchema, value);
}

function isCanonicalTableTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function isTrustedTableSnapshot(
  value: unknown,
  expectedTenantId: string,
  expectedStoreId: string,
): value is TableSnapshot {
  if (!isTableSnapshot(value)) return false;
  if (
    value.tenantId !== expectedTenantId ||
    value.storeId !== expectedStoreId ||
    value.tableId.length > 128 ||
    value.tableNodeId.length > 128 ||
    value.tableNumber.length > 32 ||
    value.area.length > 80 ||
    !isCanonicalTableTimestamp(value.authoritativeAt) ||
    !Number.isSafeInteger(value.revision) ||
    !Number.isSafeInteger(value.estimatedFeeFen) ||
    value.currentTotalFen === undefined ||
    !Number.isSafeInteger(value.currentTotalFen) ||
    value.sessionElapsedSeconds === undefined ||
    !Number.isSafeInteger(value.sessionElapsedSeconds) ||
    value.sessionRiskLevel === undefined ||
    value.longSessionWarningMinutes === undefined ||
    !Number.isSafeInteger(value.longSessionWarningMinutes) ||
    value.longSessionCriticalMinutes === undefined ||
    !Number.isSafeInteger(value.longSessionCriticalMinutes) ||
    value.longSessionWarningMinutes >= value.longSessionCriticalMinutes
  ) {
    return false;
  }

  const inactive = value.state !== "IN_USE";
  if (inactive) {
    return (
      value.sessionId === null &&
      value.sessionStartedAt === null &&
      value.billingState === "ENDED" &&
      value.billingPausedAt === null &&
      value.pricingPlanName === null &&
      value.estimatedFeeFen === 0 &&
      value.currentTotalFen === 0 &&
      value.sessionElapsedSeconds === 0 &&
      value.sessionRiskLevel === "NORMAL"
    );
  }

  if (
    value.sessionId === null ||
    value.sessionId.trim().length === 0 ||
    value.sessionId.length > 128 ||
    value.sessionStartedAt === null ||
    !isCanonicalTableTimestamp(value.sessionStartedAt) ||
    Date.parse(value.sessionStartedAt) > Date.parse(value.authoritativeAt) ||
    value.billingState === undefined ||
    value.pricingPlanName === null ||
    value.pricingPlanName.trim().length === 0 ||
    value.pricingPlanName.length > 80 ||
    value.currentTotalFen < value.estimatedFeeFen
  ) {
    return false;
  }
  if (value.billingState === "PAUSED") {
    if (
      value.billingPausedAt === undefined ||
      value.billingPausedAt === null ||
      !isCanonicalTableTimestamp(value.billingPausedAt) ||
      Date.parse(value.billingPausedAt) < Date.parse(value.sessionStartedAt) ||
      Date.parse(value.billingPausedAt) > Date.parse(value.authoritativeAt)
    ) {
      return false;
    }
  } else if (value.billingPausedAt !== null) {
    return false;
  }

  const expectedRiskLevel =
    value.sessionElapsedSeconds >= value.longSessionCriticalMinutes * 60
      ? "CRITICAL"
      : value.sessionElapsedSeconds >= value.longSessionWarningMinutes * 60
        ? "WARNING"
        : "NORMAL";
  return value.sessionRiskLevel === expectedRiskLevel;
}

export function isTableSnapshotList(
  value: unknown,
  expectedTenantId: string,
  expectedStoreId: string,
): value is TableSnapshot[] {
  if (!Array.isArray(value) || value.length > 10_000) return false;
  const tableIds = new Set<string>();
  const tableNodeIds = new Set<string>();
  const tableNumbers = new Set<string>();
  const activeSessionIds = new Set<string>();
  for (const snapshot of value) {
    if (!isTrustedTableSnapshot(snapshot, expectedTenantId, expectedStoreId)) return false;
    if (
      tableIds.has(snapshot.tableId) ||
      tableNodeIds.has(snapshot.tableNodeId) ||
      tableNumbers.has(snapshot.tableNumber)
    ) {
      return false;
    }
    tableIds.add(snapshot.tableId);
    tableNodeIds.add(snapshot.tableNodeId);
    tableNumbers.add(snapshot.tableNumber);
    if (snapshot.state === "IN_USE" && snapshot.sessionId !== null) {
      if (activeSessionIds.has(snapshot.sessionId)) return false;
      activeSessionIds.add(snapshot.sessionId);
    }
  }
  return true;
}

export function isOrderDetail(value: unknown): value is OrderDetail {
  return (
    Value.Check(OrderDetailSchema, value) &&
    isOrderNumberForOrderId(value.orderNumber, value.id, value.businessDate) &&
    value.items.every(orderItemPricingFactsAreConsistent) &&
    (value.packageItems?.every(
      (item) =>
        isStorePackageOrderItem(item) &&
        item.orderId === value.id &&
        item.sessionId === value.sessionId,
    ) ??
      true) &&
    (value.rentalItems?.every(
      (item) =>
        isCustomerRentalChargeItem(item) &&
        item.orderId === value.id &&
        item.sessionId === value.sessionId,
    ) ??
      true) &&
    value.payments.every(isPaymentRecordConsistent)
  );
}

function isPaymentRecordConsistent(payment: PaymentRecord): boolean {
  const official = payment.method === "WECHAT_PAY" || payment.method === "ALIPAY";
  const hasOfficialBinding =
    typeof payment.officialPaymentAttemptId === "string" &&
    typeof payment.providerCode === "string" &&
    typeof payment.providerTransactionReference === "string" &&
    payment.referenceId === payment.providerTransactionReference;
  if (official) {
    return (
      payment.evidence === "PROVIDER_ACCEPTED" &&
      payment.status !== "CONFIRMED_LOCAL" &&
      payment.status !== "PENDING_RECONCILIATION" &&
      payment.status !== "MANUALLY_RECONCILED" &&
      payment.status !== "RECONCILIATION_EXCEPTION" &&
      !payment.offlineOrigin &&
      (payment.memberId === undefined || payment.memberId === null) &&
      hasOfficialBinding
    );
  }
  return (
    payment.evidence !== "PROVIDER_ACCEPTED" &&
    (payment.officialPaymentAttemptId === undefined || payment.officialPaymentAttemptId === null) &&
    (payment.providerCode === undefined || payment.providerCode === null) &&
    (payment.providerTransactionReference === undefined ||
      payment.providerTransactionReference === null)
  );
}

const ORDER_DETAIL_LINE_CAPACITY = 10_000;
const ORDER_DETAIL_AUXILIARY_CAPACITY = 1_000;
const ORDER_DETAIL_PRICE_SNAPSHOT_CAPACITY = 128;

function safeIntegerSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

export function isOrderDetailReadModel(value: unknown): value is OrderDetail {
  if (!isOrderDetail(value)) return false;
  const groupBuyFulfillments = value.groupBuyFulfillments ?? [];
  const priceSnapshots = value.priceSnapshots ?? [value.priceSnapshot];
  if (
    value.items.length > ORDER_DETAIL_LINE_CAPACITY ||
    (value.packageItems?.length ?? 0) > ORDER_DETAIL_AUXILIARY_CAPACITY ||
    (value.rentalItems?.length ?? 0) > ORDER_DETAIL_AUXILIARY_CAPACITY ||
    value.coachServiceItems.length > ORDER_DETAIL_LINE_CAPACITY ||
    groupBuyFulfillments.length > ORDER_DETAIL_AUXILIARY_CAPACITY ||
    value.payments.length > ORDER_DETAIL_AUXILIARY_CAPACITY ||
    value.refunds.length > ORDER_DETAIL_AUXILIARY_CAPACITY ||
    priceSnapshots.length > ORDER_DETAIL_PRICE_SNAPSHOT_CAPACITY
  ) {
    return false;
  }

  if (
    !isCanonicalIsoTimestamp(value.startedAt) ||
    !isCanonicalIsoTimestamp(value.endedAt) ||
    !isCanonicalIsoTimestamp(value.billingPausedAt ?? null) ||
    !isCanonicalIsoTimestamp(value.voidedAt ?? null) ||
    (value.endedAt !== null && Date.parse(value.endedAt) < Date.parse(value.startedAt)) ||
    (value.voidedAt === null) !== (value.voidReason === null)
  ) {
    return false;
  }

  const orderMoney = [
    value.tableFeeFen,
    value.productTotalFen,
    value.packageTotalFen ?? 0,
    value.rentalTotalFen ?? 0,
    value.coachServiceTotalFen,
    value.totalFen,
    value.paidFen,
    value.refundedFen,
  ];
  if (!orderMoney.every(Number.isSafeInteger) || value.refundedFen > value.paidFen) return false;

  const priceSnapshotKeys = new Set<string>();
  for (const snapshot of priceSnapshots) {
    const key = `${snapshot.id}\u0000${snapshot.version}`;
    if (
      priceSnapshotKeys.has(key) ||
      !isCanonicalIsoTimestamp(snapshot.effectiveAt) ||
      ![
        snapshot.version,
        snapshot.hourlyRateFen,
        snapshot.roundingMinutes,
        snapshot.minimumMinutes,
        snapshot.minimumAmountFen ?? 0,
        snapshot.packageMinutes ?? 1,
        snapshot.packagePriceFen ?? 0,
      ].every(Number.isSafeInteger) ||
      !(snapshot.timeBands ?? []).every((band) =>
        [band.startMinute, band.endMinute, band.hourlyRateFen].every(Number.isSafeInteger),
      ) ||
      !priceSnapshotTimeBandsAreCoherent(snapshot)
    ) {
      return false;
    }
    priceSnapshotKeys.add(key);
  }
  if (!priceSnapshotKeys.has(`${value.priceSnapshot.id}\u0000${value.priceSnapshot.version}`)) {
    return false;
  }

  const itemIds = new Set<string>();
  const productAmounts: number[] = [];
  for (const item of value.items) {
    if (
      itemIds.has(item.id) ||
      ![item.unitPriceFen, item.quantity, item.returnedQuantity].every(Number.isSafeInteger) ||
      (item.productVersion !== undefined && !Number.isSafeInteger(item.productVersion)) ||
      (item.unitCostFen !== undefined && !Number.isSafeInteger(item.unitCostFen)) ||
      (item.inventoryCostSnapshot !== undefined &&
        !trackedSaleInventoryCostSnapshotMatchesItem(item.inventoryCostSnapshot, item)) ||
      item.returnedQuantity > item.quantity
    ) {
      return false;
    }
    const amount = (item.quantity - item.returnedQuantity) * item.unitPriceFen;
    if (!Number.isSafeInteger(amount)) return false;
    itemIds.add(item.id);
    productAmounts.push(amount);
  }
  if (safeIntegerSum(productAmounts) !== value.productTotalFen) return false;

  const packageAmounts = (value.packageItems ?? []).map((item) =>
    item.status === "ACTIVE" ? item.receivableFen : 0,
  );
  if (safeIntegerSum(packageAmounts) !== (value.packageTotalFen ?? 0)) return false;

  const rentalIds = new Set<string>();
  const rentalNumbers = new Set<string>();
  const rentalAmounts: number[] = [];
  for (const item of value.rentalItems ?? []) {
    if (
      !isCustomerRentalChargeItem(item) ||
      item.orderId !== value.id ||
      item.sessionId !== value.sessionId ||
      rentalIds.has(item.id) ||
      rentalNumbers.has(item.rentalNumber)
    ) {
      return false;
    }
    rentalIds.add(item.id);
    rentalNumbers.add(item.rentalNumber);
    rentalAmounts.push(item.receivableFen);
  }
  if (safeIntegerSum(rentalAmounts) !== (value.rentalTotalFen ?? 0)) return false;

  const coachLineIds = new Set<string>();
  const coachServiceAmounts: number[] = [];
  for (const line of value.coachServiceItems) {
    if (
      coachLineIds.has(line.id) ||
      !Number.isSafeInteger(line.billedMinutes) ||
      !Number.isSafeInteger(line.amountFen)
    ) {
      return false;
    }
    coachLineIds.add(line.id);
    coachServiceAmounts.push(line.amountFen);
  }
  if (safeIntegerSum(coachServiceAmounts) !== value.coachServiceTotalFen) return false;

  const fulfillmentIds = new Set<string>();
  const supplementAmounts: number[] = [];
  for (const fulfillment of groupBuyFulfillments) {
    if (
      fulfillmentIds.has(fulfillment.id) ||
      fulfillment.orderId !== value.id ||
      fulfillment.sessionId !== value.sessionId ||
      fulfillment.tableId !== value.tableId ||
      fulfillment.tableNumber !== value.tableNumber ||
      fulfillment.businessDate !== value.businessDate ||
      ![
        fulfillment.channelSnapshot.version,
        fulfillment.packageSnapshot.version,
        fulfillment.packageSnapshot.includedMinutes,
        fulfillment.voucherCoveredFen,
        fulfillment.customerSupplementFen,
        fulfillment.expectedSettlementFen,
        fulfillment.expectedFeeFen,
        fulfillment.expectedSubsidyFen,
        fulfillment.actualSettlementFen ?? 0,
        fulfillment.actualFeeFen ?? 0,
        fulfillment.actualSubsidyFen ?? 0,
      ].every(Number.isSafeInteger) ||
      !isCanonicalIsoTimestamp(fulfillment.coverageStartedAt) ||
      !isCanonicalIsoTimestamp(fulfillment.coverageEndsAt) ||
      !isCanonicalIsoTimestamp(fulfillment.fulfilledAt) ||
      !isCanonicalIsoTimestamp(fulfillment.reconciledAt) ||
      Date.parse(fulfillment.coverageEndsAt) < Date.parse(fulfillment.coverageStartedAt)
    ) {
      return false;
    }
    fulfillmentIds.add(fulfillment.id);
    supplementAmounts.push(fulfillment.customerSupplementFen);
  }

  const supplementTotal = safeIntegerSum(supplementAmounts);
  const expectedTotal = safeIntegerSum([
    value.tableFeeFen,
    value.productTotalFen,
    value.packageTotalFen ?? 0,
    value.rentalTotalFen ?? 0,
    value.coachServiceTotalFen,
    supplementTotal ?? Number.NaN,
  ]);
  if (supplementTotal === null || expectedTotal === null || expectedTotal !== value.totalFen) {
    return false;
  }

  const paymentIds = new Set<string>();
  const paymentAmounts: number[] = [];
  for (const payment of value.payments) {
    if (
      paymentIds.has(payment.id) ||
      !Number.isSafeInteger(payment.amountFen) ||
      !isCanonicalIsoTimestamp(payment.recordedAt)
    ) {
      return false;
    }
    paymentIds.add(payment.id);
    paymentAmounts.push(payment.amountFen);
  }
  if (safeIntegerSum(paymentAmounts) !== value.paidFen) return false;

  const refundIds = new Set<string>();
  const refundAmounts: number[] = [];
  const refundedByPayment = new Map<string, number>();
  for (const refund of value.refunds) {
    const refunded = (refundedByPayment.get(refund.paymentId) ?? 0) + refund.amountFen;
    if (
      refundIds.has(refund.id) ||
      !paymentIds.has(refund.paymentId) ||
      !Number.isSafeInteger(refund.amountFen) ||
      !Number.isSafeInteger(refunded) ||
      !isCanonicalIsoTimestamp(refund.createdAt)
    ) {
      return false;
    }
    refundIds.add(refund.id);
    refundedByPayment.set(refund.paymentId, refunded);
    refundAmounts.push(refund.amountFen);
  }
  if (safeIntegerSum(refundAmounts) !== value.refundedFen) return false;
  for (const payment of value.payments) {
    if ((refundedByPayment.get(payment.id) ?? 0) > payment.amountFen) return false;
  }

  if (value.billingBreakdown) {
    const breakdown = value.billingBreakdown;
    if (
      breakdown.segments.length > ORDER_DETAIL_LINE_CAPACITY ||
      !isCanonicalIsoTimestamp(breakdown.calculatedAt) ||
      ![
        breakdown.priceSnapshotVersion,
        breakdown.elapsedSeconds,
        breakdown.pausedSeconds,
        breakdown.billableSeconds,
        breakdown.billedMinutes,
        breakdown.subtotalFen,
        breakdown.minimumAmountAdjustmentFen,
        breakdown.totalFen,
      ].every(Number.isSafeInteger) ||
      safeIntegerSum([breakdown.subtotalFen, breakdown.minimumAmountAdjustmentFen]) !==
        breakdown.totalFen ||
      breakdown.totalFen !== value.tableFeeFen
    ) {
      return false;
    }
    const segmentIds = new Set<string>();
    const segmentAmounts: number[] = [];
    for (const segment of breakdown.segments) {
      if (
        segmentIds.has(segment.id) ||
        !isCanonicalIsoTimestamp(segment.startedAt) ||
        !isCanonicalIsoTimestamp(segment.endedAt) ||
        Date.parse(segment.endedAt) < Date.parse(segment.startedAt) ||
        ![
          segment.priceSnapshotVersion ?? 1,
          segment.elapsedSeconds,
          segment.billedMinutes,
          segment.hourlyRateFen,
          segment.roundingMinutes,
          segment.amountFen,
        ].every(Number.isSafeInteger)
      ) {
        return false;
      }
      segmentIds.add(segment.id);
      segmentAmounts.push(segment.amountFen);
    }
    if (safeIntegerSum(segmentAmounts) !== breakdown.subtotalFen) return false;
  }

  return true;
}

export function isManualReviewItem(value: unknown): value is ManualReviewItem {
  if (!Value.Check(ManualReviewItemSchema, value)) return false;
  if (value.type === "MEMBER_RECHARGE") {
    const snapshot = value.memberRecharge;
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value.id) ||
      value.orderId !== null ||
      value.offlineOrigin ||
      value.businessContext !== undefined ||
      value.resolutionDisposition !== undefined ||
      snapshot === undefined ||
      value.amountFen !== snapshot.paidAmountFen
    ) {
      return false;
    }
    const hasReconciliation = snapshot.reconciledAt !== null;
    if (snapshot.rechargeStatus === "PENDING_RECONCILIATION") {
      return (
        !hasReconciliation &&
        snapshot.latestReconciliationNote === null &&
        value.status === "PENDING" &&
        value.resolvedAt === null
      );
    }
    if (snapshot.rechargeStatus === "RECONCILIATION_EXCEPTION") {
      return (
        hasReconciliation &&
        snapshot.latestReconciliationNote !== null &&
        value.status === "PENDING" &&
        value.resolvedAt === null
      );
    }
    return (
      hasReconciliation &&
      snapshot.latestReconciliationNote !== null &&
      value.status === "RESOLVED" &&
      value.resolvedAt === snapshot.reconciledAt
    );
  }
  if (value.type === "MEMBER_RECEIVABLE") {
    const snapshot = value.memberReceivable;
    if (
      value.orderId === null ||
      !value.offlineOrigin ||
      value.businessContext !== undefined ||
      value.resolutionDisposition !== undefined ||
      value.memberRecharge !== undefined ||
      snapshot === undefined ||
      value.amountFen !== snapshot.amountFen ||
      !isMemberReceivableWorkItemSnapshot(snapshot)
    ) {
      return false;
    }
    return snapshot.debitStatus === "MEMBER_RECEIVABLE"
      ? value.status === "PENDING" && value.resolvedAt === null
      : value.status === "RESOLVED" && value.resolvedAt === snapshot.resolvedAt;
  }
  if (value.type === "CUSTOMER_STORAGE") {
    const snapshot = value.customerStorage;
    return Boolean(
      value.orderId !== null &&
        value.amountFen === null &&
        value.businessContext === undefined &&
        value.resolutionDisposition === undefined &&
        value.memberRecharge === undefined &&
        value.memberReceivable === undefined &&
        snapshot &&
        value.objectId === snapshot.storageNumber &&
        (value.status === "PENDING"
          ? value.resolvedAt === null
          : value.status === "RESOLVED" && value.resolvedAt !== null),
    );
  }
  return (
    value.orderId !== null &&
    value.memberRecharge === undefined &&
    value.memberReceivable === undefined &&
    value.customerStorage === undefined
  );
}

export const MANUAL_REVIEW_READ_CAPACITY = 50;

const ManualReviewReadCommonFields = {
  itemKey: Type.String({
    pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
  }),
  title: Type.String({ minLength: 1, maxLength: 80 }),
  summary: Type.String({ minLength: 1, maxLength: 300 }),
  nextStep: Type.String({ minLength: 1, maxLength: 300 }),
  createdAt: Type.String({ format: "date-time" }),
  tableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
  businessDate: Type.Union([Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }), Type.Null()]),
  occurredAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
};

const ManualReviewReadAmountFields = {
  amountFen: Type.Union([
    Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    Type.Null(),
  ]),
  offlineOrigin: Type.Boolean(),
};

export const ManualReviewReadItemSchema = Type.Union([
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      ...ManualReviewReadAmountFields,
      category: Type.Literal("PAYMENT_RECONCILIATION"),
      workItemId: Type.String({
        minLength: 1,
        maxLength: 128,
        pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
      }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      ...ManualReviewReadAmountFields,
      category: Type.Literal("ORDER_SYNC_REVIEW"),
      workItemId: Type.String({
        minLength: 1,
        maxLength: 128,
        pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
      }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      ...ManualReviewReadAmountFields,
      category: Type.Literal("GROUP_BUY_REDEMPTION_REVIEW"),
      reviewKind: Type.Union([
        Type.Literal("DAILY_RECONCILIATION_DIFFERENCE"),
        Type.Literal("OFFLINE_PRECHECK_REJECTED"),
        Type.Literal("REDEMPTION_COMPENSATION_UNKNOWN"),
      ]),
      workItemId: GroupBuyReconciliationInternalReferenceIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      category: Type.Literal("COACH_SERVICE_REVIEW"),
      workItemId: Type.String({
        minLength: 1,
        maxLength: 128,
        pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
      }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      ...ManualReviewReadAmountFields,
      category: Type.Literal("MEMBER_RECHARGE_RECONCILIATION"),
      workItemId: Type.String({
        pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
      }),
      memberName: Type.String({ minLength: 1, maxLength: 80 }),
      method: Type.Union([Type.Literal("STATIC_WECHAT"), Type.Literal("STATIC_ALIPAY")]),
      rechargeStatus: Type.Union([
        Type.Literal("PENDING_RECONCILIATION"),
        Type.Literal("RECONCILIATION_EXCEPTION"),
      ]),
      originalRequestNote: Type.String({ maxLength: 500 }),
      latestReconciliationNote: Type.Union([Type.String({ maxLength: 500 }), Type.Null()]),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      ...ManualReviewReadAmountFields,
      category: Type.Literal("MEMBER_RECEIVABLE"),
      workItemId: Type.String({ minLength: 1, maxLength: 128 }),
      receivableNumber: Type.String({ minLength: 1, maxLength: 64 }),
      orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
      memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
      memberName: Type.String({ minLength: 1, maxLength: 80 }),
      debitStatus: Type.Literal("MEMBER_RECEIVABLE"),
      reason: Type.String({ minLength: 1, maxLength: 300 }),
      lastAttemptAt: Type.String({ format: "date-time" }),
      attemptCount: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      category: Type.Literal("CUSTOMER_STORAGE_EXCEPTION"),
      workItemId: Type.String({ minLength: 1, maxLength: 128 }),
      storageNumber: Type.String({ pattern: "^JC-[0-9]{8}-[0-9A-F]{12}$" }),
      memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
      memberName: Type.String({ minLength: 1, maxLength: 80 }),
      productName: Type.String({ minLength: 1, maxLength: 120 }),
      remainingQuantity: Type.Integer({ minimum: 1, maximum: 999_999 }),
      unit: Type.String({ minLength: 1, maxLength: 16 }),
      exceptionType: Type.Union([
        Type.Literal("LOST"),
        Type.Literal("DAMAGED"),
        Type.Literal("DISPOSAL"),
      ]),
      storageVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      expiresAt: Type.String({ format: "date-time" }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ManualReviewReadCommonFields,
      category: Type.Literal("CUSTOMER_RENTAL_EXCEPTION"),
      workItemId: Type.String({ minLength: 1, maxLength: 128 }),
      rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
      orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
      tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
      productName: Type.String({ minLength: 1, maxLength: 120 }),
      remainingQuantity: Type.Integer({ minimum: 1, maximum: 999_999 }),
      unit: Type.String({ minLength: 1, maxLength: 16 }),
      exceptionType: Type.Union([
        Type.Literal("LOST"),
        Type.Literal("DAMAGED"),
        Type.Literal("OVERDUE"),
      ]),
      rentalVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      expectedReturnAt: Type.String({ format: "date-time" }),
    },
    { additionalProperties: false },
  ),
]);
export type ManualReviewReadItem = Static<typeof ManualReviewReadItemSchema>;

export const MemberRechargeManualReviewResultSchema = Type.Object(
  {
    workItemId: Type.String({
      pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
    }),
    outcome: Type.Union([
      Type.Literal("CONFIRMED"),
      Type.Literal("NOT_RECEIVED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_PAYMENT"),
    ]),
    workItemStatus: Type.Union([Type.Literal("PENDING"), Type.Literal("RESOLVED")]),
    rechargeStatus: Type.Union([
      Type.Literal("CONFIRMED_RECONCILED"),
      Type.Literal("RECONCILIATION_EXCEPTION"),
    ]),
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    paidAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    method: Type.Union([Type.Literal("STATIC_WECHAT"), Type.Literal("STATIC_ALIPAY")]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    reconciledAt: Type.String({ format: "date-time" }),
    balanceEffect: Type.Union([Type.Literal("CREDITED"), Type.Literal("UNCHANGED_EXCEPTION")]),
  },
  { additionalProperties: false },
);
export type MemberRechargeManualReviewResult = Static<
  typeof MemberRechargeManualReviewResultSchema
>;

export function isMemberRechargeManualReviewResult(
  value: unknown,
): value is MemberRechargeManualReviewResult {
  if (!Value.Check(MemberRechargeManualReviewResultSchema, value)) return false;
  return value.outcome === "CONFIRMED"
    ? value.workItemStatus === "RESOLVED" &&
        value.rechargeStatus === "CONFIRMED_RECONCILED" &&
        value.balanceEffect === "CREDITED"
    : value.workItemStatus === "PENDING" &&
        value.rechargeStatus === "RECONCILIATION_EXCEPTION" &&
        value.balanceEffect === "UNCHANGED_EXCEPTION";
}

export const ManualReviewListFilterSchema = Type.Object(
  {
    category: Type.Literal("MEMBER_RECEIVABLE"),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
  },
  { additionalProperties: false },
);
export type ManualReviewListFilter = Static<typeof ManualReviewListFilterSchema>;

export const ManualReviewListReadModelSchema = Type.Object(
  {
    items: Type.Array(ManualReviewReadItemSchema, { maxItems: MANUAL_REVIEW_READ_CAPACITY }),
    pendingCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    capacity: Type.Literal(MANUAL_REVIEW_READ_CAPACITY),
    generatedAt: Type.String({ format: "date-time" }),
    filter: Type.Optional(ManualReviewListFilterSchema),
    memberRechargeCoverage: Type.Optional(
      Type.Union([Type.Literal("CURRENT"), Type.Literal("LOCAL_ONLY")]),
    ),
  },
  { additionalProperties: false },
);
export type ManualReviewListReadModel = Static<typeof ManualReviewListReadModelSchema>;

export const ManualReviewSummaryReadModelSchema = Type.Object(
  {
    pendingCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    capacity: Type.Literal(MANUAL_REVIEW_READ_CAPACITY),
    capacityExceeded: Type.Boolean(),
    generatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type ManualReviewSummaryReadModel = Static<typeof ManualReviewSummaryReadModelSchema>;

export function isManualReviewListReadModel(
  value: unknown,
  expectedFilter?: ManualReviewListFilter | null,
): value is ManualReviewListReadModel {
  if (!Value.Check(ManualReviewListReadModelSchema, value)) return false;
  if (
    value.filter !== undefined &&
    (!isCanonicalCalendarDate(value.filter.businessDate) ||
      value.memberRechargeCoverage !== undefined ||
      value.items.some(
        (item) =>
          item.category !== value.filter?.category ||
          item.businessDate !== value.filter.businessDate,
      ))
  ) {
    return false;
  }
  if (
    expectedFilter === null
      ? value.filter !== undefined
      : expectedFilter !== undefined &&
        (value.filter?.category !== expectedFilter.category ||
          value.filter.businessDate !== expectedFilter.businessDate)
  ) {
    return false;
  }
  return (
    value.pendingCount === value.items.length &&
    value.pendingCount <= value.capacity &&
    new Set(value.items.map((item) => item.itemKey)).size === value.items.length
  );
}

export function isManualReviewSummaryReadModel(
  value: unknown,
): value is ManualReviewSummaryReadModel {
  return (
    Value.Check(ManualReviewSummaryReadModelSchema, value) &&
    value.capacityExceeded === value.pendingCount > value.capacity
  );
}

const BusinessEventEnvelopeFields = {
  contractVersion: Type.Literal(EDGE_CONTRACT_VERSION),
  eventId: Type.String({ minLength: 8 }),
  tenantId: Type.String({ minLength: 1 }),
  storeId: Type.String({ minLength: 1 }),
  gatewayId: Type.String({ minLength: 1 }),
  fencingToken: Type.Integer({ minimum: 1 }),
  gatewaySequence: Type.Integer({ minimum: 1 }),
  businessId: Type.String({ minLength: 1 }),
  idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  aggregateId: Type.String({ minLength: 1 }),
  aggregateVersion: Type.Integer({ minimum: 1, maximum: 2_147_483_647 }),
  occurredAt: Type.String({ format: "date-time" }),
  offlineOrigin: Type.Boolean(),
  actorId: Type.String({ minLength: 1 }),
  authorizerId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
};

const BusinessProjectionPayloadFields = {
  order: OrderDetailSchema,
  tableSnapshot: TableSnapshotSchema,
  tableSnapshots: Type.Array(TableSnapshotSchema, { minItems: 1 }),
  sessionVoidEvidence: Type.Optional(
    Type.Object(
      {
        previousOrderStatus: Type.Literal("OPEN"),
        previousAmountFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        reason: Type.String({ minLength: 2, maxLength: 200 }),
      },
      { additionalProperties: false },
    ),
  ),
};

const ProjectionPayloadSchema = Type.Object(BusinessProjectionPayloadFields, {
  additionalProperties: false,
});

export const BusinessEventSchema = Type.Union([
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("WORKFORCE_PAYROLL_UPDATED"),
      payload: WorkforcePayrollBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("GROUP_BUY_FULFILLED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          fulfillment: GroupBuyFulfillmentRecordSchema,
          inventoryCostSnapshots: Type.Optional(
            Type.Array(GroupBuyInventoryCostSnapshotSchema, { maxItems: 30 }),
          ),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("GROUP_BUY_RECONCILED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          fulfillment: GroupBuyFulfillmentRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("GROUP_BUY_RECONCILIATION_SIGNED_OFF"),
      payload: Type.Object(
        {
          signoff: GroupBuyReconciliationSignoffPackageSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_OPENED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_TRANSFERRED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_START_ADJUSTED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          adjustment: Type.Optional(SessionStartAdjustmentEventEvidenceSchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Union([
        Type.Literal("SESSION_PAUSED"),
        Type.Literal("SESSION_RESUMED"),
        Type.Literal("SESSION_ENDED_EARLY"),
        Type.Literal("SESSION_LONG_RUNNING_RESOLVED"),
        Type.Literal("SESSION_VOIDED"),
      ]),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("KIOSK_PRODUCT_RESERVATION_CHANGED"),
      payload: KioskProductReservationBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("INVENTORY_COUNT_APPLIED"),
      payload: InventoryCountBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("INVENTORY_LOSS_APPLIED"),
      payload: InventoryLossBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("ORDER_ITEM_ADDED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          sourceReservation: Type.Optional(KioskProductReservationReadModelSchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("ORDER_ITEM_RETURNED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          reason: Type.String({ minLength: 2, maxLength: 200 }),
          quantity: Type.Integer({ minimum: 1, maximum: 99 }),
          saleReturn: Type.Optional(OrderItemSaleReturnRecordSchema),
          paidSaleReturn: Type.Optional(PaidOrderItemSaleReturnRecordSchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("STORE_PACKAGE_ORDER_ITEM_ADDED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          packageItem: StorePackageOrderItemSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("STORE_PACKAGE_ORDER_ITEM_RETURNED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          packageItem: StorePackageOrderItemSchema,
          packageReturn: StorePackageOrderItemReturnRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_STORAGE_CREATED"),
      payload: Type.Object(
        { storage: CustomerStorageRecordSchema },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_STORAGE_PICKED_UP"),
      payload: Type.Object(
        { storage: CustomerStorageRecordSchema },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_STORAGE_EXCEPTION_REPORTED"),
      payload: Type.Object(
        { storage: CustomerStorageRecordSchema, manualReview: ManualReviewItemSchema },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_STORAGE_EXCEPTION_RESOLVED"),
      payload: Type.Object(
        { storage: CustomerStorageRecordSchema },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_RENTAL_CREATED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          rental: CustomerRentalRecordSchema,
          rentalChargeItem: CustomerRentalChargeItemSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_RENTAL_RETURNED"),
      payload: Type.Object({ rental: CustomerRentalRecordSchema }, { additionalProperties: false }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_RENTAL_EXCEPTION_REPORTED"),
      payload: Type.Object(
        { rental: CustomerRentalRecordSchema, manualReview: ManualReviewItemSchema },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CUSTOMER_RENTAL_EXCEPTION_RESOLVED"),
      payload: Type.Object({ rental: CustomerRentalRecordSchema }, { additionalProperties: false }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CHECKOUT_SPLIT_PLAN_CHANGED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          splitPlan: CheckoutSplitPlanReadModelSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("CHECKOUT_SPLIT_SHARE_PAID"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          splitPlan: CheckoutSplitPlanReadModelSchema,
          manualReview: Type.Union([ManualReviewItemSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SESSION_CHECKED_OUT"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          manualReview: Type.Union([ManualReviewItemSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COMBINED_CHECKOUT_COMPLETED"),
      payload: Type.Object(
        {
          combinedCheckout: CombinedCheckoutReadModelSchema,
          orders: Type.Array(OrderDetailSchema, { minItems: 2, maxItems: 4 }),
          tableSnapshots: Type.Array(TableSnapshotSchema, { minItems: 2, maxItems: 4 }),
          manualReview: Type.Union([ManualReviewItemSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COMBINED_CHECKOUT_RECONCILED"),
      payload: Type.Object(
        {
          combinedCheckout: CombinedCheckoutReadModelSchema,
          orders: Type.Array(OrderDetailSchema, { minItems: 2, maxItems: 4 }),
          tableSnapshots: Type.Array(TableSnapshotSchema, { minItems: 2, maxItems: 4 }),
          manualReview: ManualReviewItemSchema,
          outcome: Type.Union([
            Type.Literal("MANUALLY_RECONCILED"),
            Type.Literal("NOT_RECEIVED"),
            Type.Literal("AMOUNT_MISMATCH"),
            Type.Literal("DUPLICATE_PAYMENT"),
          ]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Union([Type.Literal("TABLE_CLEANING_CONFIRMED"), Type.Literal("TABLE_RELEASED")]),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PURCHASE_RECEIPT_RECORDED"),
      payload: PurchaseReceiptBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PURCHASE_ORDER_SHORT_CLOSED"),
      payload: PurchaseOrderShortClosedBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      // Historical replay only; new closeout writers use the two events above.
      type: Type.Literal("TABLE_CLEARED"),
      payload: ProjectionPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PAYMENT_REFUNDED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          refund: RefundRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PAYMENT_RECONCILED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          manualReview: ManualReviewItemSchema,
          outcome: Type.Union([
            Type.Literal("MANUALLY_RECONCILED"),
            Type.Literal("NOT_RECEIVED"),
            Type.Literal("AMOUNT_MISMATCH"),
            Type.Literal("DUPLICATE_PAYMENT"),
          ]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("MEMBER_DEBIT_RECONCILED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          memberDebit: MemberDebitStateSchema,
          manualReview: Type.Union([ManualReviewItemSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PURCHASE_RETURN_RECORDED"),
      payload: PurchaseReturnBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("PURCHASE_PAYABLE_RECONCILED"),
      payload: PurchasePayableReconciliationBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SUPPLIER_PAYMENT_RECORDED"),
      payload: SupplierPaymentBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SUPPLIER_BATCH_PAYMENT_RECORDED"),
      payload: SupplierBatchPaymentBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SUPPLIER_CREDIT_RECORDED"),
      payload: SupplierCreditBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SUPPLIER_REFUND_RECORDED"),
      payload: SupplierRefundBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("SUPPLIER_PAYMENT_REVERSED"),
      payload: SupplierPaymentReversalBusinessEventPayloadSchema,
    },
    { additionalProperties: false },
  ),
]);
export type BusinessEvent = Static<typeof BusinessEventSchema>;

export const CoachBusinessEventSchema = Type.Union([
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SHIFT_CHANGED"),
      payload: Type.Object(
        {
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_QUEUE_CHANGED"),
      payload: Type.Object(
        {
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SERVICE_CHANGED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
          coachService: CoachServiceRecordSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SERVICE_ADJUSTED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
          coachService: CoachServiceRecordSchema,
          refund: Type.Union([RefundRecordSchema, Type.Null()]),
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SERVICE_CORRECTED"),
      payload: Type.Object(
        {
          ...BusinessProjectionPayloadFields,
          coachShift: CoachShiftSchema,
          coachQueue: Type.Array(CoachQueueEntrySchema),
          correction: CoachServiceCorrectionResultSchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_SERVICE_CORRECTION_NOTICE_CHANGED"),
      payload: Type.Object(
        { notice: CoachServiceCorrectionNoticeSchema },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...BusinessEventEnvelopeFields,
      type: Type.Literal("COACH_COMMISSION_MONTH_SETTLED"),
      payload: Type.Object(
        {
          monthlyCommission: CoachMonthlyCommissionSummarySchema,
        },
        { additionalProperties: false },
      ),
    },
    { additionalProperties: false },
  ),
]);
export type CoachBusinessEvent = Static<typeof CoachBusinessEventSchema>;

export function isCoachBusinessEvent(value: unknown): value is CoachBusinessEvent {
  return Value.Check(CoachBusinessEventSchema, value);
}

export const CustomerReservationBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("CUSTOMER_RESERVATION_CHANGED"),
    payload: CustomerReservationBusinessEventPayloadSchema,
  },
  { additionalProperties: false },
);
export type CustomerReservationBusinessEvent = Static<
  typeof CustomerReservationBusinessEventSchema
>;

export function isCustomerReservationBusinessEvent(
  value: unknown,
): value is CustomerReservationBusinessEvent {
  return Value.Check(CustomerReservationBusinessEventSchema, value);
}

export const AnnouncementBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("ANNOUNCEMENT_CHANGED"),
    payload: AnnouncementBusinessEventPayloadSchema,
  },
  { additionalProperties: false },
);
export type AnnouncementBusinessEvent = Static<typeof AnnouncementBusinessEventSchema>;

export function isAnnouncementBusinessEvent(value: unknown): value is AnnouncementBusinessEvent {
  return (
    Value.Check(AnnouncementBusinessEventSchema, value) &&
    isAnnouncementBusinessEventPayload(value.payload)
  );
}

export const WorkforceAttendanceBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("WORKFORCE_ATTENDANCE_UPDATED"),
    payload: WorkforceAttendanceBusinessEventPayloadSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceBusinessEvent = Static<
  typeof WorkforceAttendanceBusinessEventSchema
>;

export function isWorkforceAttendanceBusinessEvent(
  value: unknown,
): value is WorkforceAttendanceBusinessEvent {
  if (!Value.Check(WorkforceAttendanceBusinessEventSchema, value)) return false;
  const event = value as WorkforceAttendanceBusinessEvent;
  const item = event.payload.item;
  if (
    !isWorkforceAttendanceMutationResult(event.payload) ||
    event.aggregateId !== item.schedule.scheduleId ||
    event.aggregateVersion !== item.revision ||
    event.businessId !== item.schedule.scheduleNumber ||
    event.tenantId !== item.schedule.tenantId ||
    event.storeId !== item.schedule.storeId ||
    event.actorId !== event.payload.actorAccountId ||
    event.authorizerId !== null
  ) {
    return false;
  }
  const occurredAt =
    event.payload.operation === "SCHEDULE_CREATED"
      ? item.schedule.createdAt
      : event.payload.operation === "CHECKED_IN"
        ? item.attendance?.checkedInAt
        : event.payload.operation === "CHECKED_OUT"
          ? item.attendance?.checkedOutAt
          : event.payload.operation === "CORRECTION_REQUESTED"
            ? item.latestCorrection?.requestedAt
            : event.payload.operation === "CORRECTION_ACKNOWLEDGED"
              ? item.latestCorrection?.acknowledgedAt
              : event.payload.operation === "CORRECTION_APPROVED" ||
                  event.payload.operation === "CORRECTION_REJECTED"
                ? item.latestCorrection?.decidedAt
                : event.payload.operation === "LEAVE_REQUESTED"
                  ? item.latestLeaveRequest?.requestedAt
                  : event.payload.operation === "LEAVE_ACKNOWLEDGED"
                    ? item.latestLeaveRequest?.acknowledgedAt
                    : item.latestLeaveRequest?.decidedAt;
  return occurredAt !== null && occurredAt !== undefined && event.occurredAt === occurredAt;
}

export const StoreOperatingDayOpenedBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("STORE_OPERATING_DAY_OPENED"),
    payload: Type.Object(
      { operatingDay: StoreOperatingDayRecordSchema },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export const StoreOperatingDayClosingStartedBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("STORE_OPERATING_DAY_CLOSING_STARTED"),
    payload: Type.Object(
      { operatingDay: StoreOperatingDayRecordSchema },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export const StoreOperatingDayLegacyAcknowledgedBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("STORE_OPERATING_DAY_LEGACY_ACKNOWLEDGED"),
    payload: Type.Object(
      { operatingDay: StoreOperatingDayRecordSchema },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export const StoreOperatingDayClosedBusinessEventSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: Type.Literal("STORE_OPERATING_DAY_CLOSED"),
    payload: Type.Object(
      { operatingDay: StoreOperatingDayRecordSchema },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export const StoreOperatingDayBusinessEventSchema = Type.Union([
  StoreOperatingDayOpenedBusinessEventSchema,
  StoreOperatingDayClosingStartedBusinessEventSchema,
  StoreOperatingDayLegacyAcknowledgedBusinessEventSchema,
  StoreOperatingDayClosedBusinessEventSchema,
]);
export type StoreOperatingDayBusinessEvent = Static<typeof StoreOperatingDayBusinessEventSchema>;

export function isStoreOperatingDayBusinessEvent(
  value: unknown,
): value is StoreOperatingDayBusinessEvent {
  if (!Value.Check(StoreOperatingDayBusinessEventSchema, value)) return false;
  const event = value as StoreOperatingDayBusinessEvent;
  const operatingDay = event.payload.operatingDay;
  if (
    !isStoreOperatingDayRecord(operatingDay) ||
    event.aggregateId !== operatingDay.id ||
    event.aggregateVersion !== operatingDay.version ||
    event.businessId !== operatingDay.id ||
    event.tenantId !== operatingDay.tenantId ||
    event.storeId !== operatingDay.storeId
  ) {
    return false;
  }
  if (event.type === "STORE_OPERATING_DAY_OPENED") {
    return (
      operatingDay.status === "OPEN" &&
      event.idempotencyKey === operatingDay.openingRequestId &&
      event.actorId === operatingDay.openedBy &&
      event.occurredAt === operatingDay.openedAt
    );
  }
  const closing = operatingDay.closing;
  if (!closing) return false;
  if (event.type === "STORE_OPERATING_DAY_CLOSING_STARTED") {
    return (
      operatingDay.status === "CLOSING" &&
      operatingDay.version === 2 &&
      event.idempotencyKey === closing.requestId &&
      event.actorId === closing.startedBy &&
      event.occurredAt === closing.startedAt
    );
  }
  if (event.type === "STORE_OPERATING_DAY_LEGACY_ACKNOWLEDGED") {
    const acknowledgement = closing.legacyAcknowledgement;
    return (
      operatingDay.status === "CLOSING" &&
      acknowledgement !== null &&
      event.idempotencyKey === acknowledgement.requestId &&
      event.actorId === acknowledgement.acknowledgedBy &&
      event.occurredAt === acknowledgement.acknowledgedAt
    );
  }
  const completion = closing.completion;
  return (
    operatingDay.status === "CLOSED" &&
    completion !== null &&
    event.idempotencyKey === completion.requestId &&
    event.actorId === completion.closedBy &&
    event.occurredAt === completion.closedAt
  );
}

export const BusinessSyncEventSchema: TUnion<
  [
    typeof BusinessEventSchema,
    typeof CoachBusinessEventSchema,
    typeof CustomerReservationBusinessEventSchema,
    typeof AnnouncementBusinessEventSchema,
    typeof WorkforceAttendanceBusinessEventSchema,
    typeof StoreOperatingDayBusinessEventSchema,
  ]
> = Type.Union([
  BusinessEventSchema,
  CoachBusinessEventSchema,
  CustomerReservationBusinessEventSchema,
  AnnouncementBusinessEventSchema,
  WorkforceAttendanceBusinessEventSchema,
  StoreOperatingDayBusinessEventSchema,
]);
export type BusinessSyncEvent = Static<typeof BusinessSyncEventSchema>;

// The transport validates the trusted envelope first; each payload is then validated and
// isolated per event so one damaged record cannot block later gateway sequences in the batch.
export const BusinessEventTransportSchema = Type.Object(
  {
    ...BusinessEventEnvelopeFields,
    type: BusinessEventTypeSchema,
    payload: Type.Record(Type.String(), Type.Unknown()),
  },
  { additionalProperties: false },
);

export const SyncBatchRequestSchema = Type.Object(
  {
    gatewayId: Type.String({ minLength: 1 }),
    events: Type.Array(BusinessEventTransportSchema, { maxItems: 100 }),
    operationTraces: Type.Optional(
      Type.Array(BusinessOperationTraceSidecarV1Schema, { maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type SyncBatchRequest = Static<typeof SyncBatchRequestSchema>;

export interface GatewayStatus {
  mode: ConnectivityMode;
  syncStage: SyncStage;
  gatewayId: string;
  fencingToken: number | null;
  leaseExpiresAt: string | null;
  offlineAuthorityAvailable: boolean;
  offlineSince: string | null;
  offlineDurationSeconds: number;
  lastSuccessfulSyncAt: string | null;
  pendingRecords: number;
  failedRecords: number;
  conflictRecords: number;
  pendingReconciliationCount: number;
  selectedConditionalModules: ConditionalModuleCode[];
  manualPaymentMethods: VenueOperatingSettingsInput["manualPaymentMethods"];
  simulationAllowed: boolean;
  serviceHealth: {
    cloudSync: boolean;
    onlineRefund: boolean;
    paymentReconciliation: boolean;
    channelPaymentConfirmation: boolean;
    vouchers: boolean;
    memberFunds: boolean;
  };
  syncProgress: { processed: number; total: number; remaining: number; failed: number };
  availableFeatures: Array<{ code: string; label: string }>;
  unavailableFeatures: Array<{ code: string; label: string; reason: string }>;
  lastRecoverySummary: {
    synced: number;
    failed: number;
    pendingReconciliation: number;
    conflicts: number;
    restoredFeatures: string[];
    completedAt: string;
  } | null;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

const ConnectivityFeatureSchema = Type.Object(
  {
    code: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Z][A-Z0-9_]*$" }),
    label: Type.String({ minLength: 1, maxLength: 120 }),
  },
  { additionalProperties: false },
);

export const ConnectivityReadModelSchema = Type.Object(
  {
    mode: ConnectivityModeSchema,
    syncStage: SyncStageSchema,
    offlineAuthorityAvailable: Type.Boolean(),
    offlineDurationSeconds: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    lastSuccessfulSyncAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    pendingRecords: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    pendingReconciliationCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    selectedConditionalModules: Type.Array(ConditionalModuleCodeSchema, {
      maxItems: 8,
      uniqueItems: true,
    }),
    manualPaymentMethods: Type.Object(
      {
        cash: Type.Boolean(),
        staticWechat: Type.Boolean(),
        staticAlipay: Type.Boolean(),
      },
      { additionalProperties: false },
    ),
    simulationAllowed: Type.Boolean(),
    serviceHealth: Type.Object(
      {
        onlineRefund: Type.Boolean(),
        paymentReconciliation: Type.Boolean(),
        vouchers: Type.Boolean(),
        memberFunds: Type.Boolean(),
      },
      { additionalProperties: false },
    ),
    syncProgress: Type.Object(
      {
        processed: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        total: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        remaining: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        failed: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      },
      { additionalProperties: false },
    ),
    availableFeatures: Type.Array(ConnectivityFeatureSchema, { maxItems: 100 }),
    unavailableFeatures: Type.Array(
      Type.Object(
        {
          ...ConnectivityFeatureSchema.properties,
          reason: Type.String({ minLength: 1, maxLength: 300 }),
        },
        { additionalProperties: false },
      ),
      { maxItems: 100 },
    ),
    lastRecoverySummary: Type.Union([
      Type.Object(
        {
          synced: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
          pendingReconciliation: Type.Integer({
            minimum: 0,
            maximum: Number.MAX_SAFE_INTEGER,
          }),
          conflicts: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
          restoredFeatures: Type.Array(Type.String({ minLength: 1, maxLength: 120 }), {
            maxItems: 100,
            uniqueItems: true,
          }),
        },
        { additionalProperties: false },
      ),
      Type.Null(),
    ]),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type ConnectivityReadModel = Static<typeof ConnectivityReadModelSchema>;

export function isConnectivityReadModel(value: unknown): value is ConnectivityReadModel {
  if (!Value.Check(ConnectivityReadModelSchema, value)) return false;
  if (
    value.selectedConditionalModules.includes("CUSTOMER_SELF_SERVICE_RESERVATION") &&
    !value.selectedConditionalModules.includes("RESERVATION")
  ) {
    return false;
  }
  if (
    value.selectedConditionalModules.includes("CUSTOMER_STORAGE") &&
    (!value.selectedConditionalModules.includes("MEMBER") ||
      !value.selectedConditionalModules.includes("PRODUCT_ORDER"))
  ) {
    return false;
  }
  if (value.lastSuccessfulSyncAt !== null) {
    const lastSuccessfulSyncAt = Date.parse(value.lastSuccessfulSyncAt);
    if (
      !Number.isFinite(lastSuccessfulSyncAt) ||
      new Date(lastSuccessfulSyncAt).toISOString() !== value.lastSuccessfulSyncAt
    ) {
      return false;
    }
  }
  const progress = value.syncProgress;
  if (
    progress.processed > progress.total ||
    progress.remaining !== progress.total - progress.processed ||
    progress.failed > progress.total
  ) {
    return false;
  }

  const availableCodes = value.availableFeatures.map((feature) => feature.code);
  const unavailableCodes = value.unavailableFeatures.map((feature) => feature.code);
  if (
    new Set(availableCodes).size !== availableCodes.length ||
    new Set(unavailableCodes).size !== unavailableCodes.length ||
    unavailableCodes.some((code) => availableCodes.includes(code))
  ) {
    return false;
  }

  const online = value.mode === "ONLINE";
  if (
    value.serviceHealth.onlineRefund !== online ||
    value.serviceHealth.paymentReconciliation !== online ||
    value.serviceHealth.vouchers !==
      (online && value.selectedConditionalModules.includes("GROUP_BUY")) ||
    value.serviceHealth.memberFunds !==
      (online && value.selectedConditionalModules.includes("MEMBER")) ||
    (value.mode === "AUTHORITY_EXPIRED") === value.offlineAuthorityAvailable ||
    (value.simulationAllowed && value.evidence !== "SIMULATED")
  ) {
    return false;
  }
  return true;
}

export interface SystemComponentStatus {
  component: "ops-web" | "cloud-api" | "venue-edge-gateway" | "table-edge-agent";
  productVersion: string;
  buildNumber: string;
  schemaVersion: number;
  contractVersion: string;
  health: "HEALTHY" | "DEGRADED" | "UNREACHABLE";
  evidence: EvidenceLabel;
}

export const SystemStatusReadModelSchema = Type.Object(
  {
    generatedAt: Type.String({ format: "date-time" }),
    overall: Type.Union([Type.Literal("HEALTHY"), Type.Literal("DEGRADED")]),
    components: Type.Array(
      Type.Object(
        {
          component: Type.Union([
            Type.Literal("ops-web"),
            Type.Literal("cloud-api"),
            Type.Literal("venue-edge-gateway"),
            Type.Literal("table-edge-agent"),
          ]),
          productVersion: Type.String({ minLength: 1 }),
          schemaVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
          health: Type.Union([
            Type.Literal("HEALTHY"),
            Type.Literal("DEGRADED"),
            Type.Literal("UNREACHABLE"),
          ]),
        },
        { additionalProperties: false },
      ),
      { minItems: 4, maxItems: 4 },
    ),
    gateway: Type.Object({ mode: ConnectivityModeSchema }, { additionalProperties: false }),
    storage: Type.Object(
      {
        orders: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        pendingOutbox: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        failedOutbox: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        pendingManualReview: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        integrity: Type.Union([Type.Literal("OK"), Type.Literal("FAILED")]),
      },
      { additionalProperties: false },
    ),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type SystemStatusReadModel = Static<typeof SystemStatusReadModelSchema>;

export function systemStatusOverall(input: {
  components: ReadonlyArray<{ health: "HEALTHY" | "DEGRADED" | "UNREACHABLE" }>;
  gateway: { mode: ConnectivityMode };
  storage: { integrity: "OK" | "FAILED" };
}): SystemStatusReadModel["overall"] {
  const degraded =
    input.components.some((component) => component.health !== "HEALTHY") ||
    input.gateway.mode !== "ONLINE" ||
    input.storage.integrity !== "OK";
  return degraded ? "DEGRADED" : "HEALTHY";
}

export function isSystemStatusReadModel(value: unknown): value is SystemStatusReadModel {
  if (!Value.Check(SystemStatusReadModelSchema, value)) return false;
  const status = value as SystemStatusReadModel;
  const expectedComponents = new Set<string>([
    "ops-web",
    "cloud-api",
    "venue-edge-gateway",
    "table-edge-agent",
  ]);
  const actualComponents = new Set<string>(
    status.components.map((component) => component.component),
  );
  if (
    actualComponents.size !== expectedComponents.size ||
    [...expectedComponents].some((component) => !actualComponents.has(component))
  ) {
    return false;
  }
  const generatedAt = Date.parse(status.generatedAt);
  if (!Number.isFinite(generatedAt) || new Date(generatedAt).toISOString() !== status.generatedAt) {
    return false;
  }
  return status.overall === systemStatusOverall(status);
}

export const DIAGNOSTIC_EXCLUDED_ENTRIES = [
  "authorization headers",
  "gateway credentials",
  "secondary authorization tokens and PINs",
  "employee login identifiers",
  "raw tenant/store/gateway identifiers",
  "order payloads and payment notes",
] as const;

export const DiagnosticBundleReadModelSchema = Type.Object(
  {
    format: Type.Literal("billiard-erp-diagnostic-v2"),
    generatedAt: Type.String({ format: "date-time" }),
    redaction: Type.Object(
      {
        policy: Type.Literal("STRICT"),
        containsSecrets: Type.Literal(false),
        identifiers: Type.Literal("SHA256_PREFIX"),
      },
      { additionalProperties: false },
    ),
    scope: Type.Object(
      {
        tenantHash: Type.String({ pattern: "^[a-f0-9]{12}$" }),
        storeHash: Type.String({ pattern: "^[a-f0-9]{12}$" }),
        gatewayHash: Type.String({ pattern: "^[a-f0-9]{12}$" }),
      },
      { additionalProperties: false },
    ),
    system: SystemStatusReadModelSchema,
    recentFailures: Type.Array(
      Type.Object(
        {
          category: Type.String({
            minLength: 1,
            maxLength: 128,
            pattern: "^[A-Z][A-Z0-9_]*$",
          }),
          count: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          lastOccurredAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
        },
        { additionalProperties: false },
      ),
      { maxItems: 100 },
    ),
    excluded: Type.Array(
      Type.Union([
        Type.Literal("authorization headers"),
        Type.Literal("gateway credentials"),
        Type.Literal("secondary authorization tokens and PINs"),
        Type.Literal("employee login identifiers"),
        Type.Literal("raw tenant/store/gateway identifiers"),
        Type.Literal("order payloads and payment notes"),
      ]),
      { minItems: 6, maxItems: 6 },
    ),
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: false },
);
export type DiagnosticBundleReadModel = Static<typeof DiagnosticBundleReadModelSchema>;

function isCanonicalIsoDateTime(value: string): boolean {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

export function isDiagnosticBundleReadModel(value: unknown): value is DiagnosticBundleReadModel {
  if (!Value.Check(DiagnosticBundleReadModelSchema, value)) return false;
  const bundle = value as DiagnosticBundleReadModel;
  if (
    !isCanonicalIsoDateTime(bundle.generatedAt) ||
    !isSystemStatusReadModel(bundle.system) ||
    bundle.system.evidence !== bundle.evidence
  ) {
    return false;
  }
  if (
    bundle.excluded.length !== DIAGNOSTIC_EXCLUDED_ENTRIES.length ||
    bundle.excluded.some((entry, index) => entry !== DIAGNOSTIC_EXCLUDED_ENTRIES[index])
  ) {
    return false;
  }
  const categories = new Set<string>();
  for (const failure of bundle.recentFailures) {
    if (categories.has(failure.category)) return false;
    categories.add(failure.category);
    if (failure.lastOccurredAt !== null && !isCanonicalIsoDateTime(failure.lastOccurredAt)) {
      return false;
    }
  }
  return true;
}

export interface SystemStatus {
  generatedAt: string;
  overall: "HEALTHY" | "DEGRADED";
  components: SystemComponentStatus[];
  gateway: GatewayStatus;
  storage: {
    tables: number;
    orders: number;
    openSessions: number;
    operatorAccounts: number;
    coachProfiles: number;
    activeCoachShifts: number;
    coachQueueEntries: number;
    coachServices: number;
    activeCoachServices: number;
    pendingOutbox: number;
    failedOutbox: number;
    pendingManualReview: number;
    auditRecords: number;
    integrity: "OK" | "FAILED";
  };
  operationalEvidence: "SIMULATED";
}

export type DiagnosticBundle = DiagnosticBundleReadModel;

export const TransferSessionRequestSchema = Type.Object(
  {
    targetTableId: Type.String({ minLength: 1 }),
    reason: Type.Optional(Type.String({ minLength: 2, maxLength: 200 })),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const AdjustSessionStartRequestSchema = Type.Object(
  {
    startedAt: Type.String({ format: "date-time" }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const SessionBillingActionRequestSchema = Type.Object(
  {
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const DeviceEffectActionRequestSchema = Type.Object(
  { idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }) },
  { additionalProperties: false },
);

export const AddOrderItemRequestSchema = Type.Union([
  Type.Object(
    {
      skuId: Type.String({ minLength: 1 }),
      quantity: Type.Integer({ minimum: 1, maximum: 99 }),
      idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      skuId: Type.String({ minLength: 1 }),
      quantity: Type.Integer({ minimum: 1, maximum: 99 }),
      lineType: Type.Literal("GIFT"),
      expectedProductName: Type.String({ minLength: 1, maxLength: 120 }),
      expectedListUnitPriceFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      reason: Type.String({ minLength: 2, maxLength: 200 }),
      idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    },
    { additionalProperties: false },
  ),
]);
export type AddOrderItemRequest = Static<typeof AddOrderItemRequestSchema>;

export const ReturnOrderItemRequestSchema = Type.Object(
  {
    itemId: Type.String({ minLength: 1 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const OrderItemSaleReturnRequestSchema = Type.Object(
  {
    itemId: Type.String({ minLength: 1, maxLength: 128 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    paymentResolution: Type.Literal("UNPAID_ORDER_REDUCTION"),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type OrderItemSaleReturnRequest = Static<typeof OrderItemSaleReturnRequestSchema>;

export const CheckoutRequestSchema = Type.Object(
  {
    method: PaymentMethodSchema,
    memberId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    memberBalanceFen: Type.Optional(Type.Integer({ minimum: 0 })),
    memberSpendPlanHash: Type.Optional(Type.String({ pattern: "^[a-f0-9]{64}$" })),
    expectedTotalFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CheckoutRequest = Static<typeof CheckoutRequestSchema>;

export function isCheckoutRequest(value: unknown): value is CheckoutRequest {
  if (!Value.Check(CheckoutRequestSchema, value)) return false;
  const request = value as CheckoutRequest;
  const memberId = request.memberId ?? null;
  const memberBalanceFen = request.memberBalanceFen ?? 0;
  const hasMemberSpendPlan = request.memberSpendPlanHash !== undefined;
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/u.test(request.idempotencyKey)) return false;
  if (memberId !== null && (memberId !== memberId.trim() || memberId.length > 256)) return false;
  if (request.method === "MEMBER_BALANCE") {
    return (
      memberId !== null &&
      request.expectedTotalFen > 0 &&
      (!hasMemberSpendPlan || request.memberSpendPlanHash?.length === 64) &&
      (request.memberBalanceFen === undefined || memberBalanceFen === request.expectedTotalFen)
    );
  }
  if (request.memberBalanceFen !== undefined && memberBalanceFen === 0) return false;
  if (memberBalanceFen > 0) {
    return (
      memberId !== null &&
      (!hasMemberSpendPlan || request.memberSpendPlanHash?.length === 64) &&
      ["CASH", "STATIC_WECHAT", "STATIC_ALIPAY"].includes(request.method) &&
      memberBalanceFen < request.expectedTotalFen
    );
  }
  return memberId === null && !hasMemberSpendPlan;
}

export const RefundRequestSchema = Type.Object(
  {
    amountFen: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export const ReconcilePaymentRequestSchema = Type.Object(
  {
    outcome: Type.Union([
      Type.Literal("MANUALLY_RECONCILED"),
      Type.Literal("NOT_RECEIVED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_PAYMENT"),
    ]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
